import {
  useCallback, useEffect, useId, useMemo, useRef, useState,
  type FormEvent, type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent, type ReactNode,
} from 'react';
import { SurfaceShell, BackLink } from '../../../components/SurfaceShell.tsx';
import { Panel, Section, PanelGrid, Eyebrow } from '../../../components/Panel.tsx';
import { Button, IconButton } from '../../../components/Button.tsx';
import { DataTable, type Column } from '../../../components/DataTable.tsx';
import { KeyValue, type KVItem } from '../../../components/KeyValue.tsx';
import { StatusBadge, StateLabel } from '../../../components/StatusBadge.tsx';
import { LivenessBadge } from '../../../components/LivenessBadge.tsx';
import { StatusDot } from '../../../components/StatusDot.tsx';
import { Chip } from '../../../components/Chip.tsx';
import { Distribution } from '../../../components/Distribution.tsx';
import {
  RibbonStack, type RibbonLane, type RibbonSegment,
} from '../../../components/Ribbon.tsx';
import { axisClock, frac } from '../../../components/timeScale.ts';
import { Tabs, type TabItem } from '../../../components/Tabs.tsx';
import { SegmentedControl } from '../../../components/Toolbar.tsx';
import { LiveTag } from '../../../components/Freshness.tsx';
import { Timestamp } from '../../../components/Timestamp.tsx';
import { Empty, ErrorLine, Awaiting } from '../../../components/Empty.tsx';
import { Field, Input, Select, Checkbox } from '../../../components/Field.tsx';
import { Icon } from '../../../shell/Icon.tsx';
import { useNow } from '../../../components/useNow.ts';
import {
  NO_VALUE, bytes, duration, latlon, local, midEllipsis, num, relative, unitIsTight,
  utc, utcTime,
} from '../../../components/format.ts';
import {
  level, levelTone, matrixTone, STATUS_STATE,
  type StateDescriptor, type Tone,
} from '../../../components/state.ts';
import { VideosTab } from './VideosTab.tsx';
import { useRobot } from '../../../hooks/useRobot.ts';
import { useRobotRevisions, useSetRevision, type Revision } from '../../../hooks/useRevisions.ts';
import { useTelemetry, type TelemetryReading } from '../../../hooks/useTelemetry.ts';
import {
  scanDerivatives,
  scanFiles,
  useRobotScans,
  type Scan,
} from '../../../hooks/useScans.ts';
import {
  useIssueCommand, useRobotCommands,
  type Command, type CommandName, type CommandStatus,
} from '../../../hooks/useCommands.ts';
import { useCommandStream } from '../../../hooks/useCommandStream.ts';
import {
  useDeleteRobot, useMintEnrollmentToken, useUpdateRobot,
  MORPHOLOGIES, STATUSES,
  type EnrollmentToken, type Morphology, type Robot, type RobotStatus,
} from '../../../hooks/useRobots.ts';
import {
  CHANNELS as KIT_CHANNELS, CursorCtx, SignalStack, commandMarks, seriesColour,
  type CursorApi,
} from '../../../components/charts/index.ts';
import {
  FaultLine, Figure, ReadPanel, ReadTag, SkeletonPlot, SkeletonRail, SkeletonTable,
  StoppedContext, figure, unread, unreadLine, useRead,
  type Read, type SkeletonColumn,
} from '../../../components/states/index.ts';
import { realtimeConfig } from '../../../realtime/config.ts';
import { useFeedWord } from '../../../shell/TopBar.tsx';
import { useFleetRoute } from '../../../router/useFleetRoute.ts';
import { EnrollmentTokenDialog } from './EnrollmentTokenDialog.tsx';
import { Unit } from '../../../components/Unit.tsx';
import { scanState } from '../../../components/scanState.ts';
import styles from './RobotDetail.module.css';

/* ==========================================================================
   Robot detail — everything about one robot, on one surface.

   Four bands, top to bottom, and the first three never scroll away:

     1  IDENTITY      name + robot id, the operational state pair (mode AND
                      liveness — Auterion's frame proves a fused "status" has
                      to choose and chooses wrong), and a one-line provenance
                      strip: model · morphology+revision · board · firmware ·
                      real-vs-sim · site. A line, not a definition list.
     2  VITALS        seven telemetry channels as instruments, each carrying a
                      health dot resolved off its OWN threshold, so which
                      subsystem is in trouble is legible before a click
                      (auterion-flight-log-review.png's per-section verdict
                      dots, applied to live data). Ends with the one thing
                      that qualifies all seven: is this now, and how old.
     3  TABS          the object's surfaces, verb-tabbed.
     4  RAIL + BODY   a 328px reference rail (identity, position, enrolment,
                      agent provenance) beside the active surface — the rail
                      is the spec sheet you glance at while working in the
                      body, which is why it does not live in a tab.

   Everything on screen is either a field of the REST contract or arithmetic
   over the telemetry window (runs of `state`, gaps between samples, sequence
   continuity, a track projected from the fixes). Nothing is invented; a
   quantity the platform does not publish renders as an em dash or as an
   explicit awaiting state.
   ========================================================================== */

/* -------------------------------------------------------------------------
   Fields the mock/FastAPI contract returns that `TelemetryReading` does not
   yet declare. Read through a guard rather than a cast that could lie: a
   payload without the key yields null, which every consumer renders as an em
   dash. (Noted for the API/types pass — these belong in useTelemetry.ts.)
   ------------------------------------------------------------------------- */
function topLevelNum(t: TelemetryReading, key: string): number | null {
  const v = (t as unknown as Record<string, unknown>)[key];
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}
function topLevelStr(t: TelemetryReading, key: string): string | null {
  const v = (t as unknown as Record<string, unknown>)[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function payloadNum(t: TelemetryReading, key: string): number | null {
  const v = t.payload[key];
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}
function scanNum(s: Scan, key: string): number | null {
  const v = (s as unknown as Record<string, unknown>)[key];
  return typeof v === 'number' && !Number.isNaN(v) ? v : null;
}
function scanStr(s: Scan, key: string): string | null {
  const v = (s as unknown as Record<string, unknown>)[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function scanBool(s: Scan, key: string): boolean | null {
  const v = (s as unknown as Record<string, unknown>)[key];
  return typeof v === 'boolean' ? v : null;
}
function metaStr(robot: Robot, key: string): string | null {
  const v = robot.meta[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function revBool(rev: Revision, key: string): boolean | null {
  const v = (rev as unknown as Record<string, unknown>)[key];
  return typeof v === 'boolean' ? v : null;
}
function revMetaStr(rev: Revision, key: string): string | null {
  const v = rev.meta[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function revStr(rev: Revision, key: string): string | null {
  const v = (rev as unknown as Record<string, unknown>)[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/* -------------------------------------------------------------------------
   Thresholds. Written once, in the value's own units, and PUBLISHED — every
   channel's limits go into its title and its sparkline draws them, so no
   number on this page relies on the operator knowing where the line is.
   ------------------------------------------------------------------------- */
interface ChannelDef {
  key: string;
  /** Uppercase eyebrow. Short enough to survive a 146px cell. */
  label: string;
  unit: string;
  digits: number;
  pick: (t: TelemetryReading) => number | null;
  /** The metric's real domain, where it has one. Omitted ⇒ fitted trace. */
  min?: number | undefined;
  max?: number | undefined;
  warn?: number | undefined;
  bad?: number | undefined;
  /** High is bad — CPU, temperature. */
  invert?: boolean | undefined;
  /** Thresholds this channel is judged on but does not display. */
  judge?: ((t: TelemetryReading) => number | null) | undefined;
  /** What the judged quantity IS, when it is not the displayed one. */
  judgeLabel?: string | undefined;
  judgeUnit?: string | undefined;
  /** Which way the value moves when the robot is working — used to name a
      trend honestly ("draining", "filling") rather than printing a bare sign. */
  fallLabel?: string | undefined;
  riseLabel?: string | undefined;
  /** The value a falling channel runs out AT, for a projection with meaning. */
  floor?: number | undefined;
  hint: string;
}

const CHANNELS: readonly ChannelDef[] = [
  {
    key: 'batt', label: 'Battery', unit: '%', digits: 1,
    pick: (t) => t.battery_pct, min: 0, max: 100, warn: 40, bad: 15,
    fallLabel: 'draining', riseLabel: 'charging', floor: 0,
    hint: 'State of charge. Warn at 40%, recover-now at 15%.',
  },
  {
    key: 'speed', label: 'Speed', unit: 'm/s', digits: 2,
    pick: (t) => t.speed_mps,
    hint: 'Ground speed reported by the agent. No limit — speed is a fact, not a fault.',
  },
  {
    key: 'cpu', label: 'CPU', unit: '%', digits: 1,
    pick: (t) => t.cpu_pct, min: 0, max: 100, warn: 75, bad: 90, invert: true,
    hint: 'Agent host load. Warn at 75%, saturated at 90%.',
  },
  {
    key: 'temp', label: 'Temp', unit: '°C', digits: 1,
    pick: (t) => t.temp_c, min: 0, max: 90, warn: 65, bad: 80, invert: true,
    hint: 'Compute-bay temperature. Warn at 65 °C, throttling at 80 °C.',
  },
  {
    key: 'disk', label: 'Disk free', unit: 'GiB', digits: 1,
    pick: (t) => t.disk_free_gib,
    /* Free GiB is the number an operator acts on, but the threshold lives on
       used% — capacity is not published per robot, so a bounded free-space
       domain would be a guess. The trace is therefore fitted and says so. */
    judge: (t) => t.disk_used_pct, warn: 80, bad: 92, invert: true,
    judgeLabel: 'disk used', judgeUnit: '%',
    fallLabel: 'filling', riseLabel: 'freeing', floor: 0,
    hint: 'Capture space left on the agent. Judged on disk used: warn at 80%, full at 92%.',
  },
  {
    key: 'rssi', label: 'Link', unit: 'dBm', digits: 0,
    pick: (t) => payloadNum(t, 'link_rssi_dbm'),
    min: -100, max: -40, warn: -75, bad: -85,
    hint: 'Uplink RSSI from the telemetry payload. Warn at −75 dBm, marginal at −85 dBm.',
  },
  {
    key: 'gnss', label: 'GNSS', unit: 'sats', digits: 0,
    pick: (t) => payloadNum(t, 'gnss_sats'),
    min: 0, max: 24, warn: 8, bad: 5,
    hint: 'Satellites in the fix. Warn at 8, position untrustworthy at 5.',
  },
];

/* The Identity rail's row labels. Chrome, not data: they are true before the
   first read, after a failed one and on a robot that does not exist, so the
   instrument draws them in every state and only the values are ever absent.
   Kept beside the rail they stand in for, so the two cannot drift. */
const IDENTITY_ROWS: readonly string[] = [
  'Revision', 'Board', 'Bringup', 'Pinned', 'Firmware', 'Tasking', 'Site', 'Bay',
];
const POSITION_ROWS: readonly string[] = ['Fix', 'Speed', 'Last fix'];
const ENROLMENT_ROWS: readonly string[] = [
  'Registered', 'Enrolled', 'By', 'Cert serial', 'Expires',
];

/* The two ledgers' column specs, and the lane spec the telemetry window would
   have drawn — every one of them chrome, and every one of them true about a
   robot the console has not managed to read. */
const QUEUE_COLUMNS: readonly SkeletonColumn[] = [
  { label: 'Command' },
  { label: 'Status', width: '90px' },
  { label: 'In state', width: '72px', align: 'right' },
  { label: 'Issued', width: '84px', align: 'right' },
  { label: 'By', width: '132px' },
];
const CAPTURE_COLUMNS: readonly SkeletonColumn[] = [
  { label: 'Session' },
  { label: 'State', width: '86px' },
  { label: 'Duration', width: '72px', align: 'right' },
  { label: 'Size', width: '62px', align: 'right' },
  { label: 'Captured', width: '86px', align: 'right' },
];

/* The lane stack's channels come from the kit, not from this file: /telemetry
   and this panel draw the same nine lanes and a second table of limits here is
   how two surfaces end up disagreeing about where a battery becomes a problem.
   The table above stays for the vitals band, which needs the trend words and
   the projection floor the kit's rows do not carry. */
const STACK_CHANNELS = KIT_CHANNELS.filter((c) => c.secondary !== true);

/* The same lanes, as the states kit's plot instrument draws them when the read
   has not landed: the channel's name, its unit, and the limits it is judged
   against — which do not move because the readings are missing. */
const STACK_LANES = STACK_CHANNELS.map((c) => ({
  label: c.label,
  unit: c.unit,
  ...(c.warn !== undefined && c.bad !== undefined && c.min !== undefined && c.max !== undefined
    ? {
      rules: [c.warn, c.bad].map((v) => ({
        at: 1 - (v - c.min!) / Math.max(1, c.max! - c.min!),
        label: String(v),
      })),
    }
    : {}),
}));

/** Certificate life the operator plans in: a month out is this month's job. */
const CERT_WARN_S = 30 * 24 * 3600;
const CERT_BAD_S = 7 * 24 * 3600;

/** Motion authority expires on its own — a lease, not a mode. */
const LEASE_MS = 120_000;

type TabId = 'overview' | 'telemetry' | 'commands' | 'captures' | 'videos' | 'config';

const TAB_IDS: readonly string[] =
  ['overview', 'telemetry', 'commands', 'captures', 'videos', 'config'];

function isTabId(v: string | undefined): v is TabId {
  return v !== undefined && TAB_IDS.includes(v);
}

/* -------------------------------------------------------------------------
   THE WINDOW. An absolute span of wall-clock, not a row count.

   A "last 200 readings" control is a scale that moves with the agent's
   cadence: 200 readings is 8 hours on a 2m24s robot and 33 minutes on a 10s
   one, so two robots' plots cannot be compared and the same robot's plot
   changes meaning when its cadence does. Every reference transport bar
   (formant-timeline-data-presence-scrubber.png's `24 HRS`,
   palantir-gotham-facet-rail-timeline.png's ruler) is in time units. So is
   this one; the reading count moved to the footer, where it is a fact about
   the window rather than the definition of it.

   `limit` is how deep to fetch for that span — sized off a 10s worst-case
   cadence, and the scrubber says plainly when the store had more.
   ------------------------------------------------------------------------- */
type SpanKey = '1h' | '6h' | '24h' | '7d';

const SPANS: readonly { key: SpanKey; ms: number; limit: number }[] = [
  { key: '1h', ms: 3_600_000, limit: 600 },
  { key: '6h', ms: 21_600_000, limit: 1_200 },
  { key: '24h', ms: 86_400_000, limit: 2_400 },
  { key: '7d', ms: 604_800_000, limit: 5_000 },
];

function spanDef(k: SpanKey) {
  return SPANS.find((s) => s.key === k) ?? SPANS[1]!;
}

/**
 * Which clock the x-axis is. The agent stamps `ts` off its own clock and
 * reports `clock_offset_ms`, the measured skew of that clock against the
 * ingest tier — so the console genuinely holds two different timelines and
 * must say which one it is drawing. `ingest_time = agent_ts − clock_offset`.
 */
type TimeAxisKey = 'agent' | 'ingest';

const AXIS_LABEL: Record<TimeAxisKey, string> = {
  agent: 'agent_time',
  ingest: 'ingest_time',
};

/* -------------------------------------------------------------------------
   Command vocabulary. The BUTTONS come from the robot's own command_profile
   (which commands it accepts, each one's hazard class and required params);
   this map only supplies the human label and the transport mark, and a
   command the profile names but this map does not still renders under its
   raw wire name rather than disappearing.
   ------------------------------------------------------------------------- */
const COMMAND_LABEL: Partial<Record<CommandName, string>> = {
  move: 'Move',
  stop: 'Stop',
  return_home: 'Return home',
  start_mission: 'Start mission',
  pause: 'Pause',
  resume: 'Resume',
  start_capture: 'Start capture',
  stop_capture: 'Stop capture',
  start_mapping: 'Start mapping',
  stop_mapping: 'Stop mapping',
};

/* A glyph only where it is one of the four transport marks an operator reads
   faster than the word. Everything else is text. */
const COMMAND_MARK: Partial<Record<CommandName, 'play' | 'pause' | 'stop' | 'record'>> = {
  pause: 'pause',
  resume: 'play',
  stop: 'stop',
  start_capture: 'record',
  stop_capture: 'stop',
};

/**
 * The order the hazard classes are presented in — safest first, so the row an
 * operator reaches for without thinking is the row that cannot hurt anything.
 * A class the contract publishes that is not on this list keeps its own name
 * and sorts last; it is never folded into `benign`.
 */
const HAZARD_ORDER: readonly string[] = ['benign', 'motion', 'hazardous'];

function hazardRank(h: string): number {
  const i = HAZARD_ORDER.indexOf(h);
  return i === -1 ? HAZARD_ORDER.length : i;
}

const CMD_TONE: Record<CommandStatus, Tone> = {
  pending: 'stale',
  sent: 'charging',
  acked: 'idle',
  completed: 'good',
  failed: 'error',
  cancelled: 'neutral',
};

interface ProfileEntry {
  /** The wire name — the identity, and what the button issues. */
  name: string;
  hazard: string;
  required: readonly string[];
}

function readProfile(robot: Robot): ProfileEntry[] | null {
  const raw = robot.command_profile?.['commands'];
  if (raw === null || raw === undefined || typeof raw !== 'object') return null;
  const out: ProfileEntry[] = [];
  for (const [name, spec] of Object.entries(raw as Record<string, unknown>)) {
    const s = (spec ?? {}) as Record<string, unknown>;
    const params = Array.isArray(s['required_params'])
      ? (s['required_params'] as unknown[]).filter((p): p is string => typeof p === 'string')
      : [];
    out.push({
      name,
      hazard: typeof s['hazard'] === 'string' ? s['hazard'] : 'unknown',
      required: params,
    });
  }
  return out;
}

/* -------------------------------------------------------------------------
   Derivations over the telemetry window. Each one is arithmetic on readings
   the API returned — no smoothing, no interpolation across a gap.
   ------------------------------------------------------------------------- */

interface WindowFacts {
  /** Oldest → newest. */
  readings: readonly TelemetryReading[];
  times: readonly number[];
  from: number;
  to: number;
  latest: TelemetryReading | null;
  /** Median inter-sample gap, ms — the cadence the agent is actually keeping. */
  cadenceMs: number | null;
  /** Sequence numbers seen vs the span they cover: honest frame loss. */
  seqSpan: number | null;
  seqSeen: number | null;
  bootIds: readonly string[];
  clockOffsetMs: number | null;
  /** Largest |correction| the chosen time axis applied, ms. 0 on agent_time. */
  axisShiftMs: number;
  /** The store returned exactly as many rows as were asked for: there is
      older history the console has NOT fetched, and the scrubber must say so
      rather than painting the shortfall as "no data". */
  truncated: boolean;
}

function statsOf(
  readings: readonly TelemetryReading[],
  times: readonly number[],
): Pick<WindowFacts, 'cadenceMs' | 'seqSpan' | 'seqSeen' | 'bootIds'> {
  const gaps: number[] = [];
  for (let i = 1; i < times.length; i += 1) gaps.push(times[i]! - times[i - 1]!);
  gaps.sort((a, b) => a - b);
  const seqs = readings
    .map((r) => topLevelNum(r, 'seq'))
    .filter((v): v is number => v !== null);
  const boots = new Set<string>();
  for (const r of readings) {
    const b = topLevelStr(r, 'agent_boot_id');
    if (b !== null) boots.add(b);
  }
  return {
    cadenceMs: gaps.length > 0 ? gaps[Math.floor(gaps.length / 2)]! : null,
    seqSpan: seqs.length > 1 ? Math.max(...seqs) - Math.min(...seqs) + 1 : null,
    seqSeen: seqs.length > 1 ? seqs.length : null,
    bootIds: [...boots],
  };
}

/** Everything the store returned, on the chosen time axis. The scrubber's extent. */
function windowFacts(
  rows: readonly TelemetryReading[] | undefined,
  now: number,
  axis: TimeAxisKey,
  limit: number,
): WindowFacts {
  const readings = [...(rows ?? [])].reverse();
  /* The correction is per reading, not per window: the agent's skew drifts,
     which is the whole reason the two axes are worth naming. */
  const shiftOf = (r: TelemetryReading) =>
    (axis === 'ingest' ? -(topLevelNum(r, 'clock_offset_ms') ?? 0) : 0);
  const times = readings.map((r) => new Date(r.ts).getTime() + shiftOf(r));
  const latest = readings.length > 0 ? readings[readings.length - 1]! : null;
  const newest = times.length > 0 ? times[times.length - 1]! : null;
  const axisShiftMs = readings.reduce((m, r) => Math.max(m, Math.abs(shiftOf(r))), 0);

  return {
    readings,
    times,
    from: times.length > 0 ? times[0]! : now - 3600_000,
    to: Math.max(newest ?? now, now),
    latest,
    ...statsOf(readings, times),
    clockOffsetMs: latest === null ? null : topLevelNum(latest, 'clock_offset_ms'),
    axisShiftMs,
    truncated: readings.length >= limit,
  };
}

/**
 * The VIEW: `all`, cut to an absolute [from, to].
 *
 * `from`/`to` are the REQUESTED window, never the data's extent — that is the
 * point of an absolute axis. A robot that stopped reporting four hours into a
 * six-hour window gets four hours of trace and two hours of visible silence,
 * and its plot lines up pixel-for-pixel with the robot in the next tab.
 */
function sliceFacts(all: WindowFacts, from: number, to: number): WindowFacts {
  const readings: TelemetryReading[] = [];
  const times: number[] = [];
  for (let i = 0; i < all.readings.length; i += 1) {
    const t = all.times[i]!;
    if (t >= from && t <= to) {
      readings.push(all.readings[i]!);
      times.push(t);
    }
  }
  const latest = readings.length > 0 ? readings[readings.length - 1]! : null;
  const stats = statsOf(readings, times);
  return {
    readings,
    times,
    from,
    to,
    latest,
    ...stats,
    /* A one-sample window has no cadence of its own; the fetch's median is
       still the agent's real cadence and is what tolerance must be built on. */
    cadenceMs: stats.cadenceMs ?? all.cadenceMs,
    clockOffsetMs: latest === null ? null : topLevelNum(latest, 'clock_offset_ms'),
    axisShiftMs: all.axisShiftMs,
    truncated: all.truncated,
  };
}

/** How far past its last sample a reading may be believed. */
function tolerance(cadenceMs: number | null): number {
  return Math.max(30_000, (cadenceMs ?? 60_000) * 2.5);
}

/**
 * Contiguous runs of one discrete value → ribbon segments.
 *
 * A run never extends more than one tolerance past its own last sample. A
 * robot that stopped reporting at 00:12 while `idle` was idle *at 00:12*; a
 * ribbon that paints it idle to the right-hand edge is claiming eight hours
 * of state nobody observed, and it would sit directly above a contact lane
 * that says the opposite.
 */
function runSegments(
  readings: readonly TelemetryReading[],
  times: readonly number[],
  to: number,
  tol: number,
  valueOf: (t: TelemetryReading) => string | null,
  toneOf: (value: string) => Tone,
): RibbonSegment[] {
  const out: RibbonSegment[] = [];
  for (let i = 0; i < readings.length; i += 1) {
    const v = valueOf(readings[i]!);
    if (v === null) continue;
    const start = times[i]!;
    let j = i;
    while (j + 1 < readings.length && valueOf(readings[j + 1]!) === v) j += 1;
    const next = j + 1 < readings.length ? times[j + 1]! : to;
    const end = Math.min(next, times[j]! + tol);
    if (end > start) out.push({ start, end, tone: toneOf(v), label: v });
    i = j;
  }
  return out;
}


/* -------------------------------------------------------------------------
   THE BOX every trace of a channel is drawn in — one function, so the
   62px vitals trace and the 90px named lane never disagree about the same
   quantity.

   The naive choices are both wrong. Autoscaling to the window makes a battery
   falling 98 → 90 and one falling 57 → 8 draw the identical diagonal, which
   is why `Sparkline` refuses to do it by default. But handing every channel
   its full physical domain is the other failure: CPU 15–61 inside 0–100 and
   RSSI −72…−58 inside −100…−40 spend two thirds of the lane on regions the
   robot was never in, and six lanes of flat line is not an instrument.

   So: THE BOX RUNS FROM THE DATA TO THE LIMIT IT IS NEAREST TO. The extent of
   this window, extended to whichever published threshold sits just outside it
   — the largest limit below the trace and the smallest above — then padded so
   that rule lands inside the box rather than on its edge, then clipped to the
   metric's physical domain (a battery is never 104%, a satellite count is
   never −1).

   The property that matters survives: height in the box is still measured
   against the threshold, not against the window. A battery at 90% draws near
   the top of a 40–98 box; one at 8% draws at the floor of a 8–57 box; they do
   not look alike. And the limit is now always ON SCREEN — a channel can no
   longer be plotted against a rule that fell off the top of its own lane.
   ------------------------------------------------------------------------- */
interface ChannelBox {
  lo: number;
  hi: number;
  /** The box is wider than the window because a limit anchors an edge. */
  guided: boolean;
  /** Decimals the two axis numerals carry — and the precision the box was
      rounded to, so a printed edge is the edge and not a rounding of it. */
  digits: number;
}

function channelBox(
  def: ChannelDef,
  values: readonly (number | null)[],
): ChannelBox | null {
  const known = values.filter((v): v is number => v !== null);
  if (known.length === 0) return null;
  const dataLo = Math.min(...known);
  const dataHi = Math.max(...known);

  /* Only limits this channel is entitled to DRAW count. Disk free is judged
     on used %, so 80/92 are not points on a GiB axis and must not stretch one. */
  const limits = def.judge === undefined
    ? [def.warn, def.bad].filter((v): v is number => v !== undefined)
    : [];
  const below = limits.filter((v) => v < dataLo);
  const above = limits.filter((v) => v > dataHi);
  const guideLo = below.length > 0 ? Math.max(...below) : null;
  const guideHi = above.length > 0 ? Math.min(...above) : null;

  const rawLo = guideLo ?? dataLo;
  const rawHi = guideHi ?? dataHi;
  const span = rawHi - rawLo > 0 ? rawHi - rawLo : Math.max(1, Math.abs(rawHi) * 0.02);
  /* Enough air that a guided edge's dashed rule reads as a rule rather than as
     the lane's own frame, and that a free edge's extreme reading is not drawn
     on the boundary. */
  let lo = rawLo - span * (guideLo === null ? 0.08 : 0.07);
  let hi = rawHi + span * (guideHi === null ? 0.08 : 0.07);

  /* The physical domain is a clip, not a scale: it stops the padding from
     inventing a negative satellite or a 104% battery, and nothing else. */
  if (def.min !== undefined) lo = Math.max(lo, def.min);
  if (def.max !== undefined) hi = Math.min(hi, def.max);
  if (dataLo >= 0 && lo < 0) lo = 0;

  /* Round OUTWARDS to the precision the numerals will print at, so the number
     under the axis is the box's true edge rather than a tidier neighbour. */
  const digits = span >= 20 ? 0 : span >= 2 ? 1 : 2;
  const q = 10 ** digits;
  lo = Math.floor(lo * q) / q;
  hi = Math.ceil(hi * q) / q;
  if (hi <= lo) hi = lo + 1 / q;

  return { lo, hi, guided: guideLo !== null || guideHi !== null, digits };
}

/* =========================================================================
   THE TRACE. A 1px line whose COLOUR CHANGES AT THE CROSSING, not after it.

   `Sparkline` resolves ONE tone for a whole series from its newest reading.
   That is right for a 48px trend cell sitting beside the number it trends,
   and wrong for a named lane an operator reads as a history: koko-04 spent
   the first two hours of a six-hour window above both of its battery rules
   and only the last stretch under the recover-now line, but toned off the
   latest sample the entire window draws red — and the instant the battery
   actually went critical, which is the one thing the lane exists to show,
   becomes unfindable.

   So this splits the series at the exact point where it crosses each
   published limit — linearly interpolated between the two samples that
   straddle it, so the change of colour lands on the x-axis WHERE THE EVENT
   HAPPENED — and re-strokes only the spans genuinely past a limit. Everything
   else is `--nominal` steel, including the parts of a currently-failing
   channel that were fine.

   Three more things it does that the primitive does not:

     · it PRINTS THE LIMIT at the right-hand end of its own dashed rule, so
       "what is this red against" is answered by the pixels rather than by a
       hover title;
     · it judges on a DIFFERENT SERIES than it plots where the contract makes
       us — disk is judged on used %, drawn as free GiB — so the free-space
       trace can still redden across the spans where the disk was actually
       full, at the crossings of the series that carries the threshold;
     · an isolated sample between two gaps draws as a dot rather than
       vanishing, which on a lane full of holes is the difference between
       "no data" and "one reading".

   All four belong in `Sparkline` itself; they are written here rather than
   forked into the primitive mid-wave, and noted for the primitive pass.
   ========================================================================= */

type Lv = 'ok' | 'warn' | 'bad';

interface TracePath { lv: Lv; d: string }
interface TraceDot { lv: Lv; x: number; y: number }

function traceLevel(
  v: number | null,
  warn: number | undefined,
  bad: number | undefined,
  invert: boolean,
): Lv {
  if (v === null || warn === undefined || bad === undefined) return 'ok';
  const l = level(v, warn, bad, invert);
  return l === 'warn' || l === 'bad' ? l : 'ok';
}

/**
 * Series → per-level polylines, cut at the threshold crossings.
 *
 * `judged` carries the quantity the limits are written in and `values` the
 * quantity being drawn; they are the same array for six of the seven
 * channels. A crossing is found on `judged`, and the point it lands on is
 * `values` interpolated to the same fraction — so the split is at the true
 * time of the crossing on whichever axis is plotted.
 */
function tracePaths(
  values: readonly (number | null)[],
  judged: readonly (number | null)[],
  warn: number | undefined,
  bad: number | undefined,
  invert: boolean,
  x: (i: number) => number,
  y: (v: number) => number,
): { paths: TracePath[]; dots: TraceDot[] } {
  const limits = [warn, bad].filter((l): l is number => l !== undefined);
  const paths: TracePath[] = [];
  const dots: TraceDot[] = [];
  const n = values.length;
  const at = (i: number) => (values[i] ?? null) !== null && (judged[i] ?? null) !== null;

  let s = 0;
  while (s < n) {
    if (!at(s)) { s += 1; continue; }
    let e = s;
    while (e + 1 < n && at(e + 1)) e += 1;

    if (e === s) {
      const v = values[s] as number;
      dots.push({ lv: traceLevel(judged[s] as number, warn, bad, invert), x: x(s), y: y(v) });
      s = e + 1;
      continue;
    }

    /* Vertices in fractional index space: every sample, plus a vertex at each
       limit the segment between two samples passes through. */
    const verts: { i: number; v: number; j: number }[] = [];
    for (let k = s; k <= e; k += 1) {
      if (k > s) {
        const j0 = judged[k - 1] as number;
        const j1 = judged[k] as number;
        const v0 = values[k - 1] as number;
        const v1 = values[k] as number;
        const cuts = limits
          .filter((lim) => (j0 < lim && j1 > lim) || (j0 > lim && j1 < lim))
          .map((lim) => (lim - j0) / (j1 - j0))
          .sort((p, q) => p - q);
        for (const f of cuts) {
          verts.push({ i: (k - 1) + f, v: v0 + (v1 - v0) * f, j: j0 + (j1 - j0) * f });
        }
      }
      verts.push({ i: k, v: values[k] as number, j: judged[k] as number });
    }

    let curLv: Lv | null = null;
    let cur: string[] = [];
    const close = () => {
      if (curLv !== null && cur.length > 1) paths.push({ lv: curLv, d: cur.join(' ') });
    };
    for (let m = 1; m < verts.length; m += 1) {
      const p = verts[m - 1]!;
      const q = verts[m]!;
      /* The midpoint of the sub-segment, so a vertex sitting exactly ON a
         limit is not what decides the colour of the span leading to it. */
      const lv = traceLevel((p.j + q.j) / 2, warn, bad, invert);
      if (lv !== curLv) {
        close();
        cur = [`M${x(p.i).toFixed(2)},${y(p.v).toFixed(2)}`];
        curLv = lv;
      }
      cur.push(`L${x(q.i).toFixed(2)},${y(q.v).toFixed(2)}`);
    }
    close();
    s = e + 1;
  }
  return { paths, dots };
}

function Trace({
  def,
  values,
  judged,
  times,
  box,
  width,
  height,
  guided,
  head = false,
  probe = false,
  ruleLabels = false,
  label,
}: {
  def: ChannelDef;
  /** What is drawn. */
  values: readonly (number | null)[];
  /** What is judged. Identical to `values` unless the contract separates them. */
  judged: readonly (number | null)[];
  /** Epoch ms per sample, for the crosshair readout. */
  times?: readonly number[] | undefined;
  box: ChannelBox | null;
  width: number;
  height: number;
  /** The box was anchored on a published limit rather than fitted to the window. */
  guided: boolean;
  head?: boolean;
  probe?: boolean;
  /** Print each rule's value at its right-hand end. Needs ~24px of lane. */
  ruleLabels?: boolean;
  label: string;
}) {
  const [cursor, setCursor] = useState<number | null>(null);
  const n = values.length;
  const known = values.filter((v): v is number => v !== null);

  const lo = box?.lo ?? (def.min ?? (known.length > 0 ? Math.min(...known) : 0));
  const hiRaw = box?.hi ?? (def.max ?? (known.length > 0 ? Math.max(...known) : 1));
  const hi = hiRaw > lo ? hiRaw : lo + Math.max(1, Math.abs(lo) * 0.02);

  const padY = 1.5;
  const yTop = padY;
  const yBot = height - padY;
  const x = (i: number) => (n <= 1 ? width / 2 : (i / (n - 1)) * (width - 1) + 0.5);
  const y = (v: number) =>
    yBot - Math.max(0, Math.min(1, (v - lo) / (hi - lo))) * (yBot - yTop);

  /* Limits belong to the trace's own axis only where the trace plots the
     quantity they judge. Disk free is drawn in GiB and judged on used %, so
     80/92 are not points on this axis and no rule may be drawn for them —
     but the SPANS still colour, off the judged series. */
  const onAxis = def.judge === undefined;
  const { paths, dots } = tracePaths(values, judged, def.warn, def.bad, def.invert ?? false, x, y);

  const lastIdx = (() => {
    for (let i = n - 1; i >= 0; i -= 1) if (values[i] !== null) return i;
    return -1;
  })();
  const lastVal = lastIdx >= 0 ? (values[lastIdx] as number) : null;
  const lastLv = lastIdx >= 0
    ? traceLevel(judged[lastIdx] ?? null, def.warn, def.bad, def.invert ?? false)
    : 'ok';

  /* Which rules are drawable. Two dashed lines the eye cannot separate are
     one rule that lies about where the limits are, so a soft limit within
     3.5px of the hard one is dropped — the same law `Sparkline` keeps. */
  const inBox = (v: number | undefined): v is number => v !== undefined && v > lo && v < hi;
  const hard = onAxis && inBox(def.bad) ? def.bad : undefined;
  const softRaw = onAxis && inBox(def.warn) ? def.warn : undefined;
  const soft = softRaw !== undefined && hard !== undefined && Math.abs(y(softRaw) - y(hard)) < 3.5
    ? undefined
    : softRaw;

  const rules: { v: number; kind: 'warn' | 'error' }[] = [
    ...(soft === undefined ? [] : [{ v: soft, kind: 'warn' as const }]),
    ...(hard === undefined ? [] : [{ v: hard, kind: 'error' as const }]),
  ];

  const describe = [
    label,
    lastVal === null ? 'no readings' : `${lastVal.toFixed(def.digits)}${unitSuffix(def.unit)}`,
    known.length > 0
      ? `${known.length} readings, scale ${axisNum(lo)}–${axisNum(hi)}${unitSuffix(def.unit)}`
      : '',
    rules.length > 0
      ? `limits ${rules.map((r) => `${axisNum(r.v)}${unitSuffix(def.unit)}`).join(' / ')}`
      : '',
  ].filter(Boolean).join(' · ');

  const idxFromX = (clientX: number, rect: DOMRect) => {
    if (n <= 1) return 0;
    const f = Math.max(0, Math.min(1, (clientX - rect.left - 0.5) / Math.max(1, rect.width - 1)));
    return Math.round(f * (n - 1));
  };
  const onKey = (e: ReactKeyboardEvent<HTMLSpanElement>) => {
    if (n === 0) return;
    const at = cursor ?? lastIdx;
    if (e.key === 'ArrowLeft') { setCursor(Math.max(0, at - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { setCursor(Math.min(n - 1, at + 1)); e.preventDefault(); }
    else if (e.key === 'Home') { setCursor(0); e.preventDefault(); }
    else if (e.key === 'End') { setCursor(n - 1); e.preventDefault(); }
    else if (e.key === 'Escape') setCursor(null);
  };

  const active = probe && n > 1 && known.length > 0 ? cursor : null;
  const cursorVal = active === null ? null : values[active] ?? null;

  return (
    <span
      className={styles.trace}
      style={{ width: `${width}px`, height: `${height}px` }}
      title={describe}
      {...(probe
        ? {
          tabIndex: 0,
          role: 'group',
          'aria-label': describe,
          onPointerMove: (e: PointerEvent<HTMLSpanElement>) =>
            setCursor(idxFromX(e.clientX, e.currentTarget.getBoundingClientRect())),
          onPointerLeave: () => setCursor(null),
          onKeyDown: onKey,
          onBlur: () => setCursor(null),
        }
        : { role: 'img', 'aria-label': describe })}
    >
      <svg
        className={styles.traceSvg}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
        focusable="false"
      >
        {/* The domain floor: solid where the box is anchored on a published
            limit, dotted where it was fitted to this window — the same claim
            the gutter numerals make by dimming. */}
        <line
          className={styles.traceFloor}
          data-fitted={!guided}
          x1={0}
          x2={width}
          y1={yBot}
          y2={yBot}
        />
        {rules.map((r) => (
          <line
            key={r.kind}
            className={styles.traceLimit}
            data-limit={r.kind}
            x1={0}
            x2={ruleLabels ? Math.max(0, width - ruleLabelW(def, r.v) - 6) : width}
            y1={y(r.v)}
            y2={y(r.v)}
          />
        ))}
        {paths.length === 0 && dots.length === 0 ? (
          <line className={styles.traceEmpty} x1={0} x2={width} y1={height / 2} y2={height / 2} />
        ) : (
          (['ok', 'warn', 'bad'] as const).map((lv) => (
            /* Drawn worst last, so a two-pixel excursion is never hidden
               under the steel span that follows it. */
            <g key={lv}>
              {paths.filter((p) => p.lv === lv).map((p) => (
                <path key={p.d} className={styles.traceLine} data-lv={lv} d={p.d} />
              ))}
              {dots.filter((d) => d.lv === lv).map((d) => (
                <circle
                  key={`${d.x},${d.y}`}
                  className={styles.traceDot}
                  data-lv={lv}
                  cx={d.x}
                  cy={d.y}
                  r={0.9}
                />
              ))}
            </g>
          ))
        )}
        {/* The limit, printed on the pixels. Right-aligned at the rule's own
            end and haloed in the panel ground, so the trace crossing it does
            not cut the numeral in half. */}
        {ruleLabels && rules.map((r) => (
          <text
            key={`t-${r.kind}`}
            className={styles.traceLimitLabel}
            data-limit={r.kind}
            x={width - 3}
            /* Clamped inside the lane: a limit pinned near an edge of its own
               box must not print its value across the channel above. */
            y={Math.max(5.5, Math.min(height - 5.5, y(r.v)))}
            textAnchor="end"
            dominantBaseline="central"
          >
            {axisNum(r.v)}
            <tspan
              className={styles.traceLimitUnit}
              dx={unitIsTight(def.unit) ? 0 : 2}
            >
              {def.unit}
            </tspan>
          </text>
        ))}
        {head && lastVal !== null && (
          <circle
            className={styles.traceHead}
            data-lv={lastLv}
            cx={x(lastIdx)}
            cy={y(lastVal)}
            r={1.5}
          />
        )}
        {active !== null && (
          <line className={styles.traceCross} x1={x(active)} x2={x(active)} y1={0} y2={height} />
        )}
        {active !== null && cursorVal !== null && (
          <circle className={styles.traceMark} cx={x(active)} cy={y(cursorVal)} r={2} />
        )}
      </svg>
      {active !== null && (
        <span className={styles.traceChip} role="status">
          <span className={styles.traceChipKey}>
            {times !== undefined && times[active] !== undefined
              ? axisClock(times[active]!)
              : `${active + 1}/${n}`}
          </span>
          <span className={styles.traceChipVal}>
            {cursorVal === null ? NO_VALUE : cursorVal.toFixed(def.digits)}
            {cursorVal !== null && (
              <span className={styles.traceChipUnit}>{def.unit}</span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

/** Splice a sentence-case string into the middle of a sentence. */
function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** The same, for a channel label — except an acronym keeps its case, because
    `cPU` and `gNSS` are not words and read as typos in an aria-label. */
function lowerLabel(text: string): string {
  return /^[A-Z0-9]{2,}$/.test(text) ? text : lowerFirst(text);
}

/** `%` and `°` sit hard against the numeral; every other unit takes a space. */
function unitSuffix(unit: string): string {
  if (unit === '') return '';
  return unitIsTight(unit) ? unit : ` ${unit}`;
}

/** How much room a rule's printed value needs, so the dashes stop short of it. */
function ruleLabelW(def: ChannelDef, v: number): number {
  const glyphs = axisNum(v).length + def.unit.length;
  return glyphs * 5.4 + (unitIsTight(def.unit) ? 3 : 5);
}

/* -------------------------------------------------------------------------
   The vitals band.
   ------------------------------------------------------------------------- */

function channelLevel(
  def: ChannelDef,
  reading: TelemetryReading | null,
): 'ok' | 'warn' | 'bad' | 'unknown' {
  if (reading === null) return 'unknown';
  const judged = (def.judge ?? def.pick)(reading);
  if (def.warn === undefined || def.bad === undefined) {
    return judged === null ? 'unknown' : 'ok';
  }
  return level(judged, def.warn, def.bad, def.invert ?? false);
}

/**
 * WHICH READINGS THE BAND SPEAKS FOR.
 *
 * The band answers a question about the ROBOT — what is its state, and how
 * old is that — not a question about the telemetry window. So it reads the
 * window only while the window has something to say:
 *
 *   · window has readings   → the newest one in it. Live, that is the newest
 *                             there is; scrubbed, it is the playhead's value,
 *                             which is the point of scrubbing.
 *   · window is empty       → the newest reading there IS, dimmed and
 *                             labelled `no data in <span>`. A robot quiet for
 *                             longer than the span still has a last known
 *                             state, and eight em dashes claim it does not —
 *                             while the Position panel one column left prints
 *                             `Last fix 7h 39m ago` from the same fetch.
 *   · nothing ever fetched  → em dashes, and the readout says `never
 *                             reported`. That string belongs to jetrover-08,
 *                             and to nothing else.
 */
function bandSource(facts: WindowFacts, all: WindowFacts): WindowFacts {
  return facts.readings.length > 0 ? facts : all;
}

/* =========================================================================
   THE UPLINK INSTRUMENT.

   `−58 dBm` is one sample of one quantity, and on its own it is the exact
   failure THE BAR §13 tell 13 names: a link readout with no distribution
   behind it. A −58 that has been −58 all afternoon and a −58 that is the one
   good sample in a window of −88s are the same pixel, and they fail
   differently. So the LINK cell is a disclosure — one click, an anchored
   panel of label:value rows in the shape of
   formant-teleop-link-health-popover.png — and it answers four separate
   questions the strip cannot:

     SIGNAL     the distribution: mean/min/max/σ over the window, plus how
                many readings sat in each of the channel's PUBLISHED bands,
                and whether the strip's instantaneous value disagrees with
                the window's mean.
     DELIVERY   `seq` continuity. The agent numbers its readings, so frames
                the store never received are countable rather than inferable
                — this is the loss figure, measured, not modelled.
     TIMING     the arrival process: median cadence, p95, the widest gap, and
                how many intervals ran past tolerance.
     CLOCK      the agent's own clock against the server's (`clock_offset_ms`)
                and against its own monotonic counter (skew in ppm). Both
                were buried in the rail's Agent block; they are facts about
                the link's timebase and belong here.

   WHAT IS NOT HERE, AND WHY. The reference popover also carries RTT
   min/avg/max, TX/RX Mbps and both endpoint IPs. The F3 agent contract
   publishes none of those — no ping/pong, no byte counters, no peer address
   — so this panel says so in one line rather than inventing four numbers
   that would look exactly as authoritative as the measured ones. What the
   console genuinely knows about the path (which endpoint it is talking to,
   whether the device plane is pushing or being polled) is on screen instead.
   ========================================================================= */

const RSSI_DEF = CHANNELS.find((c) => c.key === 'rssi')!;

function mean(vs: readonly number[]): number | null {
  return vs.length === 0 ? null : vs.reduce((a, b) => a + b, 0) / vs.length;
}

function stdev(vs: readonly number[]): number | null {
  if (vs.length < 2) return null;
  const m = mean(vs)!;
  return Math.sqrt(vs.reduce((a, v) => a + (v - m) ** 2, 0) / (vs.length - 1));
}

/** Nearest-rank percentile over an ascending array. No interpolation: every
    number this panel prints is a value the link actually took. */
function quantile(asc: readonly number[], p: number): number | null {
  if (asc.length === 0) return null;
  const i = Math.min(asc.length - 1, Math.max(0, Math.ceil(p * asc.length) - 1));
  return asc[i]!;
}

interface LinkStats {
  /** Readings the panel is speaking for — zero means there is nothing to measure. */
  readings: number;
  n: number;
  now: number | null;
  min: number | null;
  avg: number | null;
  max: number | null;
  sd: number | null;
  /** Readings per PUBLISHED band, so the distribution is judged, not just shown. */
  bands: { ok: number; warn: number; bad: number };
  seqSeen: number | null;
  seqSpan: number | null;
  lost: number | null;
  lossPct: number | null;
  intervals: number;
  cadenceMs: number | null;
  p95Ms: number | null;
  maxGapMs: number | null;
  lateCount: number;
  tolMs: number;
  offsetNow: number | null;
  offsetMin: number | null;
  offsetMax: number | null;
  /** Agent wall clock against its own monotonic counter, ppm. One boot only. */
  driftPpm: number | null;
  boots: readonly string[];
  lastContact: string | null;
}

function linkStats(src: WindowFacts, robot: Robot, tolMs: number): LinkStats {
  const vals: number[] = [];
  for (const r of src.readings) {
    const v = payloadNum(r, 'link_rssi_dbm');
    if (v !== null) vals.push(v);
  }
  const asc = [...vals].sort((a, b) => a - b);

  /* The buckets are `level()` itself, not a re-derivation of it. A panel that
     counted `v < warn` while the cell's dot counted `v <= warn` would put a
     reading in the amber band on one surface and the steel band on the other,
     which is worse than not counting them at all. */
  const warn = RSSI_DEF.warn ?? -75;
  const bad = RSSI_DEF.bad ?? -85;
  const bands = { ok: 0, warn: 0, bad: 0 };
  for (const v of vals) {
    const lv = level(v, warn, bad, RSSI_DEF.invert ?? false);
    if (lv === 'bad') bands.bad += 1;
    else if (lv === 'warn') bands.warn += 1;
    else bands.ok += 1;
  }

  const gaps: number[] = [];
  let late = 0;
  for (let i = 1; i < src.times.length; i += 1) {
    const g = src.times[i]! - src.times[i - 1]!;
    gaps.push(g);
    if (g > tolMs) late += 1;
  }
  gaps.sort((a, b) => a - b);

  const offs: number[] = [];
  for (const r of src.readings) {
    const v = topLevelNum(r, 'clock_offset_ms');
    if (v !== null) offs.push(v);
  }

  /* Skew in ppm — how fast the agent's wall clock is running against its own
     monotonic counter. Only computable inside ONE boot: a monotonic counter
     restarts at zero when the agent does, and differencing across that
     boundary would report a drift of several thousand ppm that never
     happened. */
  let driftPpm: number | null = null;
  if (src.bootIds.length === 1 && src.readings.length > 1) {
    const a = src.readings[0]!;
    const b = src.readings[src.readings.length - 1]!;
    const ma = topLevelNum(a, 'mono_ns');
    const mb = topLevelNum(b, 'mono_ns');
    if (ma !== null && mb !== null) {
      const dMono = (mb - ma) / 1e6;
      const dWall = new Date(b.ts).getTime() - new Date(a.ts).getTime();
      if (dMono > 60_000) driftPpm = ((dWall - dMono) / dMono) * 1e6;
    }
  }

  const seen = src.seqSeen;
  const span = src.seqSpan;
  const lost = seen !== null && span !== null ? Math.max(0, span - seen) : null;

  return {
    readings: src.readings.length,
    n: vals.length,
    now: src.latest === null ? null : payloadNum(src.latest, 'link_rssi_dbm'),
    min: asc.length > 0 ? asc[0]! : null,
    avg: mean(vals),
    max: asc.length > 0 ? asc[asc.length - 1]! : null,
    sd: stdev(vals),
    bands,
    seqSeen: seen,
    seqSpan: span,
    lost,
    lossPct: lost !== null && span !== null && span > 0 ? (lost / span) * 100 : null,
    intervals: gaps.length,
    cadenceMs: src.cadenceMs,
    p95Ms: quantile(gaps, 0.95),
    maxGapMs: gaps.length > 0 ? gaps[gaps.length - 1]! : null,
    lateCount: late,
    tolMs,
    offsetNow: src.clockOffsetMs,
    offsetMin: offs.length > 0 ? Math.min(...offs) : null,
    offsetMax: offs.length > 0 ? Math.max(...offs) : null,
    driftPpm,
    boots: src.bootIds,
    lastContact: src.latest?.ts ?? robot.last_seen,
  };
}

/** A section header that carries its own qualifier on the same line — never a
    bare uppercase word floating above content (THE BAR §4). */
function LinkSection({ label, meta }: { label: string; meta?: string | undefined }) {
  return (
    <div className={styles.lhSec}>
      <Eyebrow>{label}</Eyebrow>
      {meta !== undefined && <span className={styles.lhSecMeta}>{meta}</span>}
    </div>
  );
}

/** One 20px `label ……… value unit` row. The panel is nothing but these. */
function LinkRow({
  label,
  value,
  unit,
  tone,
  aside,
  title,
}: {
  label: string;
  value: ReactNode;
  unit?: string | undefined;
  tone?: Tone | undefined;
  aside?: string | undefined;
  title?: string | undefined;
}) {
  return (
    <div className={styles.lhRow} title={title}>
      <span className={styles.lhKey}>{label}</span>
      {aside !== undefined && <span className={styles.lhAside}>{aside}</span>}
      <span className={styles.lhVal} data-tone={tone} data-toned={tone !== undefined}>
        {value}
        {unit !== undefined && <span className={styles.lhUnit}>{unit}</span>}
      </span>
    </div>
  );
}

function signed(v: number, digits: number): string {
  return `${v > 0 ? '+' : ''}${v.toFixed(digits)}`;
}

function LinkPanel({
  stats,
  robot,
  windowLabel,
  scoped,
  streamConnected,
  titleId,
  side,
  onClose,
}: {
  stats: LinkStats;
  robot: Robot;
  windowLabel: string;
  /** The stats came from the window; false ⇒ from the robot's last contact. */
  scoped: boolean;
  streamConnected: boolean;
  titleId: string;
  /** Which cell edge the panel hangs off, so it never runs off the monitor. */
  side: 'left' | 'right';
  /** `restore` asks the caller to put focus back on the trigger — true when the
      operator dismissed with the keyboard or the close verb, false when they
      clicked somewhere else and their focus already belongs there. */
  onClose: (restore: boolean) => void;
}) {
  /* Same disclosure contract as every other cell on this band. */
  const ref = useDisclose(onClose);

  const rt = realtimeConfig();
  const endpoint = typeof window === 'undefined' ? null : `${window.location.origin}/api`;
  const drift = stats.now !== null && stats.avg !== null && stats.sd !== null
    && Math.abs(stats.now - stats.avg) > stats.sd
    ? `${signed(stats.now - stats.avg, 0)} vs mean`
    : undefined;

  const lossTone: Tone | undefined = stats.lossPct === null
    ? undefined
    : stats.lossPct >= 2 ? 'error' : stats.lossPct > 0 ? 'warn' : undefined;
  const lateTone: Tone | undefined = stats.lateCount > 0 ? 'warn' : undefined;
  const nowLevel = channelLevel(
    RSSI_DEF,
    stats.now === null ? null : ({ payload: { link_rssi_dbm: stats.now } } as unknown as TelemetryReading),
  );

  return (
    <div
      className={styles.linkPop}
      data-side={side}
      ref={ref}
      tabIndex={-1}
      role="dialog"
      aria-labelledby={titleId}
    >
      <div className={styles.lhHead}>
        <span className={styles.lhTitle} id={titleId}>Uplink</span>
        <span className={styles.lhScope}>
          {stats.readings === 0
            ? 'no readings'
            : scoped ? `${windowLabel} window` : 'last contact'}
        </span>
        <IconButton
          icon="close"
          label="Close uplink detail"
          size="sm"
          onClick={() => onClose(true)}
        />
      </div>

      <div className={styles.lhBody}>
        {stats.readings === 0 ? (
          <>
            <p className={styles.lhNone}>
              {robot.robot_id} has never reported. There is no signal, delivery or
              timing to measure — only the path the console would use.
            </p>
            <LinkSection label="Endpoints" meta="console" />
            <LinkRow label="Robot" value={robot.robot_id} />
            <LinkRow
              label="Ingest"
              value={endpoint === null ? NO_VALUE : midEllipsis(endpoint, 26)}
              title={endpoint ?? undefined}
            />
            <LinkRow
              label="Device plane"
              value={rt === null ? 'polling' : streamConnected ? 'mqtt' : 'mqtt down'}
              aside={rt === null ? undefined : midEllipsis(rt.endpoint, 18)}
            />
            <LinkRow label="Last contact" value="never" />
          </>
        ) : (
        <>
        <LinkSection label="Signal · RSSI" meta={`${stats.n} readings`} />
        <LinkRow
          label="Now"
          value={stats.now === null ? NO_VALUE : stats.now.toFixed(0)}
          unit={stats.now === null ? undefined : ' dBm'}
          tone={levelTone(nowLevel)}
          aside={drift}
          title="The newest reading — one instantaneous sample, which is what the strip shows."
        />
        <LinkRow
          label="Mean"
          value={stats.avg === null ? NO_VALUE : stats.avg.toFixed(1)}
          unit={stats.avg === null ? undefined : ' dBm'}
        />
        <LinkRow
          label="Min / max"
          value={stats.min === null || stats.max === null
            ? NO_VALUE
            : `${stats.min.toFixed(0)} / ${stats.max.toFixed(0)}`}
          unit={stats.min === null ? undefined : ' dBm'}
        />
        <LinkRow
          label="Jitter (σ)"
          value={stats.sd === null ? NO_VALUE : stats.sd.toFixed(1)}
          unit={stats.sd === null ? undefined : ' dB'}
          title="Standard deviation of the RSSI readings in this window."
        />
        {stats.n > 0 && (
          <div className={styles.lhDist}>
            <Distribution
              barWidth={104}
              items={[
                {
                  key: 'ok',
                  label: `above ${RSSI_DEF.warn} dBm`,
                  count: stats.bands.ok,
                  tone: 'nominal',
                  title: 'Readings clear of the warning limit.',
                },
                {
                  key: 'warn',
                  label: `${RSSI_DEF.bad} … ${RSSI_DEF.warn} dBm`,
                  count: stats.bands.warn,
                  tone: 'warn',
                  title: 'Readings inside the warning band.',
                },
                {
                  key: 'bad',
                  label: `at or under ${RSSI_DEF.bad}`,
                  count: stats.bands.bad,
                  tone: 'error',
                  title: 'Readings past the hard limit — marginal link.',
                },
              ]}
            />
          </div>
        )}

        <LinkSection label="Delivery" meta="agent seq" />
        <LinkRow
          label="Received"
          value={stats.seqSeen === null || stats.seqSpan === null
            ? NO_VALUE
            : `${stats.seqSeen} / ${stats.seqSpan}`}
          title="Readings the store holds, over the span of agent sequence numbers they cover."
        />
        <LinkRow
          label="Dropped"
          value={stats.lost === null ? NO_VALUE : String(stats.lost)}
          tone={lossTone}
          title="Sequence numbers the agent issued that never arrived."
        />
        <LinkRow
          label="Loss"
          value={stats.lossPct === null ? NO_VALUE : stats.lossPct.toFixed(1)}
          unit={stats.lossPct === null ? undefined : '%'}
          tone={lossTone}
        />

        <LinkSection label="Timing" meta={`${stats.intervals} gaps`} />
        <LinkRow
          label="Cadence"
          value={stats.cadenceMs === null ? NO_VALUE : duration(stats.cadenceMs)}
          title="Median gap between consecutive readings — the cadence the agent is actually keeping."
        />
        <LinkRow
          label="p95 gap"
          value={stats.p95Ms === null ? NO_VALUE : duration(stats.p95Ms)}
          title="95th percentile of the inter-arrival gaps. Nearest-rank: a gap that really happened."
        />
        <LinkRow
          label="Widest gap"
          value={stats.maxGapMs === null ? NO_VALUE : duration(stats.maxGapMs)}
          tone={stats.maxGapMs !== null && stats.maxGapMs > stats.tolMs ? 'warn' : undefined}
        />
        <LinkRow
          label="Late"
          value={stats.intervals === 0
            ? NO_VALUE
            : `${stats.lateCount} / ${stats.intervals}`}
          tone={lateTone}
          aside={`> ${duration(stats.tolMs)}`}
          title={`Intervals that ran past tolerance (2.5× the median cadence, floor 30s = ${duration(stats.tolMs)}).`}
        />

        <LinkSection label="Clock" meta="agent − server" />
        <LinkRow
          label="Offset"
          value={stats.offsetNow === null ? NO_VALUE : signed(stats.offsetNow, 2)}
          unit={stats.offsetNow === null ? undefined : ' ms'}
          title="Agent clock minus server clock on the newest reading."
        />
        <LinkRow
          label="Offset range"
          value={stats.offsetMin === null || stats.offsetMax === null
            ? NO_VALUE
            : `${signed(stats.offsetMin, 1)} … ${signed(stats.offsetMax, 1)}`}
          unit={stats.offsetMin === null ? undefined : ' ms'}
        />
        <LinkRow
          label="Skew"
          value={stats.driftPpm === null ? NO_VALUE : signed(stats.driftPpm, 1)}
          unit={stats.driftPpm === null ? undefined : ' ppm'}
          title={stats.driftPpm === null
            ? 'Needs one uninterrupted boot and a minute of monotonic span to measure.'
            : 'Agent wall clock against its own monotonic counter across this window.'}
        />

        <LinkSection label="Endpoints" meta="console" />
        <LinkRow label="Robot" value={robot.robot_id} />
        <LinkRow
          label="Boot"
          value={stats.boots.length === 0
            ? NO_VALUE
            : stats.boots.length === 1
              ? midEllipsis(stats.boots[0]!, 14)
              : `${stats.boots.length} boots`}
          tone={stats.boots.length > 1 ? 'warn' : undefined}
          title={stats.boots.length > 1
            ? `The agent restarted inside this window: ${stats.boots.join(', ')}`
            : stats.boots.join(', ')}
        />
        <LinkRow
          label="Ingest"
          value={endpoint === null ? NO_VALUE : midEllipsis(endpoint, 26)}
          title={endpoint ?? undefined}
        />
        <LinkRow
          label="Device plane"
          value={rt === null
            ? 'polling'
            : streamConnected ? 'mqtt' : 'mqtt down'}
          tone={rt !== null && !streamConnected ? 'warn' : undefined}
          aside={rt === null ? undefined : midEllipsis(rt.endpoint, 18)}
          title={rt === null
            ? 'No IoT endpoint configured for this deployment — the console polls REST for command state.'
            : `AWS IoT Core over MQTT/WSS: ${rt.endpoint}`}
        />
        <LinkRow
          label="Last contact"
          value={stats.lastContact === null ? 'never' : utc(stats.lastContact)}
          title={stats.lastContact === null
            ? `${robot.robot_id} has never reported.`
            : `Absolute UTC of the newest reading held for ${robot.robot_id}.`}
        />
        </>
        )}
      </div>

      {stats.readings > 0 && (
        <p className={styles.lhFoot}>
          RTT, throughput and peer address are not in the agent contract. Everything
          above is measured off the readings.
        </p>
      )}
    </div>
  );
}

/* =========================================================================
   AND SO IS EVERY OTHER VITAL.

   The uplink got a disclosure because an instantaneous RSSI cannot speak for
   its channel. Neither can an instantaneous anything. `30.9%` CPU is the same
   pixel whether the agent has been at 30% all afternoon or has just come down
   off twenty minutes at 96%; `81.5 GiB` free is the same pixel whether the
   disk is filling at 0.2 GiB/h or 9. THE BAR §13 tell 13 is written about
   latency, but its argument — a summary figure with no distribution behind it
   is a decoration — applies to all seven columns of this band, and tell 15
   ("a threshold that exists only in the operator's head") applies to the six
   that are judged against a limit.

   So every cell is the same disclosure, with the same 20px label:value
   vocabulary, answering four questions the strip cannot:

     LEVEL       mean / min / max / spread over the window, and how many
                 readings sat in each of the channel's PUBLISHED bands — so
                 the limits are on screen as numbers, not as a colour someone
                 has to remember the meaning of.
     EXCURSIONS  how many readings were past a limit, the longest unbroken
                 run of them and when the last one was. A robot that touched
                 90 °C once and a robot that sat there for forty minutes are
                 the same `max` and very different machines.
     TREND       the change across the window and the rate per hour, plus —
                 only for the two channels where running out is a real event
                 — a projection, floored, labelled as a projection, with the
                 evidence it was fitted through printed beside it.
     WINDOW      how many readings the numbers speak for, at what cadence,
                 between which two absolute UTC stamps.

   Speed is judged against no limit (speed is a fact, not a fault) so it gets
   MOTION instead: the trapezoid integral of the readings, and how much of the
   window the robot was actually moving.

   Every number here is arithmetic on readings the API returned. Nothing is
   smoothed, nothing is interpolated across a gap, and the integration refuses
   to cross an interval longer than tolerance rather than inventing distance
   for a stretch nobody observed.
   ========================================================================= */

interface VitalStats {
  /** Readings in the source window, whether or not this channel reported. */
  readings: number;
  /** Readings that carried a value for THIS channel. */
  n: number;
  now: number | null;
  nowLevel: 'ok' | 'warn' | 'bad' | 'unknown';
  min: number | null;
  avg: number | null;
  max: number | null;
  sd: number | null;
  /** Counts per PUBLISHED band, resolved with the same `level()` as the dot. */
  bands: { ok: number; warn: number; bad: number } | null;
  /** Readings at warn or worse. */
  breaches: number;
  /** The longest unbroken run past a limit: readings, and the wall span. */
  runN: number;
  runMs: number;
  lastBreach: string | null;
  firstVal: number | null;
  lastVal: number | null;
  firstTs: string | null;
  lastTs: string | null;
  spanMs: number;
  /** Change per hour across the window's endpoints. Arithmetic, not a fit. */
  ratePerH: number | null;
  /** Trapezoid integral of the readings, in value·seconds. Speed only. */
  integral: number | null;
  /** Wall time the readings were above the moving threshold, and the span
      of the intervals that were integrable at all. */
  movingMs: number | null;
  integratedMs: number | null;
  cadenceMs: number | null;
}

/** Under this, a reported ground speed is the agent's noise floor, not motion. */
const MOVING_MPS = 0.05;

function vitalStats(def: ChannelDef, src: WindowFacts, tolMs: number): VitalStats {
  const pickJudge = def.judge ?? def.pick;
  const vals: number[] = [];
  const ts: number[] = [];
  const iso: string[] = [];
  const judged: (number | null)[] = [];
  for (let i = 0; i < src.readings.length; i += 1) {
    const r = src.readings[i]!;
    const v = def.pick(r);
    if (v === null) continue;
    vals.push(v);
    ts.push(src.times[i] ?? new Date(r.ts).getTime());
    iso.push(r.ts);
    judged.push(pickJudge(r));
  }

  const asc = [...vals].sort((a, b) => a - b);
  const scored = def.warn !== undefined && def.bad !== undefined;

  /* The bands are `level()` itself, exactly as the uplink panel does it: a
     count that disagreed with the cell's own dot would be worse than no
     count at all. */
  let bands: { ok: number; warn: number; bad: number } | null = null;
  let breaches = 0;
  let runN = 0;
  let runMs = 0;
  let lastBreach: string | null = null;
  if (scored) {
    const b = { ok: 0, warn: 0, bad: 0 };
    let start = -1;
    const finish = (end: number) => {
      if (start < 0) return;
      const cnt = end - start + 1;
      const span = ts[end]! - ts[start]!;
      if (cnt > runN || (cnt === runN && span > runMs)) { runN = cnt; runMs = span; }
      start = -1;
    };
    for (let i = 0; i < vals.length; i += 1) {
      const lv = level(judged[i], def.warn!, def.bad!, def.invert ?? false);
      if (lv === 'bad') b.bad += 1;
      else if (lv === 'warn') b.warn += 1;
      else b.ok += 1;
      if (lv === 'warn' || lv === 'bad') {
        breaches += 1;
        lastBreach = iso[i]!;
        if (start < 0) start = i;
      } else {
        finish(i - 1);
      }
    }
    finish(vals.length - 1);
    bands = b;
  }

  /* Distance and moving time, for the one channel where the integral of the
     readings is itself a fact an operator uses. An interval wider than
     tolerance is a gap, not a straight line at the average of its ends, so it
     is skipped and the panel prints how much of the window was integrable. */
  let integral: number | null = null;
  let movingMs: number | null = null;
  let integratedMs: number | null = null;
  if (def.key === 'speed' && vals.length > 1) {
    let d = 0;
    let mv = 0;
    let covered = 0;
    for (let i = 1; i < vals.length; i += 1) {
      const dt = ts[i]! - ts[i - 1]!;
      if (dt <= 0 || dt > tolMs) continue;
      const avg2 = (vals[i]! + vals[i - 1]!) / 2;
      d += avg2 * (dt / 1000);
      covered += dt;
      if (avg2 > MOVING_MPS) mv += dt;
    }
    integral = d;
    movingMs = mv;
    integratedMs = covered;
  }

  const firstVal = vals.length > 0 ? vals[0]! : null;
  const lastVal = vals.length > 0 ? vals[vals.length - 1]! : null;
  const spanMs = ts.length > 1 ? ts[ts.length - 1]! - ts[0]! : 0;

  return {
    readings: src.readings.length,
    n: vals.length,
    now: lastVal,
    nowLevel: scored
      ? level(judged[judged.length - 1] ?? null, def.warn!, def.bad!, def.invert ?? false)
      : lastVal === null ? 'unknown' : 'ok',
    min: asc.length > 0 ? asc[0]! : null,
    avg: mean(vals),
    max: asc.length > 0 ? asc[asc.length - 1]! : null,
    sd: stdev(vals),
    bands,
    breaches,
    runN,
    runMs,
    lastBreach,
    firstVal,
    lastVal,
    firstTs: iso.length > 0 ? iso[0]! : null,
    lastTs: iso.length > 0 ? iso[iso.length - 1]! : null,
    spanMs,
    ratePerH: firstVal === null || lastVal === null || spanMs <= 0
      ? null
      : (lastVal - firstVal) / (spanMs / 3_600_000),
    integral,
    movingMs,
    integratedMs,
    cadenceMs: src.cadenceMs,
  };
}

/** `-72 … -58 dBm` in the channel's own units — the limits, printed. */
function bandLabels(def: ChannelDef): [string, string, string] {
  const u = def.judgeUnit ?? def.unit;
  const s = unitSuffix(u);
  const w = String(def.warn);
  const b = String(def.bad);
  return def.invert === true
    ? [`under ${w}${s}`, `${w} … ${b}${s}`, `at or over ${b}${s}`]
    : [`above ${w}${s}`, `${b} … ${w}${s}`, `at or under ${b}${s}`];
}

/**
 * Esc closes, a click outside the cell closes, and focus starts inside so a
 * keyboard operator is not left behind on the trigger. A disclosure, not a
 * modal: nothing under it is inert and the page keeps updating.
 *
 * The outside test is `[data-disclose]`, not the panel, because the trigger
 * and the panel share that container — testing the panel alone would close on
 * the trigger's own mousedown and the click that followed would reopen it, so
 * the cell could never be toggled shut.
 */
function useDisclose(onClose: (restore: boolean) => void) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(true); };
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (el !== null && el.closest?.('[data-disclose="true"]') !== null) return;
      onClose(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown, true);
    };
  }, [onClose]);
  return ref;
}

function VitalPanel({
  def,
  stats,
  readings,
  robot,
  windowLabel,
  scoped,
  titleId,
  side,
  onClose,
}: {
  def: ChannelDef;
  stats: VitalStats;
  /** The same readings the stats were measured over — the battery projection
      is fitted through their trailing hour so it agrees, to the digit, with
      the runway chip at the other end of this band. */
  readings: readonly TelemetryReading[];
  robot: Robot;
  windowLabel: string;
  /** The stats came from the window; false ⇒ from the robot's last contact. */
  scoped: boolean;
  titleId: string;
  /** Which cell edge the panel hangs off, so it never runs off the monitor. */
  side: 'left' | 'right';
  onClose: (restore: boolean) => void;
}) {
  const ref = useDisclose(onClose);
  const u = unitSuffix(def.unit);
  const scored = def.warn !== undefined && def.bad !== undefined;
  const drift = stats.now !== null && stats.avg !== null && stats.sd !== null
    && stats.sd > 0 && Math.abs(stats.now - stats.avg) > stats.sd
    ? `${signed(stats.now - stats.avg, def.digits)} vs mean`
    : undefined;
  const breachTone: Tone | undefined = stats.bands === null || stats.breaches === 0
    ? undefined
    : stats.bands.bad > 0 ? 'error' : 'warn';

  /* The projection. Only two channels have one, because only two of them run
     OUT: a battery reaches 0% and a disk reaches 0 GiB, and both are events an
     operator plans a recovery around. CPU does not "run out" at 100% and
     temperature does not run out at all, so neither gets a countdown that
     would look exactly as authoritative as these two. */
  const runway = def.key === 'batt' ? batteryRunway(readings) : null;
  const diskFall = def.key === 'disk' && stats.ratePerH !== null && stats.ratePerH < 0
    && stats.lastVal !== null
    ? { leftMs: (stats.lastVal / -stats.ratePerH) * 3_600_000 }
    : null;

  return (
    <div
      className={styles.linkPop}
      data-side={side}
      ref={ref}
      tabIndex={-1}
      role="dialog"
      aria-labelledby={titleId}
    >
      <div className={styles.lhHead}>
        <span className={styles.lhTitle} id={titleId}>{def.label}</span>
        <span className={styles.lhScope}>
          {stats.n === 0
            ? 'no readings'
            : scoped ? `${windowLabel} window` : 'last contact'}
        </span>
        <IconButton
          icon="close"
          label={`Close ${lowerLabel(def.label)} detail`}
          size="sm"
          onClick={() => onClose(true)}
        />
      </div>

      <div className={styles.lhBody}>
        {stats.n === 0 ? (
          <p className={styles.lhNone}>
            {robot.robot_id} has reported no {lowerLabel(def.label)} reading
            {stats.readings === 0 ? ' at all' : ` in the ${windowLabel} window`}.
            There is nothing to measure.
          </p>
        ) : (
          <>
            <LinkSection
              label="Level"
              meta={`${stats.n} reading${stats.n === 1 ? '' : 's'}`}
            />
            <LinkRow
              label="Now"
              value={stats.now === null ? NO_VALUE : stats.now.toFixed(def.digits)}
              unit={stats.now === null ? undefined : u}
              tone={levelTone(stats.nowLevel)}
              aside={drift}
            />
            <LinkRow
              label="Mean"
              value={stats.avg === null ? NO_VALUE : stats.avg.toFixed(def.digits)}
              unit={stats.avg === null ? undefined : u}
            />
            <LinkRow
              label="Min / max"
              value={stats.min === null || stats.max === null
                ? NO_VALUE
                : `${stats.min.toFixed(def.digits)} / ${stats.max.toFixed(def.digits)}`}
              unit={stats.min === null ? undefined : u}
            />
            <LinkRow
              label="Spread (σ)"
              value={stats.sd === null ? NO_VALUE : stats.sd.toFixed(def.digits)}
              unit={stats.sd === null ? undefined : u}
            />
            {stats.bands !== null && (
              <div className={styles.lhDist}>
                <Distribution
                  barWidth={104}
                  items={[
                    {
                      key: 'ok', label: bandLabels(def)[0], count: stats.bands.ok,
                      tone: 'nominal',
                    },
                    {
                      key: 'warn', label: bandLabels(def)[1], count: stats.bands.warn,
                      tone: 'warn',
                    },
                    {
                      key: 'bad', label: bandLabels(def)[2], count: stats.bands.bad,
                      tone: 'error',
                    },
                  ]}
                />
              </div>
            )}

            {scored && (
              <>
                <LinkSection
                  label="Excursions"
                  meta={def.judgeLabel === undefined ? undefined : `on ${def.judgeLabel}`}
                />
                <LinkRow
                  label="Past a limit"
                  value={`${stats.breaches} / ${stats.n}`}
                  tone={breachTone}
                />
                {stats.breaches > 0 && (
                  <>
                    <LinkRow
                      label="Longest run"
                      value={duration(stats.runMs)}
                      aside={`${stats.runN} reading${stats.runN === 1 ? '' : 's'}`}
                      tone={breachTone}
                    />
                    <LinkRow
                      label="Last"
                      value={stats.lastBreach === null
                        ? NO_VALUE
                        : relative(stats.lastBreach)}
                    />
                  </>
                )}
              </>
            )}

            {def.key === 'speed' && stats.integral !== null && (
              <>
                <LinkSection label="Motion" meta="trapezoid" />
                <LinkRow
                  label="Distance"
                  value={stats.integral < 1000
                    ? stats.integral.toFixed(0)
                    : (stats.integral / 1000).toFixed(2)}
                  unit={stats.integral < 1000 ? ' m' : ' km'}
                  aside={stats.integratedMs === null
                    ? undefined
                    : `over ${duration(stats.integratedMs)}`}
                />
                <LinkRow
                  label="Moving"
                  value={stats.movingMs === null ? NO_VALUE : duration(stats.movingMs)}
                  aside={`> ${MOVING_MPS} m/s`}
                />
              </>
            )}

            <LinkSection
              label="Trend"
              meta={stats.spanMs > 0 ? `over ${duration(stats.spanMs)}` : undefined}
            />
            <LinkRow
              label="Change"
              value={stats.firstVal === null || stats.lastVal === null
                ? NO_VALUE
                : signed(stats.lastVal - stats.firstVal, def.digits)}
              unit={stats.firstVal === null ? undefined : u}
              aside={stats.firstVal === null
                ? undefined
                : `from ${stats.firstVal.toFixed(def.digits)}`}
            />
            <LinkRow
              label="Rate"
              value={stats.ratePerH === null ? NO_VALUE : signed(stats.ratePerH, def.digits)}
              unit={stats.ratePerH === null ? undefined : `${u}/h`}
              aside={stats.ratePerH === null || Math.abs(stats.ratePerH) < 0.05
                ? undefined
                : stats.ratePerH < 0 ? def.fallLabel : def.riseLabel}
            />
            {runway !== null && (
              <>
                {/* Its own section, because it is fitted through the trailing
                    hour and TREND above is the whole window — on a robot that
                    charged all afternoon and started discharging twenty
                    minutes ago the two carry opposite signs, and both are
                    true. Printing them under one heading would read as a
                    contradiction rather than as two spans. */}
                <LinkSection label="Projection" meta={`last ${duration(runway.overMs)}`} />
                <LinkRow
                  label="Falling"
                  value={runway.ratePctPerH.toFixed(1)}
                  unit={`${unitSuffix('%')}/h`}
                  aside={`${runway.samples} readings`}
                />
                <LinkRow
                  label="Empty in"
                  value={runwayLabel(runway.leftMs)}
                  aside="straight line"
                  tone={def.warn !== undefined && runway.level <= def.warn
                    ? def.bad !== undefined && runway.level <= def.bad ? 'error' : 'warn'
                    : undefined}
                />
              </>
            )}
            {diskFall !== null && (
              <>
                <LinkSection label="Projection" meta={`over ${duration(stats.spanMs)}`} />
                <LinkRow
                  label="Full in"
                  value={runwayLabel(diskFall.leftMs)}
                  aside="straight line"
                />
              </>
            )}

            <LinkSection label="Window" meta="utc" />
            <LinkRow
              label="Readings"
              value={stats.n === stats.readings
                ? String(stats.n)
                : `${stats.n} / ${stats.readings}`}
              aside={stats.n === stats.readings ? undefined : 'reported'}
            />
            <LinkRow
              label="Cadence"
              value={stats.cadenceMs === null ? NO_VALUE : duration(stats.cadenceMs)}
            />
            <LinkRow label="First" value={utc(stats.firstTs)} />
            <LinkRow label="Last" value={utc(stats.lastTs)} />
          </>
        )}
      </div>

      <p className={styles.lhFoot}>{def.hint}</p>
    </div>
  );
}

function VitalsBand({
  facts,
  all,
  live,
  robot,
  read,
  windowLive,
  windowLabel,
  streamConnected,
  commandsAt,
  scans,
  onGoLive,
}: {
  facts: WindowFacts;
  /** Everything the store returned — the robot's own last contact. */
  all: WindowFacts;
  /** The telemetry read the cells are counted from. */
  read: Read;
  /** The ROBOT is in contact right now. */
  live: boolean;
  robot: Robot;
  /** The WINDOW is tracking the newest reading. Two different facts. */
  windowLive: boolean;
  /** How wide the window is, for the `no data in 6h` qualifier. */
  windowLabel: string;
  /** The command channel is being pushed over MQTT rather than polled. */
  streamConnected: boolean;
  /** When the command queue last came back from the API. */
  commandsAt: number | null;
  scans: readonly Scan[] | undefined;
  onGoLive: () => void;
}) {
  /* One cell open at a time, keyed by channel. Two diagnostics panels over
     the same band would overlap each other and neither would be readable. */
  const [open, setOpen] = useState<string | null>(null);
  const panelId = useId();
  const btns = useRef<Record<string, HTMLButtonElement | null>>({});
  const openRef = useRef<string | null>(null);
  openRef.current = open;
  const close = useCallback((restore: boolean) => {
    const k = openRef.current;
    setOpen(null);
    if (restore && k !== null) btns.current[k]?.focus();
  }, []);

  /* Seven cells of one instrument are one control group, so ←/→ walk them and
     Home/End jump the ends — the same keyboard an operator already has on the
     tab strip below (THE BAR §6: every list is arrow-navigable). */
  const onBandKey = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    const keys = CHANNELS.map((c) => c.key);
    const cur = keys.findIndex((k) => btns.current[k] === e.target);
    if (cur < 0) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = (cur + 1) % keys.length;
    else if (e.key === 'ArrowLeft') next = (cur - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = keys.length - 1;
    if (next < 0) return;
    e.preventDefault();
    btns.current[keys[next]!]?.focus();
  }, []);

  const src = bandSource(facts, all);
  const { latest, readings } = src;
  const tolMs = tolerance(src.cadenceMs);
  /* Three separate reasons the numbers are not current, one rendering: the
     robot is out of contact, the window is parked on history, or the window
     has run past the last thing the robot said. The values stay — they are
     the last truth there is — and stop looking live. */
  const outside = facts.readings.length === 0 && all.readings.length > 0;
  const stale = !live || !windowLive || outside;
  const [bandRef, band] = useBox();

  /* The trace takes whatever the column has left after the reading, rather
     than a hard 62px that overruns `130.8 GiB` on a 1280 monitor. Below ~26px
     a sparkline is a smudge, so it goes entirely and the column keeps the one
     thing it must never truncate: the number. Above, it grows to 96px rather
     than parking a 62px trace against the cell's right edge and spending the
     rest of a 1920 monitor's column on the gap between the two — extra width
     on this band should buy history, not air. */
  const colW = band.w > 0 ? band.w / CHANNELS.length : 0;
  const traceW = Math.min(96, Math.floor(colW - 100));
  const showTrace = traceW >= 22;

  return (
    <div className={styles.band} data-stale={stale}>
      <div
        className={styles.channels}
        ref={bandRef}
        role="group"
        aria-label="Vitals"
        onKeyDown={onBandKey}
      >
        {CHANNELS.map((def, idx) => {
          const values = readings.map((r) => def.pick(r));
          const judged = def.judge === undefined
            ? values
            : readings.map((r) => def.judge!(r));
          const times = readings.map((r) => new Date(r.ts).getTime());
          const box = channelBox(def, values);
          const value = latest === null ? null : def.pick(latest);
          const lv = channelLevel(def, latest);
          const tone = levelTone(lv);
          const breached = lv === 'warn' || lv === 'bad';
          const known = value !== null;
          const isOpen = open === def.key;
          /* The panel is 336px wide and anchored to its own cell, so the three
             right-hand columns hang it off their right edge — otherwise GNSS,
             the last column, would put half its diagnostics off the monitor. */
          const side = idx >= CHANNELS.length - 3 ? 'right' : 'left';

          return (
            <div
              key={def.key}
              className={styles.vital}
              data-tone={tone}
              data-toned={breached}
              data-disclose="true"
              data-open={isOpen}
            >
              <button
                type="button"
                ref={(el) => { btns.current[def.key] = el; }}
                className={styles.vitalBtn}
                aria-expanded={isOpen}
                aria-haspopup="dialog"
                aria-label={
                  `${def.label} ${known ? `${value.toFixed(def.digits)} ${def.unit}` : 'no reading'}`
                  + ` — open ${lowerLabel(def.label)} detail`
                }
                onClick={() => setOpen((k) => (k === def.key ? null : def.key))}
              >
                {/* head */}
                <div className={styles.vitalHead}>
                  <StatusDot tone={tone} hollow={!known} />
                  <span className={styles.vitalName}>{def.label}</span>
                  {breached && (
                    <Icon
                      name={lv === 'bad' ? 'alert-octagon' : 'alert-triangle'}
                      size={11}
                      title={lv === 'bad' ? 'past hard limit' : 'past warning limit'}
                    />
                  )}
                  {/* A permanent chevron, on every cell: a hover-revealed one
                      would mean the operator cannot SEE that the detail exists
                      (THE BAR §6). */}
                  <span className={styles.vitalMore} aria-hidden="true">
                    <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={11} />
                  </span>
                </div>
                {/* readout */}
                <div className={styles.vitalBody}>
                  <span className={styles.vitalValue}>
                    {known ? value.toFixed(def.digits) : NO_VALUE}
                    {known && <Unit unit={def.unit} className={styles.vitalUnit} />}
                  </span>
                  {showTrace && (
                    <span className={styles.vitalTrace}>
                      <Trace
                        def={def}
                        values={values}
                        judged={judged}
                        times={times}
                        box={box}
                        guided={box?.guided === true}
                        width={traceW}
                        height={20}
                        head
                        probe
                        label={`${def.label} over the telemetry window`}
                      />
                    </span>
                  )}
                </div>
              </button>

              {isOpen && (def.key === 'rssi' ? (
                <LinkPanel
                  stats={linkStats(src, robot, tolMs)}
                  robot={robot}
                  windowLabel={windowLabel}
                  scoped={facts.readings.length > 0}
                  streamConnected={streamConnected}
                  titleId={`${panelId}-${def.key}`}
                  side={side}
                  onClose={close}
                />
              ) : (
                <VitalPanel
                  def={def}
                  stats={vitalStats(def, src, tolMs)}
                  readings={readings}
                  robot={robot}
                  windowLabel={windowLabel}
                  scoped={facts.readings.length > 0}
                  titleId={`${panelId}-${def.key}`}
                  side={side}
                  onClose={close}
                />
              ))}
            </div>
          );
        })}
      </div>
      <BandEnd
        facts={facts}
        all={all}
        live={live}
        robot={robot}
        read={read}
        windowLive={windowLive}
        windowLabel={windowLabel}
        streamConnected={streamConnected}
        commandsAt={commandsAt}
        scans={scans}
        onGoLive={onGoLive}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------
   RUNWAY. How long the battery has left, at the rate it has actually been
   falling — arithmetic on the readings, not a model, and it says so.

   The level is on screen already; the number an operator plans a recovery
   with is the TIME, and it is the one quantity here that has to be derived
   rather than read. The derivation is deliberately the dumbest defensible
   one: the straight line through the trailing hour of readings, projected to
   zero. No smoothing, no per-cell curve, no learned discharge model — those
   would all be claims the platform cannot support, and an operator who has
   to decide whether to drive a robot home is owed the crude honest figure
   with its evidence printed beside it.

   It returns null unless there is real evidence: four readings, ten minutes
   of span, and a fall big enough not to be sensor noise.
   ------------------------------------------------------------------------- */

const RUNWAY_SPAN_MS = 3_600_000;
const RUNWAY_MIN_MS = 600_000;
const RUNWAY_MIN_DROP = 0.5;

interface Runway {
  /** Milliseconds to 0%, at the observed rate. */
  leftMs: number;
  /** Percentage points per hour, positive = falling. */
  ratePctPerH: number;
  /** How much evidence the line was fitted through. */
  overMs: number;
  samples: number;
  level: number;
}

/**
 * A projection is not a measurement, and it must not be set like one.
 * `57m 48s` claims a second of accuracy on an extrapolation; the figure is
 * therefore floored — never rounded — to five minutes, so the chip is always
 * the pessimistic reading of its own arithmetic. The exact numbers and the
 * evidence they came from live in the title.
 */
function runwayLabel(ms: number): string {
  const step = 300_000;
  const floored = Math.floor(Math.max(0, ms) / step) * step;
  if (floored < step) return 'under 5m';
  if (floored < 3_600_000) return `~${Math.round(floored / 60_000)}m`;
  return `~${duration(floored)}`;
}

function batteryRunway(readings: readonly TelemetryReading[]): Runway | null {
  const pts: { t: number; v: number }[] = [];
  for (const r of readings) {
    if (r.battery_pct === null) continue;
    pts.push({ t: new Date(r.ts).getTime(), v: r.battery_pct });
  }
  if (pts.length < 4) return null;
  const end = pts[pts.length - 1]!.t;
  const tail = pts.filter((p) => end - p.t <= RUNWAY_SPAN_MS);
  if (tail.length < 4) return null;
  const a = tail[0]!;
  const b = tail[tail.length - 1]!;
  const overMs = b.t - a.t;
  if (overMs < RUNWAY_MIN_MS) return null;
  const drop = a.v - b.v;
  if (drop < RUNWAY_MIN_DROP) return null;
  const perMs = drop / overMs;
  return {
    leftMs: b.v / perMs,
    ratePctPerH: perMs * 3_600_000,
    overMs,
    samples: tail.length,
    level: b.v,
  };
}

/* -------------------------------------------------------------------------
   THE THREE CHANNELS.

   One `● Live 12s old` light for the whole page is THE BAR §13 tell 27: a
   robot does not have "a connection", it has several, and they fail
   independently. Telemetry can be arriving at cadence while the command
   queue's push transport is down; captures can sit half-uploaded for a day
   on a robot whose vitals are perfect. Skydio runs FUS, VIO and GPS as three
   separate meters in 119px for exactly this reason
   (skydio-remote-flight-deck-live.png), and the Command queue panel on the
   surface below already proves the idiom with its own `● Polling 20s`.

   So the band ends in three channel readouts, same shape, same size, each
   with its own verdict dot resolved off its OWN rule:

     TLM   telemetry — age of the newest reading against the tolerance the
           rest of this page already uses (2.5× the agent's median cadence).
     CMD   the command channel — whether it is being pushed over MQTT or
           polled, and how long ago the queue last came back.
     CAP   capture sync — sessions still uploading, and how long the oldest
           one has gone without moving.

   Everything to the right of them qualifies all three: whether the window is
   live or parked, and the two deadlines worth a chip.
   ------------------------------------------------------------------------- */

/** The command queue's REST fallback cadence — mirrors useRobotCommands. */
const CMD_POLL_MS = 20_000;

/** A capture session that has not moved in this long is not syncing, it is
    stuck, and an operator should go and look. Published in the readout's own
    title rather than living in someone's head (THE BAR §13 tell 15). */
const SYNC_STALL_MS = 6 * 3_600_000;

interface ChanRow {
  key: string;
  label: string;
  tone: Tone;
  /** Nothing is arriving on this channel — the dot goes hollow, never green. */
  hollow: boolean;
  value: string;
  /** The channel's own qualifier: its cadence, its transport, its backlog age. */
  note: string;
  title: string;
}

function ChannelStack({ rows }: { rows: readonly ChanRow[] }) {
  return (
    <div className={styles.chans} role="group" aria-label="Channel health">
      {rows.map((r) => (
        <div key={r.key} className={styles.chan} data-tone={r.tone} title={r.title}>
          <span className={styles.chanName}>
            <StatusDot tone={r.tone} hollow={r.hollow} />
            {r.label}
          </span>
          <span className={styles.chanValue}>{r.value}</span>
          <span className={styles.chanNote}>{r.note}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * The qualifier on all seven vitals — is this now, and how old — split into
 * the three channels that answer it independently, plus the window's own
 * live/replay mode and the deadlines worth a chip. Split out from the band so
 * the one-second ticker re-renders 150px of text rather than seven sparklines.
 */
function BandEnd({
  facts,
  all,
  live,
  robot,
  read,
  windowLive,
  windowLabel,
  streamConnected,
  commandsAt,
  scans,
  onGoLive,
}: {
  facts: WindowFacts;
  all: WindowFacts;
  live: boolean;
  robot: Robot;
  read: Read;
  windowLive: boolean;
  windowLabel: string;
  streamConnected: boolean;
  commandsAt: number | null;
  scans: readonly Scan[] | undefined;
  onGoLive: () => void;
}) {
  const now = useNow(true);
  const src = bandSource(facts, all);
  const latest = src.latest;
  /* The age of the reading in the cells to the left — and where there is no
     reading at all, the registry's own `last_seen`, which is the same fact
     arriving by a different route. `never` is reserved for a robot that has
     neither: registered, never phoned home. */
  const contactMs = latest !== null
    ? new Date(latest.ts).getTime()
    : robot.last_seen === null ? null : new Date(robot.last_seen).getTime();
  const ageMs = contactMs === null ? null : Math.max(0, now - contactMs);
  /* A fact about the WINDOW, said separately from the fact about the robot:
     the two were previously fused into one string, and the fusion is what
     made a 7h-quiet robot read `never reported` at 6h and `7h 39m old` at 7d.
     A robot with zero readings ever does not get this — its silence is not a
     property of the span. */
  /* AND NOT WHILE THE SOURCE IS UNREAD. `none in 6h` is a measurement of the
     window — the console looked and found nothing in it — and a read that
     never landed did not look. The registry's `last_seen` is what keeps the
     TLM cell honest in that state; this clause is what stops the band adding
     a claim the registry cannot support. */
  const gapInWindow = !unread(read)
    && facts.readings.length === 0
    && (all.readings.length > 0 || robot.last_seen !== null);
  const certLeftMs = robot.cert_not_after === null
    ? null
    : new Date(robot.cert_not_after).getTime() - now;

  /* The second deadline that stops a robot working. Promoted to a chip on the
     same terms as the certificate — only inside its warning band, so a robot
     at 80% carries no chip at all — and computed off the same readings the
     BATTERY cell two metres to the left is showing. */
  const battDef = CHANNELS[0]!;
  /* Only ever a claim about NOW: the robot has to be in contact and the window
     has to be the live one. Projecting a discharge curve out of a window the
     operator scrubbed to last night would put a countdown on the screen that
     ran out hours ago. */
  const runway = live && windowLive && facts.readings.length > 0
    ? batteryRunway(facts.readings)
    : null;
  const runwayShown = runway !== null
    && battDef.warn !== undefined
    && runway.level <= battDef.warn;

  /* ---- TLM ------------------------------------------------------------- */
  const tolMs = tolerance(src.cadenceMs);
  const tlmLevel = ageMs === null ? 'unknown' : level(ageMs, tolMs, tolMs * 2, true);
  const tlm: ChanRow = {
    key: 'tlm',
    label: 'TLM',
    tone: ageMs === null ? 'neutral' : levelTone(tlmLevel),
    hollow: ageMs === null,
    value: ageMs === null ? 'never' : duration(ageMs),
    note: src.cadenceMs === null ? NO_VALUE : duration(src.cadenceMs),
    title: contactMs === null
      ? `${robot.robot_id} has never reported telemetry.`
      : `Newest reading ${utc(latest === null ? robot.last_seen : latest.ts)} — `
        + `${duration(ageMs ?? 0)} old at a median cadence of `
        + `${src.cadenceMs === null ? 'unknown' : duration(src.cadenceMs)}. `
        + `Amber past ${duration(tolMs)}, red past ${duration(tolMs * 2)}.`,
  };

  /* ---- CMD ------------------------------------------------------------- */
  const cmdAge = commandsAt === null ? null : Math.max(0, now - commandsAt);
  const cmdBudget = streamConnected ? 60_000 : CMD_POLL_MS;
  const cmdLevel = cmdAge === null
    ? 'unknown'
    : level(cmdAge, cmdBudget * 1.5, cmdBudget * 3, true);
  const cmd: ChanRow = {
    key: 'cmd',
    label: 'CMD',
    tone: cmdAge === null ? 'neutral' : levelTone(cmdLevel),
    hollow: cmdAge === null,
    value: cmdAge === null ? NO_VALUE : duration(cmdAge),
    note: streamConnected ? 'mqtt' : `poll ${duration(CMD_POLL_MS)}`,
    title: streamConnected
      ? 'Command state is pushed over MQTT; this is how long since the last frame or reconcile.'
      : 'No MQTT device plane in this deployment — the command queue is polled over REST '
        + `every ${duration(CMD_POLL_MS)}. This is the age of the last successful poll.`,
  };

  /* ---- CAP ------------------------------------------------------------- */
  let cap: ChanRow;
  if (scans === undefined) {
    cap = {
      key: 'cap', label: 'CAP', tone: 'neutral', hollow: true,
      value: NO_VALUE, note: '', title: 'Capture sessions have not loaded.',
    };
  } else if (scans.length === 0) {
    cap = {
      key: 'cap', label: 'CAP', tone: 'neutral', hollow: true,
      value: 'none', note: '',
      title: `No capture sessions on record for ${robot.robot_id}.`,
    };
  } else {
    const inflight = scans.filter((sc) => sc.sync_state !== null && sc.sync_state !== 'synced');
    const newest = Math.max(...scans.map((sc) => new Date(sc.updated_at).getTime()));
    if (inflight.length === 0) {
      cap = {
        key: 'cap', label: 'CAP', tone: 'nominal', hollow: false,
        value: 'synced', note: duration(Math.max(0, now - newest)),
        title: `All ${scans.length} session${scans.length === 1 ? '' : 's'} are off the robot. `
          + `Newest change ${utc(new Date(newest).toISOString())}.`,
      };
    } else {
      const oldest = Math.min(...inflight.map((sc) => new Date(sc.updated_at).getTime()));
      const stalledMs = Math.max(0, now - oldest);
      const pending = inflight.reduce((a, sc) => a + sc.size_bytes, 0);
      cap = {
        key: 'cap',
        label: 'CAP',
        tone: stalledMs > SYNC_STALL_MS ? 'warn' : 'nominal',
        hollow: false,
        value: `${inflight.length} sync`,
        note: duration(stalledMs),
        title: `${inflight.length} capture session${inflight.length === 1 ? '' : 's'} still uploading `
          + `(${bytes(pending)}). The oldest has not changed for ${duration(stalledMs)}; `
          + `past ${duration(SYNC_STALL_MS)} without progress it is treated as stalled.`,
      };
    }
  }

  return (
    <div className={styles.bandEnd} data-replay={!windowLive}>
      <ChannelStack rows={[tlm, cmd, cap]} />
      <div className={styles.bandTail}>
        <div className={styles.bandMode}>
          {windowLive ? (
            <LiveTag live={live} label="Contact" pulse={live} />
          ) : (
            /* PARKED ON HISTORY. Not `Live off`, not a greyed `Live`: the word
               itself changes, the dot goes hollow and stops pulsing, and the
               whole marker drops to --unknown — because the seven readings to
               the left are the newest INSIDE a window that is no longer the
               current one, and a badge carrying the word `Live` beside a
               15-hour-old number is the exact lie this band exists to prevent
               (formant-timeline-data-presence-scrubber.png: the LIVE pill
               greys the instant you scrub off live). The escape is here rather
               than only on the instrument, so it survives a tab switch. */
            <>
              <span
                className={styles.replayTag}
                title={
                  'The window is parked on history. These readings are the newest '
                  + 'inside it, not the newest there are.'
                }
              >
                <StatusDot tone="stale" hollow />
                Replay
              </span>
              <button
                type="button"
                className={styles.goLive}
                title="Return the window to the newest reading"
                onClick={onGoLive}
              >
                Go Live
              </button>
            </>
          )}
          {gapInWindow && (
            <span
              className={styles.bandGap}
              title={
                `Nothing arrived inside the ${windowLabel} window. The readings above are `
                + 'the newest there are; the scrubber shows where they sit.'
              }
            >
              none in {windowLabel}
            </span>
          )}
        </div>
        {(runwayShown || (certLeftMs !== null && certLeftMs <= CERT_WARN_S * 1000)) && (
          <div className={styles.bandChips}>
            {/* The one deadline that stops a robot working and is invisible
                until it fires. Promoted out of the rail only inside its warning
                window, so a healthy robot's page carries no chip at all. */}
            {runwayShown && (
              <Chip
                tone={battDef.bad !== undefined && runway.level <= battDef.bad ? 'error' : 'warn'}
                icon="battery"
                title={
                  `${runway.level.toFixed(1)}% falling ${runway.ratePctPerH.toFixed(1)} %/h over the `
                  + `last ${duration(runway.overMs)} (${runway.samples} readings) — empty in about `
                  + `${duration(runway.leftMs)} if it keeps that rate. Straight line through those `
                  + 'readings, not a discharge model.'
                }
              >
                Empty {runwayLabel(runway.leftMs)}
              </Chip>
            )}
            {certLeftMs !== null && certLeftMs <= CERT_WARN_S * 1000 && (
              <Chip
                tone={certLeftMs <= CERT_BAD_S * 1000 ? 'error' : 'warn'}
                icon="key"
                title={`Client certificate valid until ${utc(robot.cert_not_after)} — the robot cannot authenticate after that.`}
              >
                {certLeftMs <= 0 ? 'Cert expired' : `Cert ${duration(certLeftMs)}`}
              </Chip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Position — the track, projected. Hand-drawn, because what is wanted here is
   a 1px trace of where the robot has actually been in this window, in metres,
   with the scale printed. A basemap would answer a different question and the
   platform has no tile source wired.
   ------------------------------------------------------------------------- */

const TRACK_W = 240;
const TRACK_H = 132;
const TRACK_PAD = 12;

function niceMetres(raw: number): number {
  const steps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000];
  for (const s of steps) if (raw <= s) return s;
  return 10_000;
}

function TrackPlot({ facts, scoped, robot, read }: {
  facts: WindowFacts;
  /** These readings are the window's; false ⇒ they are the last contact's. */
  scoped: boolean;
  robot: Robot;
  /** The read the fixes came from. */
  read: Read;
}) {
  const fixes = facts.readings.filter(
    (r): r is TelemetryReading & { lat: number; lon: number } =>
      r.lat != null && r.lon != null,
  );
  /* The empty state occupies exactly the height the track will, so the rail
     does not reflow the instant the first fix lands. */
  if (fixes.length === 0) {
    /* `No position fixes on record` is a claim about the ROBOT'S HISTORY and it
       was being made off a failed read: rover-01 has a track, and the panel
       said it had never had one. The fault line answers WHY and the module's
       own line answers WHAT IS THERE, in the same two-part shape every other
       degraded panel on this surface takes. */
    const stopped = unread(read);
    return (
      <div className={styles.track}>
        {stopped && <FaultLine read={read} />}
        <div className={styles.trackAwait}>
          {stopped ? (
            <Awaiting label={unreadLine(read)} />
          ) : (
            <Awaiting
              label={scoped ? 'No position fixes in this window' : 'No position fixes on record'}
              detail="no lat/lon on any reading"
            />
          )}
        </div>
      </div>
    );
  }

  const lat0 = fixes[0]!.lat;
  const lon0 = fixes[0]!.lon;
  const mPerLat = 110_540;
  const mPerLon = 111_320 * Math.cos((lat0 * Math.PI) / 180);
  const pts = fixes.map((r) => ({
    x: (r.lon - lon0) * mPerLon,
    y: (r.lat - lat0) * mPerLat,
    ts: r.ts,
  }));
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const spanX = Math.max(...xs) - Math.min(...xs);
  const spanY = Math.max(...ys) - Math.min(...ys);
  const span = Math.max(spanX, spanY, 5);
  const scale = Math.min(TRACK_W - TRACK_PAD * 2, TRACK_H - TRACK_PAD * 2) / span;
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  const px = (x: number) => TRACK_W / 2 + (x - cx) * scale;
  const py = (y: number) => TRACK_H / 2 - (y - cy) * scale;

  const d = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p.x).toFixed(1)},${py(p.y).toFixed(1)}`)
    .join(' ');
  const head = pts[pts.length - 1]!;
  const heading = robot.heading_deg;
  const bar = niceMetres(span / 3);
  const barPx = bar * scale;
  const tone = STATUS_STATE[robot.status].tone;

  return (
    <div className={styles.track} data-outside={!scoped}>
      <svg
        viewBox={`0 0 ${TRACK_W} ${TRACK_H}`}
        className={styles.trackSvg}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Track of ${pts.length} fixes spanning about ${Math.round(span)} metres`}
      >
        <path className={styles.trackLine} d={d} />
        {pts.map((p) => (
          <circle key={p.ts} className={styles.trackDot} cx={px(p.x)} cy={py(p.y)} r={0.9} />
        ))}
        <g
          transform={`translate(${px(head.x).toFixed(1)} ${py(head.y).toFixed(1)}) rotate(${(heading ?? 0).toFixed(1)})`}
          data-tone={tone}
        >
          <path className={styles.trackHead} d="M0,-6 L4,5 L0,2.5 L-4,5 Z" />
        </g>
        <g className={styles.trackScale} transform={`translate(${TRACK_PAD} ${TRACK_H - 8})`}>
          <line x1={0} y1={0} x2={barPx} y2={0} />
          <line x1={0} y1={-3} x2={0} y2={3} />
          <line x1={barPx} y1={-3} x2={barPx} y2={3} />
          <text x={barPx + 4} y={3}>{bar} m</text>
        </g>
      </svg>
      {/* The track is drawn dim when it predates the window, because the same
          panel's `Last fix` age is then the only thing tying it to a clock. */}
      <div className={styles.trackFoot}>
        <span>{pts.length} fixes</span>
        <span>{scoped ? 'local ENU from first fix' : 'before this window · local ENU'}</span>
      </div>
    </div>
  );
}

/**
 * The heading, with its reference printed beside it — `28° N`, exactly the way
 * skydio-remote-flight-deck-live.png boxes `18° N` on its heading tape. A
 * bearing with no datum on screen is THE BAR §13 tell 14; what the F3 contract
 * genuinely publishes is `heading_deg` clockwise from north, and whether that
 * north is true or magnetic is not in it — so the title says so rather than
 * the console picking one.
 */
function HeadingDial({ deg }: { deg: number | null }) {
  const known = deg != null && !Number.isNaN(deg);
  return (
    <div
      className={styles.dial}
      data-unknown={!known}
      title={known
        ? `${deg.toFixed(0)}° clockwise from north. The agent reports heading_deg; `
          + 'the contract does not distinguish true from magnetic north.'
        : 'No heading on the newest reading.'}
    >
      <svg viewBox="0 0 44 44" width={44} height={44} role="img"
           aria-label={known ? `Heading ${deg.toFixed(0)} degrees` : 'Heading unknown'}>
        <circle className={styles.dialRing} cx="22" cy="22" r="18" />
        {[0, 90, 180, 270].map((a) => {
          const r = (a * Math.PI) / 180;
          return (
            <line
              key={a}
              className={styles.dialTick}
              x1={22 + 15 * Math.sin(r)} y1={22 - 15 * Math.cos(r)}
              x2={22 + 18 * Math.sin(r)} y2={22 - 18 * Math.cos(r)}
            />
          );
        })}
        <text className={styles.dialN} x="22" y="11">N</text>
        {known && (
          <g transform={`rotate(${deg.toFixed(1)} 22 22)`}>
            <path className={styles.dialNeedle} d="M22,7 L25,24 L22,21 L19,24 Z" />
          </g>
        )}
      </svg>
      <span className={styles.dialValue}>
        {known ? deg.toFixed(0) : NO_VALUE}
        {known && <span className={styles.dialUnit}>°&nbsp;N</span>}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------
   The reference rail. Present on every tab, because it is what the operator
   reads WHILE working in the body — the pinned revision while sending a
   command, the certificate expiry while reading the command log.
   ------------------------------------------------------------------------- */

function IdentityRail({
  robot,
  facts,
  all,
  read,
  revisions,
}: {
  robot: Robot;
  facts: WindowFacts;
  all: WindowFacts;
  /** The telemetry read behind the position track and the agent block. */
  read: Read;
  revisions: { current: number | null; revisions: readonly Revision[] } | undefined;
}) {
  /* Same law as the vitals band: the rail reads the window while the window
     has readings, and the robot's last contact when it does not. An `Agent`
     block of six em dashes sitting under a `Last fix 7h 39m ago` is the rail
     contradicting itself about the same fetch. */
  const src = bandSource(facts, all);
  const scoped = facts.readings.length > 0;
  const pinned = revisions?.revisions.find((r) => r.id === revisions.current) ?? null;
  const newest = revisions === undefined || revisions.revisions.length === 0
    ? null
    : [...revisions.revisions].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    )[0]!;
  const behind = pinned !== null && newest !== null && newest.id !== pinned.id;

  const bringup = pinned === null ? null : revBool(pinned, 'bringup_ready');

  /* Robot id, model and morphology already ride the page title and the
     provenance line, so the rail does not repeat them — it carries what the
     line has no room for: what the pinned revision actually IS. Board,
     whether its bring-up script is complete, and how old the pin is are the
     facts an operator wants while reading the command log, and they are
     otherwise a tab away. */
  const identity: KVItem[] = [
    {
      label: 'Revision',
      value: pinned === null ? NO_VALUE : pinned.version,
      title: pinned?.notes ?? undefined,
    },
    { label: 'Board', value: pinned === null ? NO_VALUE : revMetaStr(pinned, 'board') ?? NO_VALUE },
    {
      label: 'Bringup',
      value: bringup === null
        ? NO_VALUE
        : <StateLabel tone={bringup ? 'good' : 'neutral'} label={bringup ? 'ready' : 'incomplete'} />,
      mono: false,
      title: bringup === true
        ? 'This revision publishes a complete bring-up script.'
        : 'No complete bring-up script is published for this revision.',
    },
    {
      label: 'Pinned',
      value: pinned === null ? NO_VALUE : <Timestamp value={pinned.created_at} strong />,
      mono: false,
      title: pinned === null ? undefined : `Revision published ${utc(pinned.created_at)}`,
    },
    { label: 'Firmware', value: robot.firmware ?? NO_VALUE },
    /* `robot.active` is NOT `robot.status`, and the header two rows up already
       prints a status badge reading `Active`. A rail row that also said
       `active` was two different facts wearing one word — this one is the
       tasking flag the Configuration tab sets, so it is named for what it
       gates. */
    {
      label: 'Tasking',
      value: robot.active ? 'eligible' : 'withheld',
      title: robot.active
        ? 'In service — the dispatcher may assign work to this robot.'
        : 'Withheld from tasking in the registry. Telemetry and commands still work.',
    },
    {
      label: 'Site',
      value: metaStr(robot, 'site') ?? NO_VALUE,
      title: metaStr(robot, 'site_label') ?? undefined,
    },
    { label: 'Bay', value: metaStr(robot, 'bay') ?? NO_VALUE },
  ];

  const enrolment: KVItem[] = [
    /* Registered and enrolled are not the same event, and the gap between
       them is a real operational fact — a robot can sit in the registry for
       weeks before anyone runs its bring-up. Naming both is the same
       discipline auterion-flight-log-review.png applies to `Logging Duration`
       vs `Airtime Duration`: two quantities, both printed, neither hidden. */
    {
      label: 'Registered',
      value: <Timestamp value={robot.created_at} mode="utc" />,
      mono: false,
      title: `Registry row created ${utc(robot.created_at)}.`,
    },
    {
      label: 'Enrolled',
      value: robot.enrolled_at === null
        ? 'never'
        : <Timestamp value={robot.enrolled_at} mode="utc" strong />,
      mono: false,
    },
    { label: 'By', value: robot.enrolled_by ?? NO_VALUE },
    {
      label: 'Cert serial',
      value: robot.cert_serial === null ? NO_VALUE : midEllipsis(robot.cert_serial, 20),
      title: robot.cert_serial ?? undefined,
    },
    {
      label: 'Expires',
      value: robot.cert_not_after === null
        ? NO_VALUE
        : (
          <Timestamp
            value={robot.cert_not_after}
            warnWithin={CERT_WARN_S}
            badWithin={CERT_BAD_S}
            strong
          />
        ),
      mono: false,
    },
  ];

  /* Auterion reports maintenance as a countdown in OPERATING HOURS, because
     that is the unit an operator plans in (auterion-vehicle-detail-overview
     .png). `mono_ns` is the agent's monotonic clock since boot — the same
     unit, and a real contract field rather than a calendar date the operator
     would have to subtract in their head. */
  const uptimeMs = src.latest === null
    ? null
    : (() => {
      const ns = topLevelNum(src.latest, 'mono_ns');
      return ns === null ? null : ns / 1e6;
    })();

  const profileVersion = (() => {
    const v = robot.command_profile?.['version'];
    return typeof v === 'string' && v.length > 0 ? v : null;
  })();
  const platformVersion = (() => {
    const v = robot.command_profile?.['platform_version'];
    return typeof v === 'string' && v.length > 0 ? v : null;
  })();
  /* Two version strings for the same robot from two different sources. When
     they disagree the console says so rather than printing both flat and
     leaving the operator to notice (THE BAR §13 tell 23). */
  const profileDrift = platformVersion !== null
    && robot.firmware !== null
    && platformVersion !== robot.firmware;

  const agent: KVItem[] = [
    {
      label: 'Uptime',
      value: uptimeMs === null ? NO_VALUE : duration(uptimeMs),
      mono: false,
      title: 'Agent monotonic clock at the newest reading — time since the agent last started.',
    },
    {
      label: 'Boot id',
      value: src.bootIds.length === 0
        ? NO_VALUE
        : src.bootIds.length === 1
          ? midEllipsis(src.bootIds[0]!, 16)
          : `${src.bootIds.length} boots`,
      title: src.bootIds.join(', '),
    },
    /* Clock offset, frame delivery and cadence used to sit here, three
       label:value rows deep in a rail, where they answered nothing on their
       own — an offset with no range and no skew beside it is a number an
       operator cannot act on. They now live in the Uplink disclosure on the
       LINK vital, with the distribution that makes them mean something. What
       stays is what the rail is FOR: which build of the agent this is. */
    {
      label: 'Profile',
      value: profileVersion === null && platformVersion === null
        ? NO_VALUE
        : (
          <span data-tone={profileDrift ? 'warn' : undefined} className={styles.kvToned}>
            {[profileVersion, platformVersion].filter((v) => v !== null).join(' · ')}
          </span>
        ),
      title: profileDrift
        ? `The command profile was published against platform ${platformVersion}, but the `
          + `registry has firmware ${robot.firmware}. The verbs on the Commands tab may not `
          + 'be the ones this build accepts.'
        : 'The command profile the agent published at enrolment — which verbs it '
          + 'accepts, and the platform build that defined them.',
    },
    {
      label: 'Last backfill',
      value: robot.last_backfill_at === null
        ? 'none'
        : <Timestamp value={robot.last_backfill_at} strong />,
      mono: false,
    },
  ];

  return (
    <div className={styles.rail}>
      <Panel title="Identity" meta={<span>{robot.morphology}</span>}>
        <KeyValue items={identity} layout="rows" />
        {behind && (
          <p className={styles.railNote}>
            {newest.version} is the newest {robot.morphology} revision, published{' '}
            {relative(newest.created_at)}. Repinning is on Configuration.
          </p>
        )}
      </Panel>

      <Panel title="Position" meta={<span>WGS84</span>}>
        <div className={styles.posRow}>
          <HeadingDial deg={robot.heading_deg} />
          <KeyValue
            layout="rows"
            className={styles.posKv}
            items={[
              { label: 'Fix', value: latlon(robot.lat, robot.lon) },
              { label: 'Speed', value: num(src.latest?.speed_mps, 2), unit: 'm/s' },
              {
                label: 'Last fix',
                value: <Timestamp value={robot.last_seen} strong />,
                mono: false,
              },
            ]}
          />
        </div>
        <TrackPlot facts={src} scoped={scoped} robot={robot} read={read} />
      </Panel>

      <Panel title="Enrolment" meta={<span>X.509</span>}>
        <KeyValue items={enrolment} layout="rows" />
      </Panel>

      <Panel
        title="Agent"
        meta={src.readings.length === 0
          ? undefined
          : <span>{scoped ? 'this window' : 'last contact'}</span>}
      >
        <KeyValue items={agent} layout="rows" />
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------------------
   The telemetry instrument.

   ONE window, ONE x-scale, ONE crosshair: the mode ribbon, the contact ribbon
   and every telemetry channel are stacked against the same [from,to], so
   "what was it doing when the battery fell" is one reading, not three.

   The channel stack is auterion-flight-log-review.png's per-section verdict
   index made live — every channel is a row, every row carries its own health
   dot resolved off its OWN limits, and the trace is right there beside the
   verdict rather than one click behind it. Rerun's per-joint torque stack
   (rerun-droid-multimodal-blueprint.png) is the same shape: seven small
   multiples, each on its own domain, sharing one ruler.

   Two things this fixes that a single 0–100% plot cannot:
     · Temp, Link, GNSS and Speed have no business on a percentage axis, so
       four of the seven channels were previously not plotted at all.
     · The y-axis numerals live in the LEFT GUTTER, outside the data area,
       beside the channel they scale. A numeral drawn inside the plot rect
       gets bisected by the first trace that crosses it.
   ------------------------------------------------------------------------- */

/** Left gutter: channel name + verdict dot on the left, y-domain on the right. */
const CH_GUTTER = 148;
const RIBBON_H = 22;

/** Axis numerals: as few digits as the number needs, never more — and never
    rounded to a value the trace never reached. */
function axisNum(v: number): string {
  if (Number.isInteger(v)) return String(v);
  return Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2);
}

/**
 * Measure a box. Lane height is a function of the panel's height, and the
 * vitals trace of the column's, so the instruments fill whatever they are
 * given instead of guessing.
 *
 * The observer's whole life hangs off the ref callback — attach observes,
 * detach (React passes null) disconnects — and NOT off an effect cleanup.
 * That is not a style preference: under StrictMode React 18 mounts, runs the
 * effect, tears it down and mounts again without ever re-invoking a stable
 * ref callback, so an effect that disconnects kills an observer nothing will
 * recreate. The box then freezes at whatever it measured during the first
 * commit — before the band's right-hand readout had laid out — which is how
 * seven vitals sparklines quietly vanished at 1280px while the same band
 * drew them at 1440.
 */
function useBox(): [(el: HTMLDivElement | null) => void, { w: number; h: number }] {
  const [box, setBox] = useState({ w: 0, h: 0 });
  const obs = useRef<ResizeObserver | null>(null);
  const ref = useCallback((el: HTMLDivElement | null) => {
    obs.current?.disconnect();
    obs.current = null;
    if (!el) return;
    const put = (w: number, h: number) => setBox((p) => (
      Math.abs(p.w - w) < 0.5 && Math.abs(p.h - h) < 0.5 ? p : { w, h }
    ));
    const r = el.getBoundingClientRect();
    put(r.width, r.height);
    const o = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) put(cr.width, cr.height);
    });
    o.observe(el);
    obs.current = o;
  }, []);
  return [ref, box];
}

/* -------------------------------------------------------------------------
   Overview.
   ------------------------------------------------------------------------- */

/* =========================================================================
   THE TRANSPORT BAR.

   One 34px strip, pinned under the plot stack, that owns the window for every
   surface on this page — the nine lanes, the vitals band, the track, the raw
   ledger. Six things, left to right, and each one answers a question the
   READINGS radio it replaced could not:

     LIVE / Go Live  · am I looking at now?  (palantir-gotham-facet-rail-
                       timeline.png's explicit `Go Live`; the pill greys AND
                       changes its word the instant you scrub, per inorbit's
                       Live ⇄ Live off)
     DATA PRESENCE   · what the band paints, and how wide it is in time —
                       Formant names its band in the transport row and so do
                       we, plus a gap count in --warn when there is one,
                       because a silence must be findable without a hover
     the band        · WHERE IS THE DATA, AND WHAT HAPPENED IN IT.  Two lanes
                       in 26px: below, presence over the whole retained range
                       with real holes where readings are missing; above,
                       marks at their true timestamps — command ticks in
                       their status colour, capture sessions as duration
                       bars — and mode edges as 1px hairlines crossing both.
                       Every mark is a contract field already drawn somewhere
                       else on this page, so the band is the index to the
                       rest of the surface rather than a second source of
                       truth (formant-timeline-data-presence-scrubber.png,
                       foxglove-event-editor-timeline-markers.png)
     span            · 1h / 6h / 24h / 7d in wall clock, so two robots'
                       plots are the same picture
     time axis       · agent_time ⇄ ingest_time, named, because the agent
                       publishes a clock offset and the two really differ
     playhead        · a right-aligned mono Zulu stamp of the instant under
                       the cursor, or of the window's right edge

   The whole bar is one row because a transport that costs two rows is a
   panel, and this one has to sit under an instrument.
   ========================================================================= */

interface PresenceRun {
  from: number;
  to: number;
  kind: 'data' | 'gap' | 'unfetched';
}

/**
 * Data presence across the scrubber's extent. Three states, not two.
 *
 * A gap BETWEEN two readings is a real silence: the robot was reporting on
 * both sides of it and stopped in the middle, and that is an outage worth
 * painting in the one colour that means "something is wrong with the data".
 *
 * Everything OLDER than the oldest row the store returned is a different
 * fact. Either the fetch was truncated or the store has retained nothing
 * further back; from here they are indistinguishable, and both mean "the
 * console does not hold this, not "the robot went dark". Widening to 7d over
 * a store with 24h of history would otherwise paint six days of alarm.
 */
function presenceRuns(
  times: readonly number[],
  extFrom: number,
  extTo: number,
  tol: number,
): PresenceRun[] {
  const out: PresenceRun[] = [];
  const push = (from: number, to: number, kind: PresenceRun['kind']) => {
    if (to > from) out.push({ from: Math.max(from, extFrom), to: Math.min(to, extTo), kind });
  };
  if (times.length === 0) {
    push(extFrom, extTo, 'unfetched');
    return out;
  }
  const first = times[0]!;
  const last = times[times.length - 1]!;
  push(extFrom, first, 'unfetched');
  let runStart = first;
  for (let i = 1; i < times.length; i += 1) {
    if (times[i]! - times[i - 1]! > tol) {
      push(runStart, times[i - 1]!, 'data');
      push(times[i - 1]!, times[i]!, 'gap');
      runStart = times[i]!;
    }
  }
  push(runStart, last, 'data');
  /* A trailing silence shorter than one tolerance is not a gap — it is the
     poll interval. Emitting it anyway painted a sub-pixel "outage" at the
     right-hand edge on every live robot and made the gap COUNT wrong. */
  if (extTo - last > tol) push(last, extTo, 'gap');
  return out.filter((r) => r.to > r.from);
}

/* -------------------------------------------------------------------------
   WHAT ELSE HAPPENED, on the band's own x-scale.

   formant-timeline-data-presence-scrubber.png does not stop at presence: the
   band carries event ticks at their true timestamps, so the operator sees
   that something is there before scrubbing to it. Everything below is already
   drawn somewhere else on this page — the command ribbons in the Commands
   tab, the session ledger in Captures, the mode ribbon in the chart — which
   is the point: the transport is the index to the rest of the surface, and it
   is built from the same three contract fields, not from anything invented.
   ------------------------------------------------------------------------- */
type MarkKind = 'command' | 'capture' | 'mode';

interface TransportMark {
  id: string;
  kind: MarkKind;
  /** Instants have `to === from`; the renderer gives them a 2px floor. */
  from: number;
  to: number;
  tone: Tone;
  title: string;
}

function transportMarks(
  commands: readonly Command[] | undefined,
  scans: readonly Scan[] | undefined,
  readings: readonly TelemetryReading[],
  times: readonly number[],
  now: number,
): TransportMark[] {
  const out: TransportMark[] = [];

  /* issued_at → updated_at for a command that reached a terminal state,
     issued_at → now for one still in flight. Both are contract fields. */
  for (const c of commands ?? []) {
    const a = new Date(c.issued_at).getTime();
    if (!Number.isFinite(a)) continue;
    const u = new Date(c.updated_at).getTime();
    const b = CMD_TERMINAL.has(c.status)
      ? Math.max(Number.isFinite(u) ? u : a, a)
      : Math.max(now, a);
    out.push({
      id: `cmd-${c.id}`,
      kind: 'command',
      from: a,
      to: b,
      tone: CMD_TONE[c.status],
      title: `${c.command}  ·  ${c.status}  ·  issued ${utc(c.issued_at)} by ${c.issued_by}`,
    });
  }

  /* A session that has started and not stopped runs to now — which is the
     literal truth of "still capturing", and the ledger says so too. */
  for (const s of scans ?? []) {
    if (s.started_utc === null) continue;
    const a = new Date(s.started_utc).getTime();
    if (!Number.isFinite(a)) continue;
    const raw = s.stopped_utc === null ? now : new Date(s.stopped_utc).getTime();
    const b = Math.max(Number.isFinite(raw) ? raw : a, a);
    out.push({
      id: `cap-${s.id}`,
      kind: 'capture',
      from: a,
      to: b,
      tone: scanState(s.processing_state).tone,
      title: `${s.session_id}  ·  ${scanState(s.processing_state).label}  ·  ${duration(b - a)}  ·  ${bytes(s.size_bytes)}`,
    });
  }

  /* A mode change is an edge between two consecutive readings, so it is only
     ever claimed where the console actually saw both sides of it. */
  for (let i = 1; i < readings.length; i += 1) {
    const next = readings[i]!.state;
    if (next === null || next === readings[i - 1]!.state) continue;
    out.push({
      id: `mode-${times[i]}`,
      kind: 'mode',
      from: times[i]!,
      to: times[i]!,
      tone: STATUS_STATE[next as RobotStatus]?.tone ?? 'neutral',
      title: `${readings[i - 1]!.state ?? NO_VALUE} → ${next}`,
    });
  }

  return out;
}

/** The page's one window, and the verbs that move it. Passed to every panel
    that draws against time, so there is exactly one of it. */
interface WindowCtl {
  /** Everything the scrubber can reach. */
  extFrom: number;
  extTo: number;
  /** The window in force. */
  from: number;
  to: number;
  live: boolean;
  span: SpanKey;
  custom: boolean;
  runs: readonly PresenceRun[];
  /** Commands, capture sessions and mode changes, at their true timestamps. */
  marks: readonly TransportMark[];
  axis: TimeAxisKey;
  axisShiftMs: number;
  onSpan: (s: SpanKey) => void;
  onWindow: (from: number, to: number) => void;
  onGoLive: () => void;
  onAxis: (a: TimeAxisKey) => void;
}

function TransportBar({
  extFrom, extTo, from, to, live, span, custom, runs, marks,
  axis, axisShiftMs, cursor, read,
  onSpan, onWindow, onGoLive, onAxis,
}: WindowCtl & {
  /** Instant under the crosshair, when the operator is over the stack. */
  cursor: number | null;
  /**
   * The telemetry read the presence runs and the mode hairlines come from.
   *
   * THE SCREEN-READER CHANNEL OF THE SAME RULE. This band's `aria-label` said
   * `No reporting gaps. 2 commands, 1 capture sessions, 0 mode changes.` over
   * a dead telemetry endpoint — a coverage verdict and a census, both from a
   * source that never answered, and invisible to anyone checking the pixels.
   */
  read: Read;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState<{ a: number; b: number } | null>(null);
  const descId = useId();
  const ext = Math.max(1, extTo - extFrom);
  const pct = (t: number) => `${(frac(t, extFrom, extTo) * 100).toFixed(3)}%`;
  const span01 = (a: number, b: number) =>
    ({ left: pct(a), width: `${Math.max(0, frac(b, extFrom, extTo) - frac(a, extFrom, extTo)) * 100}%` });

  const timeAtX = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return to;
    const r = el.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (clientX - r.left) / Math.max(1, r.width)));
    return extFrom + f * ext;
  };

  /* A DRAG selects an explicit range; a CLICK re-centres the window it already
     has. Two gestures, no modifier, and neither can leave the operator with a
     window they did not ask for. */
  const commit = (a: number, b: number) => {
    if (Math.abs(b - a) < ext * 0.004) {
      const half = (to - from) / 2;
      onWindow(a - half, a + half);
    } else {
      onWindow(Math.min(a, b), Math.max(a, b));
    }
  };

  const onDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const t = timeAtX(e.clientX);
    setDrag({ a: t, b: t });
  };
  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    if (drag === null) return;
    setDrag({ a: drag.a, b: timeAtX(e.clientX) });
  };
  const onUp = (e: PointerEvent<HTMLDivElement>) => {
    if (drag === null) return;
    commit(drag.a, timeAtX(e.clientX));
    setDrag(null);
  };

  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const len = to - from;
    const step = e.shiftKey ? len : len * 0.25;
    if (e.key === 'ArrowLeft') { onWindow(from - step, to - step); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { onWindow(from + step, to + step); e.preventDefault(); }
    else if (e.key === 'Home') { onWindow(extFrom, extFrom + len); e.preventDefault(); }
    else if (e.key === 'End') { onGoLive(); e.preventDefault(); }
  };

  const sel = drag === null
    ? { a: from, b: to }
    : { a: Math.min(drag.a, drag.b), b: Math.max(drag.a, drag.b) };

  const stamp = cursor ?? to;
  const shift = axisShiftMs >= 1 ? `${axisShiftMs < 10 ? axisShiftMs.toFixed(2) : Math.round(axisShiftMs)} ms` : null;

  /* Only what the band can actually reach. A session that ended before the
     retained range begins is not clipped to the left edge — it is dropped,
     because a bar pinned to the edge would claim a start time it did not have. */
  const visible = marks.filter((m) => m.to >= extFrom && m.from <= extTo);
  const cmdMarks = visible.filter((m) => m.kind === 'command');
  const capMarks = visible.filter((m) => m.kind === 'capture');
  const modeMarks = visible.filter((m) => m.kind === 'mode');
  const gapRuns = runs.filter((r) => r.kind === 'gap');
  const gapMs = gapRuns.reduce((a, r) => a + (r.to - r.from), 0);
  const unfetched = runs.some((r) => r.kind === 'unfetched');

  const bandKey = [
    `Data presence, ${utc(new Date(extFrom).toISOString(), false)} → ${utc(new Date(extTo).toISOString(), false)}`
    + ` (${duration(ext)}, everything this window size has fetched).`,
    /* The key names only what is actually painted — a legend that lists a
       mark this robot has none of is a legend you stop reading. */
    'Solid — the agent was reporting.',
    ...(gapRuns.length > 0 ? ['Hatched — a silence longer than one cadence.'] : []),
    ...(unfetched ? ['Dim — nothing in the store this far back.'] : []),
    ...(cmdMarks.length > 0
      ? [`Top ticks — ${cmdMarks.length} commands, at issue time, in their status colour.`] : []),
    ...(capMarks.length > 0
      ? [`Lower bars — ${capMarks.length} capture sessions, start to stop.`] : []),
    ...(modeMarks.length > 0 ? [`Hairlines — ${modeMarks.length} mode changes.`] : []),
    'Drag to select a window, click to re-centre, ← → to step.',
  ].join('\n');

  const blind = unread(read);
  const summary = [
    `Band spans ${duration(ext)}, ending ${utc(new Date(extTo).toISOString())}.`,
    /* `No reporting gaps` is a verdict on coverage and it needs a reading. */
    blind
      ? 'Nothing has been read, so this band shows no coverage.'
      : gapRuns.length === 0
        ? 'No reporting gaps.'
        : `${gapRuns.length} reporting gap${gapRuns.length === 1 ? '' : 's'} totalling ${duration(gapMs)}.`,
    `${cmdMarks.length} commands, ${capMarks.length} capture sessions, `
    + `${blind ? NO_VALUE : modeMarks.length} mode changes.`,
  ].join(' ');

  return (
    <div className={styles.transport}>
      {/* ---- mode ------------------------------------------------------- */}
      <div className={styles.liveSlot}>
        <span
          className={styles.livePill}
          data-live={live}
          title={live
            ? 'The window tracks the newest reading.'
            : 'The window is parked on history. Nothing here is current.'}
        >
          <StatusDot tone={live ? 'good' : 'stale'} hollow={!live} pulse={live} />
          {/* The WORD changes, not just the hue — nothing on this page may
              read `LIVE` while the window is parked on history. */}
          {live ? 'LIVE' : 'REPLAY'}
        </span>
        {!live && (
          <button type="button" className={styles.goLive} onClick={onGoLive}>
            Go Live
            <span className={styles.key}>End</span>
          </button>
        )}
      </div>

      {/* ---- what the band is ------------------------------------------- */}
      <span className={styles.bandLabel} title={bandKey}>
        <span className={styles.bandLabelLong}>Data </span>presence
        <em className={styles.bandExtent}>{duration(ext)}</em>
        {/* A silence is the one thing on this band that must be findable
            without hovering it, so it is also a word. */}
        {gapRuns.length > 0 && (
          <em
            className={styles.bandGaps}
            title={`Longest silence ${duration(Math.max(...gapRuns.map((r) => r.to - r.from)))}`}
          >
            {gapRuns.length} gap{gapRuns.length === 1 ? '' : 's'}
          </em>
        )}
      </span>

      {/* ---- the scrubber ------------------------------------------------ */}
      <div
        ref={trackRef}
        className={styles.scrub}
        role="slider"
        tabIndex={0}
        aria-label="Telemetry window position"
        aria-describedby={descId}
        aria-valuemin={extFrom}
        aria-valuemax={extTo}
        aria-valuenow={to}
        aria-valuetext={`${utc(new Date(from).toISOString(), false)} to ${utc(new Date(to).toISOString(), false)}`}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onKeyDown={onKey}
      >
        {/* Lower band: WAS THE ROBOT REPORTING. A silence is a hole you can
            see before you scrub into it (formant-timeline-data-presence-
            scrubber.png), never a uniform rail that claims coverage. */}
        <span className={styles.presence} aria-hidden="true">
          {runs.map((r) => (
            <span
              key={`${r.kind}-${r.from}`}
              className={styles.run}
              data-kind={r.kind}
              style={span01(r.from, r.to)}
              title={r.kind === 'data'
                ? `Reporting  ${utc(new Date(r.from).toISOString(), false)} → ${utc(new Date(r.to).toISOString(), false)}`
                : r.kind === 'gap'
                  ? `No data for ${duration(r.to - r.from)}  ·  ${utc(new Date(r.from).toISOString(), false)} → ${utc(new Date(r.to).toISOString(), false)}`
                  : 'Nothing in the store this far back — past retention, or deeper than this window size fetches'}
            />
          ))}
        </span>

        {/* Upper band: WHAT WE TOLD IT and WHAT IT RECORDED, at their true
            timestamps — the same two ledgers the tabs carry, indexed on the
            axis that moves the window. */}
        {/* The band's marks are hover targets, not a11y nodes: the slider is a
            leaf widget and its readout is the description below, which states
            the same counts and durations in one string. */}
        <span className={styles.events} aria-hidden="true">
          {capMarks.map((m) => (
            <span
              key={m.id}
              className={styles.mark}
              data-kind="capture"
              data-tone={m.tone}
              style={span01(m.from, m.to)}
              title={m.title}
            />
          ))}
          {/* A command is an INSTANT here — the moment it was issued. Its
              span (issued → acknowledged, or issued → still running) is drawn
              as a ribbon in the Commands tab, on this same x-scale; painting
              it here as well made four in-flight verbs run to `now` and fill
              the lane with one undifferentiated bar. */}
          {cmdMarks.map((m) => (
            <span
              key={m.id}
              className={styles.mark}
              data-kind="command"
              data-tone={m.tone}
              style={{ left: pct(m.from) }}
              title={m.title}
            />
          ))}
        </span>

        {/* Mode edges cross both bands: the state history and the reporting
            history are one picture (inorbit-robot-detail-dense.png). */}
        {modeMarks.map((m) => (
          <span
            key={m.id}
            className={styles.mark}
            data-kind="mode"
            data-tone={m.tone}
            style={{ left: pct(m.from) }}
            aria-hidden="true"
          />
        ))}

        {/* Everything outside the window dims; the window itself keeps its
            presence painting and takes two accent edges. */}
        <span className={styles.mask} style={span01(extFrom, sel.a)} aria-hidden="true" />
        <span className={styles.mask} style={span01(sel.b, extTo)} aria-hidden="true" />
        <span className={styles.sel} style={span01(sel.a, sel.b)} aria-hidden="true" />
        {cursor !== null && (
          <span className={styles.scrubCursor} style={{ left: pct(cursor) }} aria-hidden="true" />
        )}
      </div>
      <span id={descId} className={styles.srOnly}>{summary}</span>

      {/* ---- span, axis, playhead --------------------------------------- */}
      <SegmentedControl<SpanKey | 'custom'>
        label="Telemetry window span"
        value={custom ? 'custom' : span}
        onChange={(v) => { if (v !== 'custom') onSpan(v); }}
        items={[
          ...SPANS.map((s) => ({ value: s.key, label: s.key })),
          ...(custom ? [{ value: 'custom' as const, label: duration(to - from) }] : []),
        ]}
      />
      <label className={styles.axisPick}>
        <span className={styles.srOnly}>Time axis</span>
        <select
          className={styles.axisSelect}
          value={axis}
          onChange={(e) => onAxis(e.target.value as TimeAxisKey)}
          title={
            'Which clock the x-axis is.\n'
            + 'agent_time — the timestamp the agent stamped, off its own clock.\n'
            + 'ingest_time — that timestamp less the agent’s reported clock offset.'
          }
        >
          <option value="agent">{AXIS_LABEL.agent}</option>
          <option value="ingest">{AXIS_LABEL.ingest}</option>
        </select>
      </label>
      {axis === 'ingest' && (
        <span className={styles.axisShift} title="Largest clock correction applied in this window">
          {shift === null ? 'no skew' : `±${shift}`}
        </span>
      )}
      <span
        className={styles.stamp}
        data-cursor={cursor !== null}
        title={cursor === null ? 'Right edge of the window' : 'Instant under the crosshair'}
      >
        {axisClock(stamp)}
      </span>
    </div>
  );
}

/** Reports the RibbonStack's internal crosshair up to the transport bar. */
function CursorSync({ t, onChange }: { t: number | null; onChange: (t: number | null) => void }) {
  useEffect(() => { onChange(t); }, [t, onChange]);
  return null;
}

/* -------------------------------------------------------------------------
   BRING-UP. What a robot that has never reported gets instead of an empty
   instrument.

   auterion-flight-log-review.png's section index — every step carrying its
   own verdict dot, so you know which part failed before opening it — applied
   to enrolment. The six steps are the actual sequence a Forest3 robot goes
   through, and every one of them is a field of the REST contract, so the
   ladder is a reading of the record rather than a wizard: the console cannot
   perform these steps, it can only report which of them the registry has seen.

   The first incomplete step is the answer to "why has it not phoned home",
   and it is the only row that carries prose.
   ------------------------------------------------------------------------- */

interface BringupStep {
  key: string;
  label: string;
  /** Done · blocked-here · not yet. Three states, three renderings. */
  done: boolean;
  value: ReactNode;
  /** What the operator does about it, printed only on the first open step. */
  next?: string | undefined;
}

function bringupLadder(robot: Robot, pinned: Revision | null): BringupStep[] {
  const ready = pinned === null ? null : revBool(pinned, 'bringup_ready');
  const board = pinned === null ? null : revMetaStr(pinned, 'board');
  return [
    {
      key: 'registered',
      label: 'Registered',
      done: true,
      value: <Timestamp value={robot.created_at} mode="utc" strong />,
    },
    {
      key: 'pinned',
      label: 'Revision pinned',
      done: pinned !== null,
      value: pinned === null
        ? 'none'
        : board === null ? pinned.version : `${pinned.version} · ${board}`,
      next: 'Pin a hardware revision on Configuration — the bring-up script is per revision.',
    },
    {
      key: 'script',
      label: 'Bring-up published',
      done: ready === true,
      value: ready === null ? NO_VALUE : ready ? 'ready' : 'incomplete',
      next: 'This revision has no complete bring-up script. Publish one from Catalog.',
    },
    {
      key: 'cert',
      label: 'Certificate issued',
      done: robot.cert_serial !== null,
      value: robot.cert_serial === null ? 'none' : midEllipsis(robot.cert_serial, 20),
      next: 'Mint an enrolment token, then run the bring-up on the robot.',
    },
    {
      key: 'enrolled',
      label: 'Enrolled',
      done: robot.enrolled_at !== null,
      value: robot.enrolled_at === null
        ? 'never'
        : <Timestamp value={robot.enrolled_at} mode="utc" strong />,
      next: 'The bring-up exchanges the token for the client certificate. Run it on the robot.',
    },
    /* THE ONE RUNG THAT WAS NOT A READING OF THE RECORD.
       It was `done: false, value: 'never'` — hard-coded — so the rung asserted
       "this robot has never reported" wherever the ladder was drawn, and the
       ladder was drawn whenever the telemetry ARRAY was empty. `last_seen` is
       the registry's own answer to the same question, it arrives on
       `/api/robots/{id}` rather than on the telemetry endpoint, and it is the
       only source with standing to make this claim. Everything else on this
       panel is a field of the contract; now this is too. */
    {
      key: 'contact',
      label: 'First telemetry',
      done: robot.last_seen !== null,
      value: robot.last_seen === null
        ? 'never'
        : <Timestamp value={robot.last_seen} mode="utc" strong />,
      next: 'The agent is enrolled but nothing has arrived. Check that the ROS 2 agent is running.',
    },
  ];
}

function BringupLadder({
  robot,
  pinned,
  onMint,
  mintPending,
}: {
  robot: Robot;
  pinned: Revision | null;
  onMint: () => void;
  mintPending: boolean;
}) {
  const steps = bringupLadder(robot, pinned);
  const blocked = steps.findIndex((st) => !st.done);

  return (
    <ol className={styles.ladder}>
      {steps.map((st, i) => (
        <li
          key={st.key}
          className={styles.rung}
          data-state={st.done ? 'done' : i === blocked ? 'blocked' : 'pending'}
        >
          <span className={styles.rungMark}>
            <StatusDot
              tone={st.done ? 'good' : i === blocked ? 'warn' : 'neutral'}
              hollow={!st.done}
            />
          </span>
          <span className={styles.rungLabel}>{st.label}</span>
          <span className={styles.rungValue}>{st.value}</span>
          <span className={styles.rungNote}>
            {i === blocked && st.next !== undefined ? st.next : ''}
          </span>
          {/* The verb that unblocks the step sits ON the step. A checklist
              that names a remedy and then sends the operator to look for it
              somewhere else is a checklist with a hole in it.

              `i === blocked` was the wrong gate for this one rung. Minting has
              no dependency on the rungs above it — the API asks only that the
              robot exist (api/routers/robots.py, mint_enrollment_token), and
              this rung's own note says "mint a token, THEN run the bring-up".
              But a freshly registered robot pins a provisional `001` with no
              bring-up script, so `Bring-up published` was the first blocked
              rung and the mint button rendered nowhere — the console could
              register a robot it could never provision. The gate is now the
              fact the rung reports: no certificate, so offer the token. */}
          <span className={styles.rungAct}>
            {!st.done && st.key === 'cert' && (
              <Button size="sm" variant="primary" icon="key" disabled={mintPending} onClick={onMint}>
                {mintPending ? 'Minting…' : 'Generate token'}
              </Button>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

function OverviewTab({
  robot,
  pinned,
  facts,
  ctl,
  read,
  commandsRead,
  scansRead,
  hasHistory,
  commands,
  scans,
  streamConnected,
  onOpenScan,
  onGoTab,
  onMint,
  mintPending,
}: {
  robot: Robot;
  /** The pinned hardware revision, for the bring-up ladder. */
  pinned: Revision | null;
  facts: WindowFacts;
  ctl: WindowCtl;
  /** The telemetry read. Nothing here concludes anything without it. */
  read: Read;
  commandsRead: Read;
  scansRead: Read;
  /** The store has readings — they may simply be outside the window. */
  hasHistory: boolean;
  commands: readonly Command[] | undefined;
  scans: readonly Scan[] | undefined;
  streamConnected: boolean;
  onOpenScan: (id: number) => void;
  onGoTab: (t: TabId) => void;
  onMint: () => void;
  mintPending: boolean;
}) {
  const { readings } = facts;
  const [cursor, setCursor] = useState<number | null>(null);
  const tick = useNow(true);
  /* The console's word for "this is current", named after the transport that
     is actually feeding it (`shell/TopBar` → `useFeedWord`). */
  const feedWord = useFeedWord();

  /* ONE LANE RENDERER, AND IT LIVES IN components/charts.

     This panel used to draw its own nine-lane stack — its own gutters, its
     own axis padding, its own threshold placement — beside /telemetry's,
     which drew the same nine channels differently. Two renderers for one job
     is two consoles, and the one that lost had a data-fitted y-box that
     silently dropped CPU 90%, Temp 80 °C, Link −85 dBm and GNSS 5 sats. The
     kit's stack is the survivor: same window, same limits from
     `charts/channels.ts`, and both published limits on every lane. */
  const series = useMemo(
    /* The kit takes rows the way `/api/robots/{id}/telemetry` returns them —
       newest first — and reverses them itself. `facts.readings` is already
       oldest-first for this page's own ledgers, so handing it over unreversed
       would give every lane a descending time base: a negative cadence, no gap
       detection, and a `newest` reading six hours older than the newest one. */
    () => [{
      id: robot.robot_id,
      colour: seriesColour(0, 1),
      readings: [...readings].reverse(),
    }],
    [robot.robot_id, readings],
  );
  const marks = useMemo(
    () => commandMarks(commands ?? [], tick),
    [commands, tick],
  );

  /* The page's transport owns the window; the stack owns the crosshair. The
     kit reads both off one context, so the lanes, the ledger and the
     transport bar below can never quote two different instants. */
  const [hover, setHover] = useState<number | null>(null);
  const [playhead, setPlayhead] = useState<number | null>(null);
  const at = hover ?? playhead;
  useEffect(() => setCursor(at), [at]);
  const bridge = useMemo<CursorApi>(
    () => ({
      from: facts.from,
      to: facts.to,
      live: ctl.live,
      hover,
      playhead,
      at,
      setHover,
      setPlayhead,
      zoomTo: ctl.onWindow,
      goLive: ctl.onGoLive,
    }),
    [facts.from, facts.to, ctl.live, ctl.onWindow, ctl.onGoLive, hover, playhead, at],
  );


  const queueColumns: readonly Column<Command>[] = [
    {
      key: 'cmd', header: 'Command', kind: 'identity', mono: true, max: '18ch',
      cell: (c) => <span className={styles.lv}>{c.command}</span>,
    },
    {
      key: 'status', header: 'Status', kind: 'status',
      cell: (c) => (
        <StateLabel tone={CMD_TONE[c.status]} label={c.status} loud={c.status === 'failed'} />
      ),
    },
    {
      key: 'instate', header: 'In state', kind: 'age', align: 'right',
      cell: (c) => (
        <span title={`Entered ${c.status} at ${utc(c.updated_at)}`}>
          {duration(Date.now() - new Date(c.updated_at).getTime())}
        </span>
      ),
    },
    {
      key: 'issued', header: 'Issued', kind: 'age', align: 'right', drop: 1,
      cell: (c) => <Timestamp value={c.issued_at} strong />,
    },
    {
      key: 'by', header: 'By', kind: 'id', mono: true, drop: 2,
      cell: (c) => <span className={styles.lvDim}>{c.issued_by}</span>,
    },
  ];

  /* Half the surface wide, so the session id is middle-ellipsised —
     `20260826…yard-sweep` keeps the half that tells two sessions apart. The
     full ledger, with sensors, format and pose frame, is one tab away. */
  const captureColumns: readonly Column<Scan>[] = [
    {
      key: 'session', header: 'Session', kind: 'identity', mono: true, max: '24ch',
      cell: (s) => (
        <span className={styles.lv} title={s.session_id}>{midEllipsis(s.session_id, 24)}</span>
      ),
    },
    {
      key: 'state', header: 'State', kind: 'status',
      cell: (s) => (
        <StateLabel
          tone={scanState(s.processing_state).tone}
          label={scanState(s.processing_state).label}
          loud={s.processing_state === 'failed'}
        />
      ),
    },
    {
      key: 'dur', header: 'Duration', kind: 'age', align: 'right', drop: 2,
      cell: (s) => (s.started_utc === null || s.stopped_utc === null
        ? NO_VALUE
        : duration(new Date(s.stopped_utc).getTime() - new Date(s.started_utc).getTime())),
    },
    {
      key: 'size', header: 'Size', kind: 'num', align: 'right', mono: true, drop: 1,
      cell: (s) => <span className={styles.lv}>{bytes(s.size_bytes)}</span>,
    },
    {
      key: 'started', header: 'Captured', kind: 'age', align: 'right',
      cell: (s) => <Timestamp value={s.started_utc} strong />,
    },
  ];

  const enrolled = robot.enrolled_at !== null;
  const empty = readings.length === 0;
  /* THREE different nothings, and conflating the third with the first is the
     defect this page was rebuilt for.

       NEVER REPORTED       a fact about the ROBOT. `robot.last_seen === null`,
                            straight off `/api/robots/{id}` — a source that
                            answered. Gets the bring-up ladder.
       NOTHING IN THIS WINDOW  a fact about the WINDOW. Gets one line on the
                            instrument, plus the transport bar, whose presence
                            painting already shows where the readings are.
       NOTHING WAS READ     a fact about the CONSOLE. Gets the instrument, a
                            fault line and `—`; it may not be reported as
                            either of the other two.

     `empty && !hasHistory` cannot tell the first from the third — both are an
     empty array — so it used to answer a dead telemetry endpoint with `Nothing
     has arrived from rover-01 since it was enrolled`, a six-step enrolment
     checklist and an instruction to go and inspect the ROS 2 agent of a robot
     that had reported 150 times. The registry's `last_seen` is what separates
     them, and it is also strictly better on the healthy path: a robot whose
     window is empty because the operator scrubbed off its coverage is not a
     robot in bring-up either. */
  const virgin = robot.last_seen === null && empty && !hasHistory;

  return (
    <>
      {virgin ? (
        <Panel
          className={styles.fit}
          /* A panel titled `Telemetry window` over a bring-up checklist is a
             panel lying about its own contents. On a robot that has never
             reported there is no window — there is a sequence it has not
             finished — so the panel says which one it is. */
          title="Bring-up"
          /* The registry's word, because the registry is what said it: this
             panel is only reachable on `last_seen === null`. */
          meta={<span className={styles.toolbarNote}>never reported</span>}
          pad="none"
        >
          {/* No hatched 600px void: the panel reports where in bring-up the
              registry actually is, one verdict dot per step, and the first
              open step carries the sentence that says what to do about it. */}
          {/* The telemetry endpoint can be down on a robot that genuinely has
              never reported too — jetrover-08 with a dead link — and the panel
              owes the operator that fact rather than letting the ladder read as
              a complete picture. */}
          {unread(read) && <FaultLine read={read} />}
          <div className={styles.awaitFirst}>
            <span className={styles.awaitLine}>
              {enrolled
                ? `Nothing has arrived from ${robot.robot_id} since it was enrolled.`
                : `${robot.robot_id} is registered but has never reported.`}
            </span>
            <BringupLadder
              robot={robot}
              pinned={pinned}
              onMint={onMint}
              mintPending={mintPending}
            />
          </div>
        </Panel>
      ) : (
        /* The stack is the kit's panel; the transport bar under it is the
           PAGE's, not this panel's — it drives the vitals band, the track and
           both ledgers as well, and locking it inside one instrument's frame
           was this surface claiming a window that belongs to the whole tab. */
        <div className={`${styles.stack} ${styles.grow}`}>
          <CursorCtx.Provider value={bridge}>
            <SignalStack
              channels={STACK_CHANNELS}
              series={series}
              axis={ctl.axis}
              events={marks}
              read={read}
              title="Telemetry window"
              tools={
                <span className={styles.toolbarNote}>
                  {facts.cadenceMs === null
                    ? 'cadence unknown'
                    : `every ${duration(facts.cadenceMs)}`}
                  {' · '}
                  {facts.bootIds.length === 1
                    ? `boot ${midEllipsis(facts.bootIds[0]!, 12)}`
                    : /* `0 agent boots` is a census of the window's boot ids.
                         Nothing was read, so nothing was counted. */
                      <><Figure of={read} value={facts.bootIds.length} /> agent boots</>}
                </span>
              }
              {...(unread(read)
                /* THE PANEL'S OWN FAULT LINE, IN THE SLOT THE ABSENCE
                   SENTENCE USED. Nine lanes, their thresholds and the axis are
                   all true before the first byte, so the instrument stays and
                   only the trace is missing — which is the shape THE BAR asks
                   a degraded panel to keep. What it must not do is say `No
                   readings in this window`: that is a measurement of the
                   window, and nobody measured it. */
                ? { notice: <FaultLine read={read} /> }
                : empty
                  ? {
                    notice: (
                      <span>
                        No readings in this window — the scrubber shows where they are.
                      </span>
                    ),
                  }
                  : {})}
            />
          </CursorCtx.Provider>
          <TransportBar {...ctl} cursor={cursor} read={read} />
        </div>
      )}


      {/* With no telemetry to draw, the two ledgers take the frame rather
          than leaving half the surface as bare ground. */}
      <PanelGrid className={virgin ? `${styles.duo} ${styles.duoFill}` : styles.duo}>
        <Panel
          className={styles.ledger}
          title="Command queue"
          count={figure(commandsRead, commands?.length)}
          meta={
            /* `live` was hard-coded, so this header flew `● Polling 20s` over
               a queue that had never answered. The read decides. */
            <LiveTag
              live={commandsRead.phase === 'live'}
              label={feedWord}
              pulse={streamConnected && commandsRead.phase === 'live'}
              {...(streamConnected ? {} : { every: CMD_POLL_MS })}
            />
          }
          tools={
            <Button size="sm" variant="ghost" onClick={() => onGoTab('commands')}>
              Open
            </Button>
          }
          pad="none"
        >
          <DataTable
            rows={commands ?? []}
            columns={queueColumns}
            rowKey={(c) => String(c.id)}
            rowTone={(c) => CMD_TONE[c.status]}
            rowAlarm={(c) => c.status === 'failed'}
            of={commandsRead}
            empty="No commands issued to this robot."
          />
        </Panel>

        <Panel
          className={styles.ledger}
          title="Captures"
          count={figure(scansRead, scans?.length)}
          meta={
            <span>
              <Figure
                of={scansRead}
                value={bytes((scans ?? []).reduce((a, s) => a + s.size_bytes, 0))}
              />
            </span>
          }
          tools={
            <Button size="sm" variant="ghost" onClick={() => onGoTab('captures')}>
              Open
            </Button>
          }
          pad="none"
        >
          <DataTable
            rows={scans ?? []}
            columns={captureColumns}
            rowKey={(s) => String(s.id)}
            rowTone={(s) => scanState(s.processing_state).tone}
            onRowClick={(s) => onOpenScan(s.id)}
            of={scansRead}
            empty="No capture sessions from this robot."
          />
        </Panel>
      </PanelGrid>
    </>
  );
}

/* -------------------------------------------------------------------------
   Telemetry — the raw ledger.

   A ledger is an INSTRUMENT, not a printout. Three rules, and every one of
   them is the same rule the vitals strip and the lanes above already obey, so
   the three surfaces can never disagree about the same number:

     · the value is the ink. Measurements are --text-hi, so they separate from
       the --text-mid header row; the two machine-provenance columns (the fix
       and the sequence number) drop to --text-low. Three steps, so the eye
       reads "header / value / provenance" without a legend.
     · a reading past its published limit is recoloured IN PLACE, in the same
       --warn/--error the KPI and the lane use, and carries the same
       alert-triangle / alert-octagon glyph the vitals fly. Shape, not only
       hue, so the threshold survives a colour-blind operator and a greyscale
       print (THE BAR §13 tell 15).
     · the row itself takes a 2px leading-edge tick when ANY channel on it is
       out of limits — level 3 of §5's status hierarchy, the same tick the
       failed command row already carries, and the thing that makes an
       excursion findable while scrolling 150 rows at speed.

   Mode is the ribbon, projected into a column: the dot is the state's own
   colour (the one the Mode ribbon 200px above paints), so a run of `error`
   is a red edge running down the table and a mode change is a visible seam.
   ------------------------------------------------------------------------- */

/** The channels by key, so the ledger judges against the SAME published
    limits as the vitals and the lanes. Written once, in CHANNELS. */
const CHANNEL_BY_KEY: ReadonlyMap<string, ChannelDef> =
  new Map(CHANNELS.map((c) => [c.key, c]));

type Level = 'ok' | 'warn' | 'bad' | 'unknown';

/**
 * Where this reading sits against its channel's published limits.
 *
 * A channel with a `judge` is read on the quantity it is actually judged on —
 * free disk is judged on used %, because capacity is not published per robot —
 * so the `Disk free` and `Used` columns can never colour differently for the
 * same reading.
 */
function readingLevel(key: string, t: TelemetryReading): Level {
  const def = CHANNEL_BY_KEY.get(key);
  if (def === undefined || def.warn === undefined || def.bad === undefined) return 'ok';
  const v = def.judge !== undefined ? def.judge(t) : def.pick(t);
  return level(v, def.warn, def.bad, def.invert === true);
}

/** The limit sentence a breached cell carries, in the value's own units. */
function limitTitle(key: string, lv: Level): string | undefined {
  if (lv !== 'warn' && lv !== 'bad') return undefined;
  const def = CHANNEL_BY_KEY.get(key);
  if (def === undefined) return undefined;
  const limit = lv === 'bad' ? def.bad : def.warn;
  const what = def.judgeLabel ?? lowerLabel(def.label);
  const unit = def.judgeUnit ?? def.unit;
  const dir = def.invert === true ? 'over' : 'under';
  return `${what} ${dir} ${limit}${unitIsTight(unit) ? '' : ' '}${unit}`
    + ` — ${lv === 'bad' ? 'past hard limit' : 'past warning limit'}`;
}

/**
 * One measured cell.
 *
 * `--text-hi` while the reading is inside its limits; the channel's own
 * threshold colour the moment it is not, with the glyph the rest of the page
 * already uses for that severity. The glyph sits to the LEFT of the numeral
 * inside a right-aligned cell, so the column of numbers still aligns on its
 * own edge (§4) and a breach never moves a digit.
 */
function Reading({
  ch, value, digits, reading,
}: {
  ch: string;
  value: number | null;
  digits: number;
  reading: TelemetryReading;
}) {
  if (value === null) return <span className={styles.lvNull}>{NO_VALUE}</span>;
  const lv = readingLevel(ch, reading);
  const breached = lv === 'warn' || lv === 'bad';
  return (
    <span
      className={styles.lv}
      data-tone={breached ? levelTone(lv) : undefined}
      title={limitTitle(ch, lv)}
    >
      {breached && (
        <Icon name={lv === 'bad' ? 'alert-octagon' : 'alert-triangle'} size={9} />
      )}
      {num(value, digits)}
    </span>
  );
}

/** A reading with no threshold behind it — a fact, never a judgement. */
function Plain({ value, digits }: { value: number | null; digits: number }) {
  if (value === null) return <span className={styles.lvNull}>{NO_VALUE}</span>;
  return <span className={styles.lv}>{num(value, digits)}</span>;
}

/**
 * The mode column: the ribbon's own colour, as a dot plus the enum.
 *
 * A dot is §5's level 4 and scales to hundreds of rows where a badge does
 * not; the word takes the state's text variant so a run of `error` reads as a
 * red column and `active` does not read as `error` in the same grey. The enum
 * is mono, because the agent wrote it (§4).
 */
function ModeCell({ state }: { state: string | null }) {
  if (state === null) return <span className={styles.lvNull}>{NO_VALUE}</span>;
  const desc = STATUS_STATE[state as RobotStatus] as StateDescriptor | undefined;
  const tone: Tone = desc?.tone ?? 'neutral';
  /* The DOT is the state's literal colour — the one the ribbon paints — so
     the two surfaces agree and a mode change is a visible seam. The WORD is
     projected through matrixTone, which is this console's standing rule for a
     categorical state repeated down hundreds of rows: saturate only where a
     human has to act. `error` is therefore a red column; `active` is a bright
     column with a green dot, not 150 green words. */
  const eventful = matrixTone(tone) !== 'nominal';
  return (
    <span className={styles.lMode} data-tone={tone} data-eventful={eventful} title={desc?.hint}>
      <StatusDot tone={tone} hollow={desc === undefined} />
      {state}
    </span>
  );
}

/** The worst excursion anywhere on this reading — the row's leading tick. */
function rowExcursion(t: TelemetryReading): Tone | null {
  let worst: Level = 'ok';
  for (const def of CHANNELS) {
    const lv = readingLevel(def.key, t);
    if (lv === 'bad') return levelTone('bad');
    if (lv === 'warn') worst = 'warn';
  }
  return worst === 'warn' ? levelTone('warn') : null;
}

const TELEMETRY_COLUMNS: readonly Column<TelemetryReading>[] = [
  {
    /* The stamp IS the row's identity here, so it carries the row's ink. It is
       written out rather than delegated to <Timestamp strong>, whose `strong`
       step is --text-mid — right for an age beside a value, wrong for the key
       of a ledger. (Noted for the primitives pass: Timestamp has no
       "this is the primary column" step.) */
    key: 'ts', header: 'Time', kind: 'time', max: '13ch',
    cell: (t) => (
      <time
        className={styles.lv}
        dateTime={t.ts}
        title={`${utc(t.ts)}  ·  ${local(t.ts)}`}
      >
        {utcTime(t.ts)}
      </time>
    ),
  },
  {
    key: 'state', header: 'Mode', kind: 'enum', mono: true,
    cell: (t) => <ModeCell state={t.state} />,
  },
  {
    key: 'batt', header: 'Batt', kind: 'num', unit: '%', align: 'right', mono: true,
    cell: (t) => <Reading ch="batt" value={t.battery_pct} digits={1} reading={t} />,
  },
  {
    key: 'speed', header: 'Speed', kind: 'num', unit: 'm/s', align: 'right', mono: true,
    cell: (t) => <Plain value={t.speed_mps} digits={2} />,
  },
  {
    key: 'cpu', header: 'CPU', kind: 'num', unit: '%', align: 'right', mono: true,
    cell: (t) => <Reading ch="cpu" value={t.cpu_pct} digits={1} reading={t} />,
  },
  {
    key: 'temp', header: 'Temp', kind: 'num', unit: '°C', align: 'right', mono: true,
    cell: (t) => <Reading ch="temp" value={t.temp_c} digits={1} reading={t} />,
  },
  {
    key: 'disk', header: 'Disk free', kind: 'num', unit: 'GiB', align: 'right', mono: true,
    cell: (t) => <Reading ch="disk" value={t.disk_free_gib} digits={1} reading={t} />,
  },
  {
    key: 'diskused', header: 'Used', kind: 'num', unit: '%', align: 'right', mono: true, drop: 3,
    cell: (t) => <Reading ch="disk" value={t.disk_used_pct} digits={1} reading={t} />,
  },
  {
    key: 'head', header: 'Heading', kind: 'num', unit: '°', align: 'right', mono: true, drop: 2,
    cell: (t) => <Plain value={t.heading_deg} digits={1} />,
  },
  {
    key: 'rssi', header: 'Link', kind: 'num', unit: 'dBm', align: 'right', mono: true, drop: 1,
    cell: (t) => <Reading ch="rssi" value={payloadNum(t, 'link_rssi_dbm')} digits={0} reading={t} />,
  },
  {
    key: 'sats', header: 'GNSS', kind: 'num', unit: 'sats', align: 'right', mono: true, drop: 1,
    cell: (t) => <Reading ch="gnss" value={payloadNum(t, 'gnss_sats')} digits={0} reading={t} />,
  },
  {
    /* A coordinate is machine provenance, not a measurement to judge — it
       drops a step to --text-low so the columns that ARE judged own the ink. */
    key: 'fix', header: 'Fix', kind: 'id', mono: true, drop: 4,
    cell: (t) => <span className={styles.lvDim}>{latlon(t.lat, t.lon)}</span>,
  },
  {
    key: 'skew', header: 'Clock', kind: 'num', unit: 'ms', align: 'right', mono: true, drop: 5,
    cell: (t) => <Plain value={topLevelNum(t, 'clock_offset_ms')} digits={2} />,
  },
  {
    key: 'seq', header: 'Seq', kind: 'num', align: 'right', mono: true, drop: 6,
    cell: (t) => <span className={styles.lvDim}>{num(topLevelNum(t, 'seq'), 0)}</span>,
  },
];

/**
 * The fields of the selected reading that NO column carries.
 *
 * A ledger that shows thirteen of a record's seventeen fields and offers no
 * way to reach the other four is a filtered view pretending to be the record.
 * This strip is the rest of it — the store's row id, the agent boot the
 * reading belongs to, the agent's own monotonic clock, and every payload key
 * the columns did not lift out — pinned under the table at a fixed height so
 * selecting a row never moves the ledger.
 *
 * It defaults to the newest reading, so the strip is never an empty promise.
 */
function ReadingInspector({
  reading, now, selected,
}: {
  reading: TelemetryReading | null;
  now: number;
  selected: boolean;
}) {
  if (reading === null) return null;
  const boot = topLevelStr(reading, 'agent_boot_id');
  const monoNs = topLevelNum(reading, 'mono_ns');
  const extra = Object.entries(reading.payload)
    .filter(([k]) => k !== 'link_rssi_dbm' && k !== 'gnss_sats');

  return (
    <div className={styles.inspect}>
      <span className={styles.inspectEye}>{selected ? 'reading' : 'latest'}</span>
      <span className={styles.inspectRow}>
        <b>#{num(topLevelNum(reading, 'seq'), 0)}</b>
        <i>row</i>{reading.id}
        <i>agent</i>{utc(reading.ts)}
        <i>age</i>{duration(now - new Date(reading.ts).getTime())}
        <i>boot</i>{boot ?? NO_VALUE}
        <i>mono</i>{monoNs === null ? NO_VALUE : `${(monoNs / 1e9).toFixed(3)} s`}
        {extra.map(([k, v]) => (
          <span key={k}><i>{k}</i>{String(v)}</span>
        ))}
      </span>
    </div>
  );
}

/**
 * The ledger is bound to the SAME window as the instrument, and carries the
 * same transport under it. Scrubbing on Overview and switching to Telemetry
 * lands on the rows you were looking at — one page, one time axis, which is
 * the whole point (foxglove-multipanel-tabbed-workspace.png).
 *
 * Newest first here, because a table is read downward from the top and the
 * top of a telemetry ledger is "now".
 */
function TelemetryTab({
  facts, ctl, read, hasHistory, loading,
}: {
  facts: WindowFacts;
  ctl: WindowCtl;
  /** The read the rows came from. */
  read: Read;
  hasHistory: boolean;
  loading: boolean;
}) {
  const rows = useMemo(() => [...facts.readings].reverse(), [facts.readings]);
  const [pinned, setPinned] = useState<string | null>(null);
  const now = useNow();
  const lossPct = facts.seqSpan !== null && facts.seqSeen !== null && facts.seqSpan > 0
    ? (1 - facts.seqSeen / facts.seqSpan) * 100
    : null;

  /* How many readings in this window are out of limits. A count in the
     header is what turns "150 rows" into "150 rows, 36 of which you care
     about" before the operator has scrolled anything. */
  const breached = useMemo(
    () => rows.reduce((n, t) => n + (rowExcursion(t) === null ? 0 : 1), 0),
    [rows],
  );

  const shown = useMemo(
    () => rows.find((t) => String(t.id) === pinned) ?? rows[0] ?? null,
    [rows, pinned],
  );

  return (
    <Panel
      className={styles.grow}
      title="Readings"
      count={figure(read, rows.length)}
      loading={loading}
      meta={
        <span className={styles.ledgerMeta}>
          <span>{duration(ctl.to - ctl.from)} window</span>
          <span>{facts.cadenceMs === null ? 'cadence unknown' : `every ${duration(facts.cadenceMs)}`}</span>
          {lossPct !== null && (
            <span data-tone={lossPct > 1 ? 'warn' : undefined}>
              {lossPct < 0.05 ? 'no frame loss' : `${lossPct.toFixed(1)}% frames lost`}
            </span>
          )}
          {breached > 0 && (
            <span data-tone="error" title="Readings with at least one channel past a published limit.">
              {breached} out of limits
            </span>
          )}
          <ReadTag read={read} />
          <span>{AXIS_LABEL[ctl.axis]}</span>
        </span>
      }
      pad="none"
    >
      {/* The fault line the ledger owed. The header's read tag says `No
          reading`; this says WHICH source, which is the one fact nothing else
          on a partial outage can supply. */}
      {unread(read) && <FaultLine read={read} age={false} />}
      <div className={styles.instrument}>
        <div className={styles.ledgerScroll}>
          <DataTable
            rows={rows}
            columns={TELEMETRY_COLUMNS}
            rowKey={(t) => String(t.id)}
            rowTone={rowExcursion}
            toneScale="literal"
            onRowClick={(t) => setPinned(String(t.id))}
            currentKey={shown === null ? null : String(shown.id)}
            of={read}
            empty={hasHistory
              ? 'No readings in this window — the scrubber shows where they are.'
              : 'No telemetry has ever arrived from this robot.'}
          />
        </div>
        <ReadingInspector
          reading={shown}
          now={now}
          selected={pinned !== null && shown !== null && String(shown.id) === pinned}
        />
        <TransportBar {...ctl} cursor={null} read={read} />
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------
   Commands. The verb set is the robot's OWN command_profile, grouped by the
   hazard class the profile declares, and the motion class is behind a lease
   the operator has to take — driving is never the page's default state.
   ------------------------------------------------------------------------- */

/** A terminal command's span is closed; an in-flight one is still running. */
const CMD_TERMINAL: ReadonlySet<CommandStatus> = new Set<CommandStatus>([
  'completed', 'failed', 'cancelled',
]);

/**
 * Commands as MARKS AT THEIR TRUE TIMESTAMPS, one lane per verb
 * (foxglove-event-editor-timeline-markers.png), painted under the robot's own
 * mode ribbon so "what was it doing" and "what did we tell it" share one
 * x-scale (inorbit-robot-detail-dense.png).
 *
 * A span is issued_at → updated_at for a command that reached a terminal
 * state, and issued_at → now for one still in flight. Nothing is invented:
 * both endpoints are contract fields, and an in-flight bar running to the
 * right-hand edge is the literal truth of "still pending".
 */
function commandLanes(
  commands: readonly Command[] | undefined,
  now: number,
  /* A RENDERING FLOOR, not a claim: a command that completed in five seconds
     is 0.02% of a six-hour window and would otherwise be a sub-pixel nothing.
     Every segment gets at least a visible tick; the true instants are in the
     hover title and, to the millisecond, in the ledger above. */
  minSpan: number,
): RibbonLane[] {
  const order: string[] = [];
  const byVerb = new Map<string, RibbonSegment[]>();
  for (const c of [...(commands ?? [])].reverse()) {
    const start = new Date(c.issued_at).getTime();
    if (!Number.isFinite(start)) continue;
    const upd = new Date(c.updated_at).getTime();
    const raw = CMD_TERMINAL.has(c.status)
      ? Math.max(Number.isFinite(upd) ? upd : start, start + 1)
      : Math.max(now, start + 1);
    const end = Math.max(raw, start + minSpan);
    if (!byVerb.has(c.command)) { byVerb.set(c.command, []); order.push(c.command); }
    byVerb.get(c.command)!.push({
      start,
      end,
      tone: CMD_TONE[c.status],
      label: c.status,
    });
  }
  return order.map((v) => ({
    key: v,
    label: COMMAND_LABEL[v as CommandName] ?? v,
    segments: byVerb.get(v) ?? [],
  }));
}

function CommandTimeline({
  facts, ctl, commands, read,
}: {
  facts: WindowFacts;
  ctl: WindowCtl;
  commands: readonly Command[] | undefined;
  /** The telemetry read behind the mode ribbon and the presence band. */
  read: Read;
}) {
  const [cursor, setCursor] = useState<number | null>(null);
  const tick = useNow(true);
  const lanes = useMemo(() => {
    const mode: RibbonLane = {
      key: '__mode',
      label: 'Mode',
      segments: runSegments(
        facts.readings, facts.times, ctl.to, tolerance(facts.cadenceMs),
        (t) => t.state,
        (v) => STATUS_STATE[v as RobotStatus]?.tone ?? 'neutral',
      ),
    };
    return [mode, ...commandLanes(commands, tick, (ctl.to - ctl.from) * 0.004)];
  }, [facts.readings, facts.times, facts.cadenceMs, ctl.from, ctl.to, commands, tick]);

  /* How many of the robot's commands the window is actually showing. A
     timeline that silently hides four of seven is worse than no timeline. */
  const inWindow = (commands ?? []).filter((c) => {
    const t = new Date(c.issued_at).getTime();
    return t >= ctl.from && t <= ctl.to;
  }).length;
  const total = commands?.length ?? 0;

  /* The stack takes its lanes and stops: a ribbon is a 22px band whatever the
     panel's height, and stretching four of them over 300px would make a
     three-command robot look like a nine-verb one. The ledger below takes the
     rest of the surface. */
  return (
    <Panel
      className={styles.fit}
      title="Timeline"
      count={`${inWindow} of ${total}`}
      meta={
        <span className={styles.toolbarNote}>
          {inWindow === total ? 'all commands in window' : 'widen the window for the rest'}
        </span>
      }
      pad="none"
    >
      <div className={styles.instrument}>
        <div className={styles.chart}>
          <RibbonStack
            lanes={lanes}
            from={ctl.from}
            to={ctl.to}
            {...(ctl.live ? { now: ctl.extTo } : {})}
            gutter={CH_GUTTER}
            laneHeight={RIBBON_H}
            footer={
              <>
                <span>{duration(ctl.to - ctl.from)} window</span>
                <span>{lanes.length - 1} verbs</span>
                <span>{AXIS_LABEL[ctl.axis]}</span>
                <span>
                  {utc(new Date(ctl.from).toISOString(), false)}
                  {' → '}
                  {utc(new Date(ctl.to).toISOString(), false)}
                </span>
              </>
            }
          >
            {(cur) => <CursorSync t={cur} onChange={setCursor} />}
          </RibbonStack>
        </div>
        <TransportBar {...ctl} cursor={cursor} read={read} />
      </div>
    </Panel>
  );
}

function CommandsTab({
  robot,
  commands,
  streamConnected,
  facts,
  ctl,
  read,
  tlmRead,
  leaseUntil,
  onLease,
}: {
  robot: Robot;
  commands: readonly Command[] | undefined;
  /** The queue read — every count on this tab is derived from it. */
  read: Read;
  /** The telemetry read — the ribbon's mode lane and the presence band. */
  tlmRead: Read;
  streamConnected: boolean;
  facts: WindowFacts;
  ctl: WindowCtl;
  /* The lease belongs to the page, not to this tab — the switch that takes it
     rides the identity row and is on screen on every surface. This tab reads
     it and renders what it gates. */
  leaseUntil: number | null;
  onLease: (until: number | null) => void;
}) {
  const issue = useIssueCommand();
  const feedWord = useFeedWord();
  const now = useNow(leaseUntil !== null);
  const unlocked = leaseUntil !== null && leaseUntil > now;
  /* Clamped for the same reason the header's readout is: the shared ticker is
     one frame behind the click that takes the lease. */
  const leaseLeft = leaseUntil === null ? 0 : Math.min(LEASE_MS, Math.max(0, leaseUntil - now));

  const profile = readProfile(robot);
  const entries = profile ?? [];
  /* Abort goes last in its row — destructive and abort verbs sit at the end
     of a verb group (anduril: `Edit | Complete | ✕ Cancel`). Sort is stable,
     so the profile's own order survives otherwise. */
  const abortLast = (a: ProfileEntry, b: ProfileEntry) =>
    (a.name === 'stop' ? 1 : 0) - (b.name === 'stop' ? 1 : 0);

  /* ONE GROUP PER HAZARD CLASS THE PROFILE ACTUALLY PUBLISHES, in the
     contract's own vocabulary. Splitting "motion" from "everything else"
     silently files every other class under the benign heading — and koko's
     `cut` is hazard `hazardous`, a chainsaw, which is not a verb to render
     under a row that says NO LEASE REQUIRED. An unknown class the agent
     invents later gets its own group under its own name rather than being
     absorbed into the safest one on screen. */
  const groups = [...new Set(entries.map((e) => e.hazard))]
    .sort((a, b) => hazardRank(a) - hazardRank(b) || a.localeCompare(b))
    .map((hazard) => ({
      hazard,
      verbs: entries.filter((e) => e.hazard === hazard).sort(abortLast),
    }));
  const gatedCount = entries.filter((e) => e.hazard !== 'benign').length;
  const needParams = entries.filter((e) => e.required.length > 0);

  const verb = (e: ProfileEntry) => {
    const name = e.name as CommandName;
    const label = COMMAND_LABEL[name] ?? e.name;
    const mark = COMMAND_MARK[name];
    const needsParams = e.required.length > 0;
    const gated = e.hazard !== 'benign' && !unlocked;
    const disabled = issue.isPending || needsParams || gated;
    const title = needsParams
      ? `${e.name} requires ${e.required.join(', ')} — compose it from the mission surface`
      : gated
        ? `${e.name} is ${e.hazard} class — take the actuation lease from the switch in the header first`
        : `Issue ${e.name} (${e.hazard} class)`;
    return (
      <Button
        key={e.name}
        size="sm"
        /* `stop` is listed here because the robot publishes it, but it does
           NOT take the filled treatment: the page's one abort is the amber
           Stop in the identity row, which is on screen on every surface and
           never moves (§11.7 — the abort keeps its position across states).
           Two filled aborts in two places is two places to look. Red stays
           spent where the contract says the hazard is. */
        variant={e.hazard === 'hazardous' ? 'danger' : 'secondary'}
        {...(mark === undefined ? {} : { icon: mark })}
        disabled={disabled}
        title={title}
        onClick={() => issue.mutate({ robotId: robot.robot_id, command: name })}
      >
        {label}
      </Button>
    );
  };

  const columns: readonly Column<Command>[] = [
    {
      key: 'cmd', header: 'Command', kind: 'identity', mono: true,
      cell: (c) => <span className={styles.lv}>{c.command}</span>,
    },
    {
      key: 'status', header: 'Status', kind: 'status',
      cell: (c) => (
        <StateLabel tone={CMD_TONE[c.status]} label={c.status} loud={c.status === 'failed'} />
      ),
    },
    {
      key: 'instate', header: 'In state', kind: 'age', align: 'right',
      cell: (c) => (
        <span title={`Entered ${c.status} at ${utc(c.updated_at)}`}>
          {duration(Date.now() - new Date(c.updated_at).getTime())}
        </span>
      ),
    },
    {
      key: 'issued', header: 'Issued', kind: 'age', align: 'right',
      cell: (c) => <Timestamp value={c.issued_at} strong />,
    },
    {
      key: 'by', header: 'By', kind: 'id', mono: true, drop: 1,
      cell: (c) => <span className={styles.lvDim}>{c.issued_by}</span>,
    },
    {
      key: 'params', header: 'Params', kind: 'text', mono: true, drop: 3,
      cell: (c) => (Object.keys(c.params).length === 0
        ? <span className={styles.lvNull}>{NO_VALUE}</span>
        : <span className={styles.lvDim}>{JSON.stringify(c.params)}</span>),
    },
    {
      key: 'result', header: 'Result', kind: 'text', mono: true, drop: 2,
      cell: (c) => (c.result === null
        ? <span className={styles.lvNull}>{NO_VALUE}</span>
        : (
          <span
            className={styles.lv}
            data-tone={c.status === 'failed' ? 'error' : undefined}
          >
            {JSON.stringify(c.result)}
          </span>
        )),
    },
  ];

  return (
    <>
      <Panel
        title="Issue command"
        meta={
          /* Off the read, not off a constant: `● Polling 20s` over a queue
             that never answered is the same lie as `0 faulted`. */
          <LiveTag
            live={read.phase === 'live'}
            label={feedWord}
            pulse={streamConnected && read.phase === 'live'}
            {...(streamConnected ? {} : { every: CMD_POLL_MS })}
          />
        }
        /* THE LEASE ITSELF LIVES IN THE IDENTITY ROW, above every surface,
           because an arming state you can only see on one tab is an arming
           state you cannot see (§13 tell 25). What stays here is the
           consequence — a readout of the authority these verbs are being
           judged against, and a way to release it without travelling. */
        tools={gatedCount > 0 ? (
          unlocked ? (
            <button
              type="button"
              className={styles.leaseEcho}
              data-held="true"
              title={`Actuation authority held — releases in ${duration(leaseLeft)}. Click to release it now.`}
              onClick={() => onLease(null)}
            >
              <Icon name="unlock" size={12} />
              <span className={styles.leaseWord}>lease held · {duration(leaseLeft)}</span>
              <span className={styles.leaseRelease}>release</span>
            </button>
          ) : (
            <span
              className={styles.leaseEcho}
              title="Take actuation authority with the switch in the header. It gates every verb below the benign group."
            >
              <Icon name="lock" size={12} />
              <span className={styles.leaseWord}>lease not held</span>
            </span>
          )
        ) : undefined}
      >
        {profile === null ? (
          <Awaiting
            label="No command profile published for this robot"
            detail="The agent advertises its verb set on enrolment; nothing has arrived yet."
          />
        ) : (
          <div className={styles.verbs}>
            {groups.map((g) => (
              <Section
                key={g.hazard}
                label={`${g.hazard} · ${g.verbs.length}`}
                aside={
                  g.hazard === 'benign'
                    ? <span>no lease required</span>
                    : <span>{unlocked ? 'lease held' : 'lease required'}</span>
                }
              >
                <div className={styles.verbRow} data-locked={g.hazard !== 'benign' && !unlocked}>
                  {g.verbs.map(verb)}
                </div>
              </Section>
            ))}

            {robot.status === 'error' && (
              <p className={styles.verbNote}>
                {robot.robot_id} is {lowerFirst(STATUS_STATE.error.hint)}. The queue will
                accept the command and the robot will reject it.
              </p>
            )}

            {needParams.length > 0 && (
              <p className={styles.paramNote}>
                {needParams
                  .map((e) => `${e.name} needs ${e.required.join(', ')}`)
                  .join(' · ')}
                {' — '}
                the console has no parameter composer yet, so these stay inert here.
              </p>
            )}
          </div>
        )}
        {issue.isError && <ErrorLine>{String(issue.error)}</ErrorLine>}
      </Panel>

      {(commands?.length ?? 0) > 0 && (
        <CommandTimeline facts={facts} ctl={ctl} commands={commands} read={tlmRead} />
      )}

      {/* The ledger takes its rows and stops. A command log is a handful of
          entries on most robots, and a 26px header over 445px of empty panel
          body reads as a broken instrument; the workspace ground under it
          reads as what it is — nothing more to show. Past 14 rows it caps and
          scrolls under its own sticky header. */}
      <Panel
        className={styles.grow}
        title="Command history"
        count={figure(read, commands?.length)}
        pad="none"
      >
        {unread(read) && <FaultLine read={read} />}
        <DataTable
          rows={commands ?? []}
          columns={columns}
          rowKey={(c) => String(c.id)}
          rowTone={(c) => CMD_TONE[c.status]}
          rowAlarm={(c) => c.status === 'failed'}
          of={read}
          empty="No commands issued to this robot."
        />
      </Panel>
    </>
  );
}

/* -------------------------------------------------------------------------
   Configuration. A document, not a stream of mutations: the registry record
   edits in place with an explicit Save ⌘S, and the revision pin is a
   selection followed by a commit — clicking a row never dispatches.
   ------------------------------------------------------------------------- */

function RegistryForm({ robot }: { robot: Robot }) {
  const update = useUpdateRobot(robot.robot_id);
  const [name, setName] = useState(robot.name ?? '');
  const [morphology, setMorphology] = useState<Morphology>(robot.morphology as Morphology);
  const [status, setStatus] = useState<RobotStatus>(robot.status);
  const [firmware, setFirmware] = useState(robot.firmware ?? '');
  const [active, setActive] = useState(robot.active);

  /* Seeded once, at mount, and re-seeded only by the `key` the caller hangs
     off robot_id. A 5s poll changes `last_seen` on every tick, so an effect
     that re-seeded from `robot` would wipe the operator's half-typed edit
     four times a minute — the classic way a live console eats a form. */
  const revert = useCallback(() => {
    setName(robot.name ?? '');
    setMorphology(robot.morphology as Morphology);
    setStatus(robot.status);
    setFirmware(robot.firmware ?? '');
    setActive(robot.active);
  }, [robot]);

  const dirty =
    name !== (robot.name ?? '') ||
    morphology !== robot.morphology ||
    status !== robot.status ||
    firmware !== (robot.firmware ?? '') ||
    active !== robot.active;

  const save = useCallback(() => {
    if (!dirty) return;
    update.mutate({
      name: name.trim() || null,
      morphology,
      status,
      firmware: firmware.trim() || null,
      active,
    });
  }, [dirty, update, name, morphology, status, firmware, active]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [save]);

  const submit = (e: FormEvent) => { e.preventDefault(); save(); };

  return (
    <Panel
      title="Registry record"
      meta={dirty ? <span className={styles.dirty}>unsaved</span> : <span>saved</span>}
    >
      <form className={styles.form} onSubmit={submit}>
        <div className={styles.formGrid}>
          <Field label="Name" htmlFor="cfg-name" hint="Blank falls back to the robot id.">
            <Input
              id="cfg-name" mono value={name} placeholder={robot.robot_id}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field label="Morphology" htmlFor="cfg-morph">
            <Select
              id="cfg-morph" mono value={morphology}
              onChange={(e) => setMorphology(e.target.value as Morphology)}
            >
              {MORPHOLOGIES.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
          <Field label="Status" htmlFor="cfg-status">
            <Select
              id="cfg-status" mono value={status}
              onChange={(e) => setStatus(e.target.value as RobotStatus)}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Firmware" htmlFor="cfg-fw" optional>
            <Input
              id="cfg-fw" mono value={firmware} placeholder="e.g. 3.0.0-rc2"
              onChange={(e) => setFirmware(e.target.value)}
            />
          </Field>
        </div>

        <Checkbox
          label="Active — in service and eligible for tasking"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
        />

        {morphology !== robot.morphology && (
          <p className={styles.warnLine}>
            Saving repins {robot.robot_id} to {morphology} revision 001 — a revision belongs
            to a morphology — and changes which commands it may be issued.
          </p>
        )}
        {update.isError && <ErrorLine>{String(update.error)}</ErrorLine>}

        {/* The commit is raised, not accent-filled. §11.7: the abort verb
            owns the surface's one primary treatment, and it is now permanent
            in the identity row — so a staged form commits with a neutral
            raised verb and its shortcut, the way Viam ships a black `Save ⌘S`
            rather than a branded one. */}
        <div className={styles.formFoot}>
          <Button type="button" variant="ghost" disabled={!dirty} onClick={revert}>
            Revert
          </Button>
          <span className={styles.commit} data-armed={dirty && !update.isPending}>
            <Button
              type="submit"
              kbd="⌘S"
              disabled={!dirty || update.isPending}
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </span>
        </div>
      </form>
    </Panel>
  );
}

function RevisionPanel({ robotId }: { robotId: string }) {
  const revQ = useRobotRevisions(robotId);
  const { data, isLoading } = revQ;
  /* `Hardware revision (0)` on a failed read is the panel reporting that this
     morphology has no revisions on file. */
  const read = useRead(revQ, {
    path: `/api/robots/${robotId}/revisions`,
    subject: 'the revision list',
  });
  const setRev = useSetRevision(robotId);
  const [pending, setPending] = useState<number | null>(null);

  const current = data?.current ?? null;

  /* A pin that lands (or one made elsewhere) drops the staged selection, so
     the footer never offers to commit a change that already happened. */
  useEffect(() => { setPending(null); }, [robotId, current]);

  const chosen = pending ?? current;
  const target = data?.revisions.find((r) => r.id === chosen) ?? null;
  const changed = chosen !== null && chosen !== current;

  const columns: readonly Column<Revision>[] = [
    {
      key: 'pin', header: '', kind: 'icon',
      cell: (r) => (r.id === current
        ? <Icon name="check" size={14} title="Currently pinned" />
        : null),
    },
    {
      key: 'version', header: 'Revision', kind: 'identity', mono: true,
      cell: (r) => <span className={styles.lv}>{r.version}</span>,
    },
    {
      key: 'board', header: 'Board', kind: 'enum', mono: true,
      cell: (r) => <span className={styles.lv}>{revMetaStr(r, 'board') ?? NO_VALUE}</span>,
    },
    {
      key: 'notes', header: 'Notes', kind: 'text', dim: true, flex: true,
      cell: (r) => r.notes ?? NO_VALUE,
    },
    {
      key: 'bringup', header: 'Bringup', kind: 'status', drop: 1,
      cell: (r) => {
        const ready = revBool(r, 'bringup_ready');
        /* `incomplete` is a fact about an old revision, not an alarm: it goes
           grey, not amber, so the one green means "this bringup will run". */
        return ready === null
          ? NO_VALUE
          : <StateLabel tone={ready ? 'good' : 'neutral'} label={ready ? 'ready' : 'incomplete'} />;
      },
    },
    {
      key: 'created', header: 'Created', kind: 'date', align: 'right', drop: 2,
      cell: (r) => <Timestamp value={r.created_at} strong />,
    },
  ];

  return (
    <Panel
      /* Natural height, deliberately: ledger + staged manifest + commit row is
         a fixed-shape instrument, and letting it absorb the tab's slack put
         ~150px of bare ground between the manifest and its own commit verb at
         1920×1080. The tab's slack goes to the profile table below, which is
         the one surface here whose content genuinely uses height. */
      title="Hardware revision"
      count={figure(read, data?.revisions.length)}
      loading={isLoading}
      meta={<span>{data?.morphology ?? NO_VALUE}</span>}
      pad="none"
    >
      <div className={styles.tableFrame}>
        <DataTable
          rows={data?.revisions ?? []}
          columns={columns}
          rowKey={(r) => String(r.id)}
          currentKey={chosen === null ? null : String(chosen)}
          onRowClick={(r) => setPending(r.id)}
          of={read}
          empty="No revisions published for this morphology."
        />
        {/* WHAT A REPIN ACTUALLY PINS. A revision is not a version string —
            it is a bring-up script and a payload tarball at two content
            hashes, and those are the thing the robot will fetch and run. An
            operator committing an irreversible-ish change gets to read the
            artefact before the verb, not after. */}
        {target !== null && (
          <div className={styles.manifest}>
            <div className={styles.manifestHead}>
              <span className={styles.manifestVer}>{target.version}</span>
              <StateLabel
                tone={revBool(target, 'bringup_ready') === true ? 'good' : 'neutral'}
                label={revBool(target, 'bringup_ready') === true ? 'bringup ready' : 'bringup incomplete'}
              />
              {revBool(target, 'validated') === true || revMetaStr(target, 'board') !== null ? (
                <span className={styles.manifestNote}>
                  {revMetaStr(target, 'board') ?? NO_VALUE}
                  {target.meta['validated'] === true ? ' · validated' : ''}
                </span>
              ) : null}
              <span className={styles.manifestSpacer} />
              <span className={styles.manifestNote}>{target.notes ?? NO_VALUE}</span>
            </div>
            <KeyValue
              layout="grid"
              cols={2}
              className={styles.manifestKv}
              items={[
                {
                  label: 'Bring-up script',
                  value: revStr(target, 'script_key') ?? 'not published',
                  title: revStr(target, 'script_key') ?? undefined,
                },
                {
                  label: 'Script sha256',
                  value: revStr(target, 'script_sha256') === null
                    ? NO_VALUE
                    : midEllipsis(revStr(target, 'script_sha256')!, 40),
                  title: revStr(target, 'script_sha256') ?? undefined,
                },
                {
                  label: 'Payload',
                  value: revStr(target, 'payload_key') ?? 'not published',
                  title: revStr(target, 'payload_key') ?? undefined,
                },
                {
                  label: 'Payload sha256',
                  value: revStr(target, 'payload_sha256') === null
                    ? NO_VALUE
                    : midEllipsis(revStr(target, 'payload_sha256')!, 40),
                  title: revStr(target, 'payload_sha256') ?? undefined,
                },
              ]}
            />
          </div>
        )}

        {/* Selecting a row stages; only this verb sends. Clicking a revision
            must never repin a robot on its own (THE BAR §13 tell 31). */}
        <div className={styles.pinFoot}>
          <span className={styles.pinNote}>
            {setRev.isError
              ? <span className={styles.pinError}>{String(setRev.error)}</span>
              : changed && target !== null
                ? `Staged: repin from ${data?.revisions.find((r) => r.id === current)?.version ?? NO_VALUE} to ${target.version}. Nothing is sent until you commit.`
                : 'Select a revision to stage a repin.'}
          </span>
          <Button
            size="sm"
            disabled={!changed || setRev.isPending}
            onClick={() => { if (chosen !== null) setRev.mutate(chosen); }}
          >
            {setRev.isPending
              ? 'Pinning…'
              : changed && target !== null ? `Pin ${target.version}` : 'Pin revision'}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/* The robot's own contract, as a document rather than as a row of buttons.
   The verb set, its hazard classes and its required parameters are what the
   agent advertised at enrolment — the answer to "what may this machine be
   asked to do", and provenance for every command in the ledger (§13 tell 23).
   The Commands surface renders the same profile as affordances; this renders
   it as the specification it is. */
function CommandProfilePanel({ robot }: { robot: Robot }) {
  const profile = readProfile(robot);
  const version = (() => {
    const v = robot.command_profile?.['version'];
    return typeof v === 'string' && v.length > 0 ? v : null;
  })();
  const platform = (() => {
    const v = robot.command_profile?.['platform_version'];
    return typeof v === 'string' && v.length > 0 ? v : null;
  })();

  const rows = (profile ?? [])
    .slice()
    .sort((a, b) => hazardRank(a.hazard) - hazardRank(b.hazard) || a.name.localeCompare(b.name));

  const columns: readonly Column<ProfileEntry>[] = [
    {
      key: 'verb', header: 'Verb', kind: 'identity', mono: true,
      cell: (e) => <span className={styles.lv}>{e.name}</span>,
    },
    {
      key: 'hazard', header: 'Hazard', kind: 'status',
      cell: (e) => (
        <StateLabel
          tone={e.hazard === 'benign' ? 'neutral' : e.hazard === 'motion' ? 'warn' : 'error'}
          label={e.hazard}
        />
      ),
    },
    {
      /* Only the gated rows carry ink: the column exists to make the
         benign→no-lease rule legible, not to print the same two words ten
         times. */
      key: 'lease', header: 'Lease', kind: 'enum', dim: true,
      cell: (e) => (e.hazard === 'benign' ? NO_VALUE : 'required'),
    },
    {
      key: 'params', header: 'Required params', kind: 'text', mono: true, flex: true,
      cell: (e) => (e.required.length === 0
        ? <span className={styles.lvNull}>{NO_VALUE}</span>
        : <span className={styles.lv}>{e.required.join(', ')}</span>),
    },
  ];

  return (
    <Panel
      /* The stack's slack claimant: a verb table gets longer with a richer
         agent, so extra height here is extra rows rather than extra ground. */
      className={styles.grow}
      title="Command profile"
      count={rows.length}
      meta={
        <span className={styles.toolbarNote}>
          {version === null ? NO_VALUE : version}
          {platform === null ? '' : ` \u00b7 platform ${platform}`}
        </span>
      }
      pad="none"
    >
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(e) => e.name}
        empty="No command profile published — the agent advertises its verb set on enrolment."
      />
    </Panel>
  );
}

function ConfigTab({ robot }: { robot: Robot }) {
  return (
    <div className={styles.scrollStack}>
      <PanelGrid cols="minmax(0,1fr) minmax(0,1fr)">
        <RegistryForm key={robot.robot_id} robot={robot} />
        <Panel title="Certificate" meta={<span>X.509</span>}>
          <KeyValue
            layout="rows"
            items={[
              {
                label: 'Serial',
                value: robot.cert_serial ?? NO_VALUE,
                title: robot.cert_serial ?? undefined,
              },
              {
                label: 'Not after',
                value: robot.cert_not_after === null
                  ? NO_VALUE
                  : <Timestamp value={robot.cert_not_after} mode="utc" strong />,
                mono: false,
              },
              {
                label: 'Remaining',
                value: robot.cert_not_after === null
                  ? NO_VALUE
                  : (
                    <Timestamp
                      value={robot.cert_not_after}
                      warnWithin={CERT_WARN_S}
                      badWithin={CERT_BAD_S}
                      strong
                    />
                  ),
                mono: false,
              },
              {
                label: 'Enrolled',
                value: robot.enrolled_at === null
                  ? 'never'
                  : <Timestamp value={robot.enrolled_at} mode="utc" strong />,
                mono: false,
              },
              { label: 'Enrolled by', value: robot.enrolled_by ?? NO_VALUE },
            ]}
          />
          <p className={styles.note}>
            Every cloud call from {robot.robot_id} is authenticated with this certificate.
            Re-issuing an enrolment token — from the ⋮ menu in the header — invalidates any
            unused one.
          </p>
        </Panel>
      </PanelGrid>

      {/* Deregister used to sit here as a second red verb. It is permanent in
          the identity row now, and duplicating it would put two red buttons on
          one screen — §3: red that appears twice on a healthy surface is red
          that has been devalued. The consequences are stated where they are
          consulted: in the confirm dialog itself. */}
      <RevisionPanel robotId={robot.robot_id} />
      <CommandProfilePanel robot={robot} />
    </div>
  );
}

/* -------------------------------------------------------------------------
   Captures.
   ------------------------------------------------------------------------- */

function capturesColumns(open: (id: number) => void): readonly Column<Scan>[] {
  return [
    {
      key: 'session', header: 'Session', kind: 'identity', mono: true, max: '30ch',
      cell: (s) => <span className={styles.lv}>{s.session_id}</span>,
    },
    {
      key: 'started', header: 'Captured', kind: 'age', align: 'right',
      cell: (s) => <Timestamp value={s.started_utc} strong />,
    },
    {
      key: 'dur', header: 'Duration', kind: 'age', align: 'right',
      cell: (s) => (s.started_utc === null || s.stopped_utc === null
        ? NO_VALUE
        : duration(new Date(s.stopped_utc).getTime() - new Date(s.started_utc).getTime())),
    },
    {
      key: 'size', header: 'Size', kind: 'num', align: 'right', mono: true, drop: 2,
      cell: (s) => <span className={styles.lv}>{bytes(s.size_bytes)}</span>,
    },
    /* `Frame` is the pose frame every point in the session is expressed in —
       a capture with no named datum is a capture you cannot register against
       anything else, so it outranks the sensor list for shelf space. */
    {
      key: 'frame', header: 'Frame', kind: 'enum', mono: true, drop: 3,
      cell: (s) => <span className={styles.lvDim}>{s.pose_frame ?? NO_VALUE}</span>,
    },
    {
      key: 'sync', header: 'Sync', kind: 'enum', mono: true, drop: 4,
      cell: (s) => <span className={styles.lvDim}>{s.sync_state ?? NO_VALUE}</span>,
    },
    {
      key: 'format', header: 'Format', kind: 'enum', mono: true, drop: 5,
      cell: (s) => <span className={styles.lvDim}>{s.format ?? NO_VALUE}</span>,
    },
    {
      key: 'sensors', header: 'Sensors', kind: 'enum', mono: true, drop: 6,
      max: '30ch',
      cell: (s) => (s.sensors.length === 0
        ? <span className={styles.lvNull}>{NO_VALUE}</span>
        : <span className={styles.lvDim} title={s.sensors.join(', ')}>{s.sensors.join(' · ')}</span>),
    },
    {
      key: 'files', header: 'Files', kind: 'num', align: 'right', mono: true, drop: 7,
      cell: (s) => <span className={styles.lv}>{String(scanFiles(s).length)}</span>,
    },
    {
      key: 'state', header: 'State', kind: 'status',
      cell: (s) => (
        <StateLabel
          tone={scanState(s.processing_state).tone}
          label={scanState(s.processing_state).label}
          loud={s.processing_state === 'failed'}
        />
      ),
    },
    {
      key: 'open', header: '', kind: 'action', align: 'right',
      cell: (s) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); open(s.id); }}
        >
          {s.processing_state === 'copc' ? 'View 3D' : 'Open'}
        </Button>
      ),
    },
  ];
}

/* -------------------------------------------------------------------------
   Coverage — where this robot's capture sessions actually are.

   Every session publishes a WGS84 bounding box; nothing in the console was
   drawing it, so "did canopy-2 overlap yard-sweep" was a question you could
   only answer by pasting four numbers into something else. Projected to
   metres from a common origin, stroke-only, with the robot's own track from
   the telemetry window on the same ground and the scale printed.

   Not a map: there is no tile source wired, and a basemap we do not have is
   not something to fake. The frame is named, the scale bar is drawn, north is
   up, and that is the whole claim.
   ------------------------------------------------------------------------- */

const COVER_PAD = 32;

function CoveragePlot({
  scans,
  facts,
  robot,
  selectedId,
  onPick,
}: {
  scans: readonly Scan[];
  facts: WindowFacts;
  robot: Robot;
  /** The session the inspector is showing. Ringed here, so the ledger, the
      pane and the ground all agree about which capture is under discussion. */
  selectedId: number | null;
  /** Clicking an extent selects it, the way clicking its ledger row does.
      Direct manipulation on a map never navigates away from the map. */
  onPick: (id: number) => void;
}) {
  const [ref, box] = useBox();
  const W = Math.max(0, Math.floor(box.w));
  const H = Math.max(0, Math.floor(box.h));

  const extents = scans
    .map((s) => {
      const minLat = scanNum(s, 'bbox_min_lat');
      const minLon = scanNum(s, 'bbox_min_lon');
      const maxLat = scanNum(s, 'bbox_max_lat');
      const maxLon = scanNum(s, 'bbox_max_lon');
      if (minLat === null || minLon === null || maxLat === null || maxLon === null) return null;
      return { s, minLat, minLon, maxLat, maxLon };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);

  const fixes = facts.readings.filter(
    (r): r is TelemetryReading & { lat: number; lon: number } => r.lat != null && r.lon != null,
  );

  const lat0 = extents.length > 0 ? extents[0]!.minLat : fixes[0]?.lat ?? robot.lat ?? 0;
  const lon0 = extents.length > 0 ? extents[0]!.minLon : fixes[0]?.lon ?? robot.lon ?? 0;
  const mPerLat = 110_540;
  const mPerLon = 111_320 * Math.cos((lat0 * Math.PI) / 180);
  const ex = (lon: number) => (lon - lon0) * mPerLon;
  const ny = (lat: number) => (lat - lat0) * mPerLat;

  const xs: number[] = [];
  const ys: number[] = [];
  for (const e of extents) {
    xs.push(ex(e.minLon), ex(e.maxLon));
    ys.push(ny(e.minLat), ny(e.maxLat));
  }
  for (const f of fixes) { xs.push(ex(f.lon)); ys.push(ny(f.lat)); }
  if (robot.lat != null && robot.lon != null) { xs.push(ex(robot.lon)); ys.push(ny(robot.lat)); }

  const ready = W > 40 && H > 40 && xs.length > 0;
  const minX = ready ? Math.min(...xs) : 0;
  const maxX = ready ? Math.max(...xs) : 1;
  const minY = ready ? Math.min(...ys) : 0;
  const maxY = ready ? Math.max(...ys) : 1;
  const spanX = Math.max(maxX - minX, 5);
  const spanY = Math.max(maxY - minY, 5);
  /* One metres-per-pixel for both axes: a coverage plot that stretched one
     axis would make a square block look like a strip. */
  const scale = Math.min((W - COVER_PAD * 2) / spanX, (H - COVER_PAD * 2) / spanY);
  const ox = (W - spanX * scale) / 2;
  const oy = (H - spanY * scale) / 2;
  const px = (x: number) => ox + (x - minX) * scale;
  const py = (y: number) => H - oy - (y - minY) * scale;

  const bar = niceMetres(spanX / 4);
  const barPx = bar * scale;

  /* Largest first, so a small extent nested inside a big one is on top and can
     be clicked. SVG has no z-index; paint order IS hit order, and a coverage
     plot is exactly the case where one rectangle contains another. */
  const drawOrder = [...extents].sort(
    (a, b) =>
      (ex(b.maxLon) - ex(b.minLon)) * (ny(b.maxLat) - ny(b.minLat))
      - (ex(a.maxLon) - ex(a.minLon)) * (ny(a.maxLat) - ny(a.minLat)),
  );

  const track = fixes
    .map((f, i) => `${i === 0 ? 'M' : 'L'}${px(ex(f.lon)).toFixed(1)},${py(ny(f.lat)).toFixed(1)}`)
    .join(' ');

  if (extents.length === 0 && fixes.length === 0) {
    return (
      <Empty detail="no bounding box on any session, no lat/lon in the telemetry window">
        No capture extents published for this robot.
      </Empty>
    );
  }

  return (
    <div className={styles.cover} ref={ref}>
      {ready && (
        <svg
          className={styles.coverSvg}
          width={W}
          height={H}
          role="img"
          aria-label={`Coverage: ${extents.length} capture extents and ${fixes.length} position fixes over about ${Math.round(spanX)} by ${Math.round(spanY)} metres`}
        >
          {drawOrder.map((e) => {
            const x = px(ex(e.minLon));
            const y = py(ny(e.maxLat));
            const w = Math.max(1, (ex(e.maxLon) - ex(e.minLon)) * scale);
            const h = Math.max(1, (ny(e.maxLat) - ny(e.minLat)) * scale);
            const tone = scanState(e.s.processing_state).tone;
            /* Three sessions of `orchard-b` on three days are three different
               captures; the block name alone would label them identically. */
            const block = e.s.session_id.split('_').slice(1).join('_') || e.s.session_id;
            /* Day AND time: `orchard-b` is driven twice on the same morning,
               and two boxes labelled `08-27 orchard-b` name the same thing
               twice. The stamp is what tells them apart in the ledger too. */
            const when = e.s.started_utc === null
              ? null
              : `${e.s.started_utc.slice(5, 10)} ${e.s.started_utc.slice(11, 16)}Z`;
            const short = when === null ? block : `${when} ${block}`;
            const says = `${e.s.session_id} · ${scanState(e.s.processing_state).label} · ${Math.round(ex(e.maxLon) - ex(e.minLon))} × ${Math.round(ny(e.maxLat) - ny(e.minLat))} m`;
            return (
              <g
                key={e.s.id}
                className={styles.coverBox}
                data-tone={tone}
                data-current={e.s.id === selectedId}
                role="button"
                tabIndex={0}
                aria-label={`Inspect ${says}`}
                onClick={() => onPick(e.s.id)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    onPick(e.s.id);
                  }
                }}
              >
                <title>{says}</title>
                <rect x={x} y={y} width={w} height={h} />
                <text x={x + 4} y={y + 12}>{short}</text>
              </g>
            );
          })}

          {track !== '' && <path className={styles.coverTrack} d={track} />}

          {robot.lat != null && robot.lon != null && (
            <g
              transform={`translate(${px(ex(robot.lon)).toFixed(1)} ${py(ny(robot.lat)).toFixed(1)}) rotate(${(robot.heading_deg ?? 0).toFixed(1)})`}
              data-tone={STATUS_STATE[robot.status].tone}
            >
              <path className={styles.coverHead} d="M0,-7 L5,6 L0,3 L-5,6 Z" />
            </g>
          )}

          <g className={styles.coverScale} transform={`translate(${COVER_PAD} ${H - 12})`}>
            <line x1={0} y1={0} x2={barPx} y2={0} />
            <line x1={0} y1={-3} x2={0} y2={3} />
            <line x1={barPx} y1={-3} x2={barPx} y2={3} />
            <text x={barPx + 5} y={3}>{bar} m</text>
          </g>
          <g className={styles.coverScale} transform={`translate(${COVER_PAD} 20)`}>
            <line x1={0} y1={0} x2={0} y2={-12} />
            <line x1={0} y1={-12} x2={-3} y2={-8} />
            <line x1={0} y1={-12} x2={3} y2={-8} />
            <text x={5} y={-6}>N</text>
          </g>
        </svg>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   The session inspector.

   A capture is not a row in a ledger — it is a set of files at content hashes
   in object storage, produced by a named set of sensors, against a named pose
   frame, on a clock the agent may or may not have had disciplined. Every one
   of those is a field the contract already returns and none of them fitted in
   the table, so the ledger row selects and this pane answers.

   `auterion-flight-log-review.png`'s provenance grid is the model: software,
   commit, artefact hash and TWO durations named separately, above the data
   rather than behind a click. THE BAR §13 tell 23 ("provenance that stops at
   the robot name") and tell 22 ("an unnamed clock") are both answered here.
   ------------------------------------------------------------------------- */

function SessionPane({ scan, onOpen }: { scan: Scan; onOpen: (id: number) => void }) {
  const started = scan.started_utc === null ? null : new Date(scan.started_utc).getTime();
  const stopped = scan.stopped_utc === null ? null : new Date(scan.stopped_utc).getTime();
  const disciplined = scanBool(scan, 'clock_disciplined');
  const offsetMs = scanNum(scan, 'clock_ntp_offset_ms');
  const skewPpm = scanNum(scan, 'clock_ntp_skew_ppm');
  const monoNs = scanNum(scan, 'clock_monotonic_ns');
  const wall = scanStr(scan, 'clock_wall_utc');
  /* The prefix every file in the session sits under. Printed once, so each
     file row carries its name and its hash rather than 90 identical chars. */
  const uris = scanFiles(scan)
    .map((f) => f.storage_uri)
    .filter((u): u is string => u !== null && u.length > 0);
  const prefix = uris.length === 0
    ? null
    : uris[0]!.slice(0, uris[0]!.lastIndexOf('/') + 1);
  /* A 40-shard session is a number, not forty rows: the first few name the
     shape, the rest collapse to a count and their combined size. */
  const shownFiles = scanFiles(scan).length > 3 ? scanFiles(scan).slice(0, 2) : scanFiles(scan);
  const restFiles = scanFiles(scan).slice(shownFiles.length);

  return (
    <div className={styles.sessionPane}>
      <Section
        label="Session"
        aside={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onOpen(scan.id)}
          >
            {scan.processing_state === 'copc' ? 'View 3D' : 'Open'}
          </Button>
        }
      >
        <div className={styles.sessionId} title={scan.session_id}>{scan.session_id}</div>
        <KeyValue
          layout="rows"
          items={[
            {
              label: 'State',
              value: (
                <StateLabel
                  tone={scanState(scan.processing_state).tone}
                  label={scanState(scan.processing_state).label}
                  loud={scan.processing_state === 'failed'}
                />
              ),
              mono: false,
            },
            { label: 'Format', value: scan.format ?? NO_VALUE },
            { label: 'Pose frame', value: scan.pose_frame ?? NO_VALUE },
          ]}
        />
      </Section>

      {/* Two durations that mean different things, both named, neither hidden
          (auterion-flight-log-review.png: logging duration AND airtime). */}
      <Section label="Window" aside={<span className={styles.sessionAside}>UTC</span>}>
        <KeyValue
          layout="rows"
          items={[
            {
              label: 'Started',
              value: <Timestamp value={scan.started_utc} mode="utc" strong />,
              mono: false,
            },
            {
              label: 'Stopped',
              value: scan.stopped_utc === null
                ? 'still capturing'
                : <Timestamp value={scan.stopped_utc} mode="utc" strong />,
              mono: false,
            },
            {
              label: 'Capture duration',
              value: started === null || stopped === null ? NO_VALUE : duration(stopped - started),
            },
            {
              label: 'Last updated',
              value: <Timestamp value={scan.updated_at} strong />,
              mono: false,
            },
          ]}
        />
      </Section>

      <Section
        label="Sensors"
        aside={<span className={styles.sessionAside}>{scan.sensors.length}</span>}
      >
        {scan.sensors.length === 0 ? (
          <Empty>No sensor list on this session.</Empty>
        ) : (
          <div className={styles.sensorList}>
            {scan.sensors.map((s) => (
              <span key={s} className={styles.sensorItem}>{s}</span>
            ))}
          </div>
        )}
      </Section>

      {/* WHICH CLOCK. A session carries a wall stamp, a monotonic count and an
          NTP correction, and they disagree; a viewer that prints one of them
          unlabelled is guessing on the operator's behalf. */}
      <Section
        label="Clock"
        aside={
          <span className={styles.sessionAside} data-tone={disciplined === false ? 'warn' : undefined}>
            {disciplined === null ? 'unreported' : disciplined ? 'disciplined' : 'free-running'}
          </span>
        }
      >
        <KeyValue
          layout="rows"
          items={[
            {
              label: 'Wall at start',
              value: wall === null ? NO_VALUE : <Timestamp value={wall} mode="utc" strong />,
              mono: false,
            },
            {
              label: 'Monotonic',
              value: monoNs === null ? NO_VALUE : num(monoNs / 1e9, 3),
              unit: monoNs === null ? undefined : 's',
            },
            {
              label: 'NTP offset',
              value: offsetMs === null ? NO_VALUE : num(offsetMs, 2),
              unit: offsetMs === null ? undefined : 'ms',
            },
            {
              label: 'NTP skew',
              value: skewPpm === null ? NO_VALUE : num(skewPpm, 3),
              unit: skewPpm === null ? undefined : 'ppm',
            },
          ]}
        />
      </Section>

      <Section
        label="Artefacts"
        aside={
          <span className={styles.sessionAside}>
            {scan.sync_state ?? NO_VALUE} · {bytes(scan.size_bytes)}
          </span>
        }
      >
        {prefix !== null && (
          <div className={styles.storagePrefix} title={prefix}>{prefix}</div>
        )}
        {scanFiles(scan).length === 0 && scanDerivatives(scan).length === 0 ? (
          <Empty>No files registered for this session.</Empty>
        ) : (
          <ul className={styles.fileList}>
            {shownFiles.map((f) => (
              <li key={f.name} className={styles.fileRow}>
                <span className={styles.fileName} title={f.name}>{f.name}</span>
                <span className={styles.fileHash} title={f.content_sha256}>
                  {f.content_sha256.slice(0, 12)}
                </span>
                <span className={styles.fileSize}>{bytes(f.size_bytes)}</span>
              </li>
            ))}
            {restFiles.length > 0 && (
              <li className={styles.fileRow} data-derived="true">
                <span className={styles.fileName}>
                  +{restFiles.length} more capture {restFiles.length === 1 ? 'file' : 'files'}
                </span>
                <span className={styles.fileHash} />
                <span className={styles.fileSize}>
                  {bytes(restFiles.reduce((a, f) => a + f.size_bytes, 0))}
                </span>
              </li>
            )}
            {scanDerivatives(scan).map((d) => (
              <li key={d.kind} className={styles.fileRow} data-derived="true">
                <span className={styles.fileName} title={d.storage_uri ?? d.kind}>{d.kind}</span>
                <span className={styles.fileHash}>derived</span>
                <span className={styles.fileSize}>{bytes(d.size_bytes)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function CapturesTab({
  scans,
  facts,
  robot,
  read,
  onOpenScan,
}: {
  scans: readonly Scan[];
  facts: WindowFacts;
  robot: Robot;
  /** The capture-session read. `count={0}` is not a census. */
  read: Read;
  onOpenScan: (id: number) => void;
}) {
  const [picked, setPicked] = useState<number | null>(null);
  /* The newest session is the one an operator lands on this tab to look at,
     so the pane is never empty while there is anything to show. A pick that
     outlives its row (the ledger refetched) falls back to it. */
  const selected = scans.find((s) => s.id === picked) ?? scans[0] ?? null;

  if (scans.length === 0) {
    return (
      <Panel
        title="Capture sessions"
        count={figure(read, 0)}
        meta={<span><Figure of={read} value={bytes(0)} /> on object storage</span>}
        pad="none"
      >
        {unread(read) && <FaultLine read={read} />}
        <DataTable
          rows={scans}
          columns={capturesColumns(onOpenScan)}
          rowKey={(s) => String(s.id)}
          of={read}
          empty="No capture sessions from this robot."
        />
      </Panel>
    );
  }

  return (
    <>
      <Panel
        className={`${styles.fit} ${styles.ledger}`}
        title="Capture sessions"
        count={figure(read, scans.length)}
        meta={
          <span>
            <Figure of={read} value={bytes(scans.reduce((a, s) => a + s.size_bytes, 0))} />
            {' on object storage'}
          </span>
        }
        pad="none"
      >
        <DataTable
          rows={scans}
          columns={capturesColumns(onOpenScan)}
          rowKey={(s) => String(s.id)}
          rowTone={(s) => scanState(s.processing_state).tone}
          currentKey={selected === null ? null : String(selected.id)}
          /* Selecting inspects; only the row's own verb opens the viewer. A
             ledger that navigates on click cannot also be an inspector, and
             the 3D viewer is a heavier place to land by accident. */
          onRowClick={(s) => setPicked(s.id)}
          of={read}
          empty="No capture sessions from this robot."
        />
      </Panel>
      <PanelGrid className={styles.grow} cols="minmax(0, 360px) minmax(0, 1fr)">
        <Panel
          title="Session"
          meta={<span>{selected === null ? NO_VALUE : `#${selected.id}`}</span>}
          pad="tight"
        >
          {selected === null
            ? <Empty prompt>Select a session.</Empty>
            : <SessionPane scan={selected} onOpen={onOpenScan} />}
        </Panel>
        <Panel
          title="Coverage"
          count={figure(read, scans.length)}
          meta={<span>local ENU · north up</span>}
          pad="none"
          scroll={false}
        >
          <CoveragePlot
            scans={scans}
            facts={facts}
            robot={robot}
            selectedId={selected === null ? null : selected.id}
            onPick={setPicked}
          />
        </Panel>
      </PanelGrid>
    </>
  );
}

/* -------------------------------------------------------------------------
   Control authority — the header's permanent answer to the two questions an
   operator asks about a machine that can move: IS IT ARMED, and HOW DO I
   STOP IT.

   Both live in the identity row, so they are on Overview, Telemetry,
   Commands, Captures and Configuration alike. A lock that only exists on the
   surface where you issue commands is a lock the operator cannot see from the
   surface they actually live on (THE BAR §13 tell 25; §6 "the lock state must
   be visible from across the room").

   Ordering follows §11.6/§11.7 exactly: the arming control is a SWITCH and
   never takes the filled accent — that budget belongs to the abort verb,
   which is the one filled thing on the whole page. Certificate admin is not a
   verb an operator reaches for at speed, so it goes in the overflow; the
   irreversible one stays visible, red, and last.
   ------------------------------------------------------------------------- */

/** A command the robot has not finished with yet. */
const CMD_OPEN: ReadonlySet<CommandStatus> = new Set<CommandStatus>([
  'pending', 'sent', 'acked',
]);

function AuthorityBar({
  robot,
  commands,
  leaseUntil,
  onLease,
  onStop,
  stopPending,
  onMint,
  mintPending,
  onEdit,
  onTeleop,
  onDeregister,
}: {
  robot: Robot;
  commands: readonly Command[] | undefined;
  /** Epoch ms the actuation lease expires, or null when it is not held. */
  leaseUntil: number | null;
  onLease: (until: number | null) => void;
  onStop: () => void;
  stopPending: boolean;
  onMint: () => void;
  mintPending: boolean;
  onEdit: () => void;
  onTeleop: () => void;
  onDeregister: () => void;
}) {
  const now = useNow(leaseUntil !== null);
  const held = leaseUntil !== null && leaseUntil > now;
  /* The shared ticker only runs while the lease is held, so on the very frame
     the lease is taken `now` is still the value it had at mount. Clamping to
     the lease's own length keeps that first frame from printing a remaining
     time longer than a lease can ever be. */
  const left = leaseUntil === null ? 0 : Math.min(LEASE_MS, Math.max(0, leaseUntil - now));
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  /* The lease is a countdown, and it expires whether or not anyone is
     looking. Clearing it here is what makes every gated verb on the Commands
     surface go inert at the same instant the switch does. */
  useEffect(() => {
    if (leaseUntil !== null && leaseUntil <= Date.now()) onLease(null);
  }, [leaseUntil, now, onLease]);

  const profile = readProfile(robot);
  const gated = (profile ?? []).filter((e) => e.hazard !== 'benign');
  const stopVerb = (profile ?? []).find((e) => e.name === 'stop') ?? null;

  /* The abort's acknowledgement, read off the queue rather than off the
     click: sent is not acked is not completed (§11.9), and the operator's
     next question after pressing Stop is which of the three it reached. */
  const lastStop = (commands ?? [])
    .filter((c) => c.command === 'stop')
    .reduce<Command | null>(
      (best, c) =>
        best === null || new Date(c.issued_at).getTime() > new Date(best.issued_at).getTime()
          ? c
          : best,
      null,
    );
  const openStop = lastStop !== null && CMD_OPEN.has(lastStop.status) ? lastStop : null;

  const onMenuClose = useCallback((restore: boolean) => {
    setMenu(false);
    if (restore) menuRef.current?.querySelector('button')?.focus();
  }, []);

  return (
    <div className={styles.authority}>
      {profile === null ? (
        /* No advertised verb set: two dead controls with tooltips would be
           worse than one printed sentence (§11.10 — a disabled control with
           no reason is worse than an enabled one that fails). */
        <span className={styles.authNone}>
          no command profile · nothing to arm or stop
        </span>
      ) : (
        <>
          <button
            type="button"
            role="switch"
            aria-checked={held}
            className={styles.lease}
            disabled={gated.length === 0}
            title={
              gated.length === 0
                ? `${robot.robot_id} publishes no gated verbs — nothing on this robot needs an actuation lease.`
                : held
                  ? `Actuation authority held — releases in ${duration(left)}. Click to release it now.`
                  : `Take actuation authority for ${duration(LEASE_MS)}. ${gated.length} of ${profile.length} verbs on ${robot.robot_id} drive or actuate it.`
            }
            onClick={() => onLease(held ? null : Date.now() + LEASE_MS)}
          >
            <span className={styles.leaseTrack}><span className={styles.leaseKnob} /></span>
            <span className={styles.leaseWord}>
              {gated.length === 0
                ? 'No gated verbs'
                : held ? 'Actuation enabled' : 'Actuation locked'}
            </span>
            {/* Held → what is left of it; idle → what pressing it grants.
                Same cell, same width, so the switch cannot resize on the
                tick (§11.6 — a lease with no visible expiry is a lease you
                discover you lost by watching the robot stop). */}
            <span className={styles.leaseLeft} data-held={held}>
              {duration(held ? left : LEASE_MS)}
            </span>
          </button>

          {/* The abort. Filled, never red — red is fault and deletion, and a
              red Stop teaches the operator to hesitate over the one verb that
              must be pressed without thinking (§11.7). Never gated by the
              lease: the contract publishes `stop` as benign, and an abort you
              have to unlock first is not an abort. */}
          <span className={styles.abort}>
            <Button
              variant="primary"
              icon="stop"
              disabled={stopVerb === null || stopPending}
              {...(openStop === null ? {} : { badge: openStop.status })}
              title={
                stopVerb === null
                  ? `${robot.robot_id} does not publish a stop verb — there is nothing the console can abort.`
                  : openStop === null
                    ? `Issue stop to ${robot.robot_id} now. One press, no confirmation.`
                    : `stop issued ${relative(openStop.issued_at)} by ${openStop.issued_by} — ${openStop.status}. Pressing again queues another.`
              }
              onClick={onStop}
            >
              Stop
            </Button>
          </span>
        </>
      )}

      {/* The other half of a door that was one-way. `/fleet/teleop/:id` has
          carried a `Robot detail ⇧D` button since it shipped; this page had no
          teleop affordance anywhere, so the console's most consequential
          transition could only be made by typing a URL. Same variant, same
          icon, same side of the bar as its opposite number. */}
      <Button variant="ghost" size="sm" icon="external-link" onClick={onTeleop}>
        Teleop
      </Button>

      <span className={styles.authSep} aria-hidden="true" />

      <Button variant="danger" icon="trash" onClick={onDeregister}>
        Deregister
      </Button>

      <div className={styles.menuWrap} data-disclose="true" ref={menuRef}>
        <IconButton
          icon="more-vertical"
          label={`More actions for ${robot.robot_id}`}
          aria-haspopup="menu"
          aria-expanded={menu}
          active={menu}
          onClick={() => setMenu((v) => !v)}
        />
        {menu && (
          <AuthorityMenu
            enrolled={robot.enrolled_at !== null}
            mintPending={mintPending}
            onEdit={() => { setMenu(false); onEdit(); }}
            onMint={() => { setMenu(false); onMint(); }}
            onClose={onMenuClose}
          />
        )}
      </div>
    </div>
  );
}

function AuthorityMenu({
  enrolled,
  mintPending,
  onEdit,
  onMint,
  onClose,
}: {
  enrolled: boolean;
  mintPending: boolean;
  onEdit: () => void;
  onMint: () => void;
  onClose: (restore: boolean) => void;
}) {
  const ref = useDisclose(onClose);

  /* A menu is arrow-navigable or it is not a menu. useDisclose parks focus on
     the container and takes Escape; this moves it onto the items. */
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
  }, [ref]);

  const onKeys = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const all = Array.from(
      ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [],
    );
    if (all.length === 0) return;
    const i = all.indexOf(document.activeElement as HTMLButtonElement);
    let next = i;
    if (e.key === 'ArrowDown') next = (i + 1 + all.length) % all.length;
    else if (e.key === 'ArrowUp') next = (i - 1 + all.length) % all.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = all.length - 1;
    else return;
    e.preventDefault();
    all[next]?.focus();
  };

  return (
    <div className={styles.menu} role="menu" ref={ref} tabIndex={-1} onKeyDown={onKeys}>
      <button type="button" role="menuitem" className={styles.menuItem} onClick={onEdit}>
        <Icon name="edit" size={14} />
        Edit registry record
      </button>
      <button
        type="button"
        role="menuitem"
        className={styles.menuItem}
        disabled={mintPending}
        onClick={onMint}
      >
        <Icon name="key" size={14} />
        {mintPending ? 'Minting…' : enrolled ? 'Re-issue enrolment token' : 'Generate enrolment token'}
      </button>
      <p className={styles.menuNote}>
        {enrolled
          ? 'Invalidates any unused token. The certificate in use is unaffected.'
          : 'The bring-up trades this token for a client certificate.'}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Deregister confirmation. A confirm, not an editor, so a dialog is right —
   and it names what survives before it offers the red verb.
   ------------------------------------------------------------------------- */

function DeregisterDialog({
  robot,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  robot: Robot;
  pending: boolean;
  error: unknown;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className={styles.overlay} onClick={onCancel} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dereg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.dialogHead}>
          <h2 className={styles.dialogTitle} id="dereg-title">
            Deregister {robot.robot_id}
          </h2>
          <IconButton icon="close" label="Cancel" size="sm" onClick={onCancel} />
        </div>
        <KeyValue
          layout="rows"
          items={[
            { label: 'Removed', value: 'registry record, certificate trust' },
            { label: 'Retained', value: 'telemetry, commands, capture sessions' },
            { label: 'Last seen', value: relative(robot.last_seen) },
            { label: 'Enrolled', value: robot.enrolled_at === null ? 'never' : utc(robot.enrolled_at) },
          ]}
        />
        <p className={styles.dialogText}>
          This cannot be undone from the console.
        </p>
        {error != null && <ErrorLine>{String(error)}</ErrorLine>}
        <div className={styles.dialogFoot}>
          <Button onClick={onCancel} disabled={pending}>Cancel</Button>
          <Button variant="danger" icon="trash" onClick={onConfirm} disabled={pending}>
            {pending ? 'Deregistering…' : 'Deregister'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   The page.
   ========================================================================== */

export function RobotDetail() {
  const { robotId, detailTab, navigate } = useFleetRoute();
  /* The open surface is the URL, not component state — see `FleetRoute`. */
  const tab: TabId = isTabId(detailTab) ? detailTab : 'overview';
  const setTab = useCallback(
    (id: TabId) => {
      navigate('detail', { ...(robotId === undefined ? {} : { robotId }), tab: id });
    },
    [navigate, robotId],
  );
  const [span, setSpan] = useState<SpanKey>('6h');
  const [brush, setBrush] = useState<{ from: number; to: number } | null>(null);
  const [axis, setAxis] = useState<TimeAxisKey>('agent');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [enrollToken, setEnrollToken] = useState<EnrollmentToken | null>(null);
  /* Actuation authority is PAGE state, not Commands-tab state: the switch and
     the abort verb ride the identity row on every surface, and a lease taken
     on Overview has to be the same lease the Commands verbs read. */
  const [leaseUntil, setLeaseUntil] = useState<number | null>(null);

  const limit = spanDef(span).limit;
  const robotQ = useRobot(robotId);
  const robot = robotQ.data;
  /* ONE FAILURE LANGUAGE, AND IT IS `components/states`'.

     This page used to answer a failed `/api/robots/{id}` with the sentence
     "No robot with id jetrover-01 in the registry." over an empty viewport.
     That is a 404's sentence printed for a `Failed to fetch`, which is the
     console inventing a fact — the robot IS on the roster; this browser could
     not read it — and it threw the identity band, the tab strip and every
     reserved row away to say it. `useRead` tells the two apart, and a 404
     keeps the instrument and gets the way out rather than a Retry. */
  const robotRead = useRead(robotQ, {
    path: robotId === undefined ? undefined : `/api/robots/${robotId}`,
    everyMs: 5_000,
    subject: robotId ?? 'this robot',
  });
  /* THE READ, NOT JUST ITS ROWS — AND THIS IS THE DEFECT THIS PAGE EXISTED TO
     DEMONSTRATE.

     `useTelemetry(...).data ?? []` is three characters of JavaScript and it
     turned every figure derived from it into a confident lie the moment the
     endpoint stopped answering. The worst of them was not a number: with no
     rows and no history the Overview concluded `virgin` and drew the BRING-UP
     LADDER — `Nothing has arrived from rover-01 since it was enrolled`, `First
     telemetry never`, `Check that the ROS 2 agent is running` — about a robot
     with 150 readings whose ROS agent was running perfectly. A failed HTTP
     call was rendered as a diagnosis of hardware, and it would have sent an
     operator to a healthy machine.

     So the read is held, and everything counted from it asks `unread` first.
     Whether the robot has ever reported is `robot.last_seen`'s answer — the
     registry's, from a source that DID answer — and never this one's. */
  const telemetryQ = useTelemetry(robotId, { limit });
  const { data: telemetry, isFetching: telemetryFetching } = telemetryQ;
  const telemetryRead = useRead(telemetryQ, {
    path: robotId === undefined ? undefined : `/api/robots/${robotId}/telemetry`,
    everyMs: 5_000,
    subject: robotId ?? 'this robot',
  });
  const commandsQ = useRobotCommands(robotId);
  const { data: commands, dataUpdatedAt: commandsUpdatedAt } = commandsQ;
  const commandsRead = useRead(commandsQ, {
    path: robotId === undefined ? undefined : `/api/robots/${robotId}/commands`,
    everyMs: CMD_POLL_MS,
    subject: 'the command queue',
  });
  const scansQ = useRobotScans(robotId);
  const { data: scans } = scansQ;
  const scansRead = useRead(scansQ, {
    path: robotId === undefined ? undefined : `/api/robots/${robotId}/scans`,
    everyMs: 15_000,
    subject: 'the capture sessions',
  });
  const { data: revisions } = useRobotRevisions(robotId);
  const { connected: streamConnected } = useCommandStream(robotId);
  const del = useDeleteRobot();
  const mintToken = useMintEnrollmentToken(robotId ?? '');
  /* The abort lives in the identity row, so its failure has to have somewhere
     to print that is not a tooltip. It shares the body's notice line with the
     token mint. */
  const abort = useIssueCommand();

  /* Widening the span changes the fetch depth, and a query keyed on depth
     comes back undefined for one frame. Holding the last rows FOR THIS ROBOT
     means the instrument redraws rather than blinking through its own
     never-reported state on the way to more history. */
  const held = useRef<{ id: string; rows: readonly TelemetryReading[] } | null>(null);
  if (telemetry !== undefined && robotId !== undefined) held.current = { id: robotId, rows: telemetry };
  const rows = telemetry
    ?? (held.current !== null && held.current.id === robotId ? held.current.rows : undefined);

  /* Recomputed when the readings actually change — every 5s poll — not on the
     one-second clock. Ages that must tick own their own ticker (Timestamp,
     the band's contact readout); the window's arithmetic does not. */
  const all = useMemo(() => windowFacts(rows, Date.now(), axis, limit), [rows, axis, limit]);

  /* The window. Live means "the right edge is the newest thing there is";
     brushed means an absolute range the operator chose and nothing may move
     it but them. */
  const spanMs = spanDef(span).ms;
  const view = brush ?? { from: all.to - spanMs, to: all.to };
  const atLive = brush === null;
  const facts = useMemo(
    () => sliceFacts(all, view.from, view.to),
    [all, view.from, view.to],
  );

  const extFrom = Math.min(all.from, view.from);
  const extTo = Math.max(all.to, view.to);
  const runs = useMemo(
    () => presenceRuns(all.times, extFrom, extTo, tolerance(all.cadenceMs)),
    [all.times, extFrom, extTo, all.cadenceMs],
  );

  /* `all.to` is the newest thing there is (or now, whichever is later), which
     is the right "now" for an in-flight command's open span — and it means the
     marks recompute on the poll rather than on a one-second ticker. */
  const marks = useMemo(
    () => transportMarks(commands, scans, all.readings, all.times, all.to),
    [commands, scans, all.readings, all.times, all.to],
  );

  /* What to call the window's width in prose. The ladder's own key while the
     window is one of the four; its measured width once the operator has
     brushed something that is not. */
  const custom = brush !== null && Math.abs((view.to - view.from) - spanMs) > spanMs * 0.02;
  const windowLabel = custom ? duration(view.to - view.from) : span;

  const onWindow = useCallback((f: number, t: number) => {
    setBrush({ from: f, to: t });
  }, []);
  const onGoLive = useCallback(() => setBrush(null), []);
  const onSpan = useCallback((s: SpanKey) => { setSpan(s); setBrush(null); }, []);

  const ctl: WindowCtl = {
    extFrom,
    extTo,
    from: view.from,
    to: view.to,
    live: atLive,
    span,
    /* A fifth segment only where the window is genuinely a WIDTH the ladder
       does not carry. Stepping a 6h window left with ← keeps it 6h wide, and
       printing `6h 00m` beside a `6h` button says nothing — the pill already
       carries the one thing that changed, which is that it is not live. */
    custom,
    runs,
    marks,
    axis,
    axisShiftMs: all.axisShiftMs,
    onSpan,
    onWindow,
    onGoLive,
    onAxis: setAxis,
  };

  const openScan = useCallback(
    (id: number) => navigate('scan', { scanId: String(id) }),
    [navigate],
  );

  /* Authority is per robot and never travels. Walking from koko-04 to
     rover-07 must not arrive with the lease already held. */
  useEffect(() => { setLeaseUntil(null); }, [robotId]);

  if (!robotId) {
    return (
      <SurfaceShell title="Robot detail">
        <Empty prompt>No robot selected.</Empty>
      </SurfaceShell>
    );
  }

  /* Nothing has landed — reading, refused, or 404. One picture for all three:
     the page's own identity rail, drawn with its real row labels because those
     are true before the first byte, with the fault or the absence on one line
     inside it. Never a takeover, and never a sentence that claims to know
     something about the robot. */
  if (robot === undefined) {
    return (
      <SurfaceShell
        eyebrow={<BackLink label="Fleet" onClick={() => navigate('list')} />}
        title={robotId}
        pad="none"
      >
        {/* THE PAGE'S OWN SHAPE, WITH NOTHING IN IT. Same two-column grid, same
            rail, same three panels in the same order — because every one of
            those facts is true before the read lands, after it fails and on an
            id that is not on record. Only the values are ever absent. */}
        <div className={styles.workspace}>
          <div className={styles.rail}>
            <ReadPanel
              title="Identity"
              read={robotRead}
              skeleton={<SkeletonRail labels={IDENTITY_ROWS} what="this robot" />}
              leave={{ label: 'Back to the fleet', onSelect: () => navigate('list') }}
            >
              {null}
            </ReadPanel>
            <Panel title="Position" meta={<span className={styles.toolbarNote}>WGS84</span>} pad="none">
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonRail labels={POSITION_ROWS} what="position" />
              </StoppedContext.Provider>
            </Panel>
            <Panel title="Enrolment" meta={<span className={styles.toolbarNote}>X.509</span>} pad="none">
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonRail labels={ENROLMENT_ROWS} what="enrolment" />
              </StoppedContext.Provider>
            </Panel>
          </div>
          <div className={styles.main}>
            <Panel
              className={styles.grow}
              title="Telemetry window"
              count={STACK_CHANNELS.length + 1}
              pad="none"
            >
              <StoppedContext.Provider value={{ mode: 'stopped' }}>
                <SkeletonPlot lanes={STACK_LANES} ticks={6} what="telemetry" />
              </StoppedContext.Provider>
            </Panel>
            <PanelGrid className={styles.duo}>
              <Panel className={styles.ledger} title="Command queue" pad="none">
                <StoppedContext.Provider value={{ mode: 'stopped' }}>
                  <SkeletonTable columns={QUEUE_COLUMNS} rows={7} edge what="commands" />
                </StoppedContext.Provider>
              </Panel>
              <Panel className={styles.ledger} title="Captures" pad="none">
                <StoppedContext.Provider value={{ mode: 'stopped' }}>
                  <SkeletonTable columns={CAPTURE_COLUMNS} rows={7} edge what="captures" />
                </StoppedContext.Provider>
              </Panel>
            </PanelGrid>
          </div>
        </div>
      </SurfaceShell>
    );
  }

  const live = robot.liveness === 'live';
  const pinned = revisions?.revisions.find((r) => r.id === revisions.current) ?? null;
  const board = pinned === null ? null : revMetaStr(pinned, 'board');
  const kind = metaStr(robot, 'kind');
  const site = metaStr(robot, 'site');
  const bay = metaStr(robot, 'bay');
  /* THE provenance line (auterion-vehicle-detail-overview.png): model,
     morphology + pinned revision, board, firmware, real-vs-sim and site on
     ONE line under the name. Not a definition list — that lives in the rail,
     where it does not cost the operator half the viewport. */
  const provenance: { key: string; text: string; strong?: boolean; opt?: '1' | '2' }[] = [
    { key: 'model', text: robot.model },
    { key: 'rev', text: pinned === null ? robot.morphology : `${robot.morphology} ${pinned.version}`, strong: true },
    ...(board === null ? [] : [{ key: 'board', text: board, opt: '2' as const }]),
    { key: 'fw', text: robot.firmware === null ? 'no firmware' : `fw ${robot.firmware}` },
    ...(kind === null ? [] : [{ key: 'kind', text: kind, opt: '1' as const }]),
    ...(site === null
      ? []
      : [{ key: 'site', text: bay === null ? site : `${site}/${bay}`, opt: '1' as const }]),
  ];

  /* THE TAB STRIP IS A CENSUS, AND A CENSUS NEEDS A READ. `Telemetry 0` beside
     `Commands 4` told an operator this robot has no telemetry, on a frame where
     the only thing that had happened was that one endpoint timed out. Each
     count is now `—` unless its own source answered — three sources, three
     independent verdicts, so a dead telemetry read does not blank the two
     counts that are real. */
  const tabs: TabItem[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'telemetry', label: 'Telemetry', count: figure(telemetryRead, facts.readings.length) },
    { id: 'commands', label: 'Commands', count: figure(commandsRead, commands?.length) },
    { id: 'captures', label: 'Captures', count: figure(scansRead, scans?.length) },
    { id: 'videos', label: 'Videos' },
    { id: 'config', label: 'Configuration' },
  ];

  let body: ReactNode = null;
  if (tab === 'overview') {
    body = (
      <OverviewTab
        robot={robot}
        pinned={pinned}
        facts={facts}
        ctl={ctl}
        read={telemetryRead}
        commandsRead={commandsRead}
        scansRead={scansRead}
        hasHistory={all.readings.length > 0}
        commands={commands}
        scans={scans}
        streamConnected={streamConnected}
        onOpenScan={openScan}
        onGoTab={setTab}
        onMint={() => mintToken.mutate(undefined, { onSuccess: (res) => setEnrollToken(res) })}
        mintPending={mintToken.isPending}
      />
    );
  } else if (tab === 'telemetry') {
    body = (
      <TelemetryTab
        facts={facts}
        ctl={ctl}
        read={telemetryRead}
        hasHistory={all.readings.length > 0}
        loading={telemetryFetching && facts.readings.length === 0}
      />
    );
  } else if (tab === 'commands') {
    body = (
      <CommandsTab
        robot={robot}
        commands={commands}
        read={commandsRead}
        tlmRead={telemetryRead}
        streamConnected={streamConnected}
        facts={facts}
        ctl={ctl}
        leaseUntil={leaseUntil}
        onLease={setLeaseUntil}
      />
    );
  } else if (tab === 'captures') {
    body = (
      <CapturesTab
        key={robot.robot_id}
        scans={scans ?? []}
        /* The coverage plot draws the robot's track under the capture
           extents; a window with nothing in it must not erase the track when
           the same fetch still holds one. */
        facts={bandSource(facts, all)}
        robot={robot}
        read={scansRead}
        onOpenScan={openScan}
      />
    );
  } else if (tab === 'videos') {
    body = <VideosTab key={robot.robot_id} robotId={robot.robot_id} />;
  } else {
    body = <ConfigTab robot={robot} />;
  }

  return (
    <SurfaceShell
      eyebrow={<BackLink label="Fleet" onClick={() => navigate('list')} />}
      title={robot.name ?? robot.robot_id}
      subtitle={
        <>
          <span className={styles.idTag}>{robot.robot_id}</span>
          <StatusBadge status={robot.status} />
          <LivenessBadge liveness={robot.liveness} pulse={live} />
          <span className={styles.provenance}>
            {provenance.map((p, i) => (
              <span
                key={p.key}
                className={styles.provItem}
                data-strong={p.strong === true}
                data-opt={p.opt}
              >
                {i > 0 && <span className={styles.provSep} aria-hidden="true">·</span>}
                {p.text}
              </span>
            ))}
          </span>
        </>
      }
      actions={
        <AuthorityBar
          robot={robot}
          commands={commands}
          leaseUntil={leaseUntil}
          onLease={setLeaseUntil}
          onStop={() => abort.mutate({ robotId: robot.robot_id, command: 'stop' })}
          stopPending={abort.isPending}
          onMint={() =>
            mintToken.mutate(undefined, { onSuccess: (res) => setEnrollToken(res) })}
          mintPending={mintToken.isPending}
          onEdit={() => setTab('config')}
          onTeleop={() => navigate('teleop', { robotId: robot.robot_id })}
          onDeregister={() => setConfirmDelete(true)}
        />
      }
      tabs={
        <>
          <VitalsBand
            facts={facts}
            all={all}
            live={live}
            robot={robot}
            read={telemetryRead}
            windowLive={atLive}
            windowLabel={windowLabel}
            streamConnected={streamConnected}
            commandsAt={commandsUpdatedAt > 0 ? commandsUpdatedAt : null}
            scans={scans}
            onGoLive={onGoLive}
          />
          <Tabs
            items={tabs}
            active={tab}
            onChange={(id) => setTab(id as TabId)}
            label={`${robot.robot_id} surfaces`}
          />
        </>
      }
      pad="none"
    >
      <div className={styles.workspace}>
        <IdentityRail
          robot={robot}
          facts={facts}
          all={all}
          read={telemetryRead}
          revisions={revisions}
        />
        <div
          className={styles.main}
          role="tabpanel"
          id={`tabpanel-${tab}`}
          aria-labelledby={`tab-${tab}`}
          tabIndex={-1}
        >
          {abort.isError && (
            <ErrorLine>
              stop was not accepted by the queue — {String(abort.error)}
            </ErrorLine>
          )}
          {mintToken.isError && <ErrorLine>{String(mintToken.error)}</ErrorLine>}
          {body}
        </div>
      </div>

      {enrollToken && (
        <EnrollmentTokenDialog
          robotId={robot.robot_id}
          token={enrollToken.enrollment_token}
          expiresAt={enrollToken.expires_at}
          onClose={() => setEnrollToken(null)}
        />
      )}

      {confirmDelete && (
        <DeregisterDialog
          robot={robot}
          pending={del.isPending}
          error={del.isError ? del.error : null}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() =>
            del.mutate(robot.robot_id, {
              onSuccess: () => { setConfirmDelete(false); navigate('list'); },
            })}
        />
      )}
    </SurfaceShell>
  );
}
